'use client'

import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, PointerEvent, ReactNode } from 'react'
import ReactGridLayout, { WidthProvider } from 'react-grid-layout/legacy'
import type { Layout, LayoutItem } from 'react-grid-layout/legacy'
import { createPortal } from 'react-dom'
import { useRouter, usePathname } from 'next/navigation'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Funnel,
  FunnelChart,
  LabelList,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  RadialBar,
  RadialBarChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  Treemap,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts'
import { Sidebar, getLeftPanelData } from '@/components/layout/Sidebar'
import { LeftPanel } from '@/components/layout/LeftPanel'
import { TopBar } from '@/components/layout/TopBar'
import { BottomBar } from '@/components/layout/BottomBar'
import { MobileNav } from '@/components/layout/MobileNav'
import { useUser } from '@/contexts/UserContext'
import { useEmbedToken } from '@/components/embed/EmbedTokenContext'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import './dashboard.css'
import '@/styles/admin-app-banner.css'

const storeColorCount = 10
const DashboardGridLayout = WidthProvider(ReactGridLayout)
const dashboardGridCols = 60
const dashboardMobileBreakpoint = 760
const dashboardGridRowHeight = 20
const mockChartDashboardStartId = 9001
const removedMockChartDashboardIds = new Set([9009, 9010, 9012, 9014, 9015, 9020, 9021, 9023, 9024, 9028, 9029, 9030])
const mockChartDashboardIds = Array.from({ length: 30 }, (_, index) => mockChartDashboardStartId + index)
  .filter((id) => !removedMockChartDashboardIds.has(id))
const operationFlowDashboardId = 73
const refundBalanceDashboardId = 74
const refundReasonDashboardId = 75
const outboundCustomerServiceDashboardId = 76
const supplyRequestTrendDashboardId = 77
const supplyRadarDashboardId = 78
const supplyAgingDashboardId = 79
const supplyPerformanceDashboardId = 80
const marketplaceProductErrorsDashboardId = 81
const operationFlowAutoAddPreferenceKey = '__operation_flow_auto_added_v1'
const outboundCustomerServiceAutoAddPreferenceKey = '__outbound_customer_service_auto_added_v1'
const supplyRequestTrendAutoAddPreferenceKey = '__supply_request_trend_auto_added_v1'
const supplyDashboardSuiteAutoAddPreferenceKey = '__supply_dashboard_suite_auto_added_v1'
const marketplaceProductErrorsAutoAddPreferenceKey = '__marketplace_product_errors_auto_added_v1'
const backendConnectedDashboardIds = [62, 63, 64, 65, 66, 67, 58, 47, 51, operationFlowDashboardId, refundBalanceDashboardId, refundReasonDashboardId, outboundCustomerServiceDashboardId, supplyRequestTrendDashboardId, supplyRadarDashboardId, supplyAgingDashboardId, supplyPerformanceDashboardId, marketplaceProductErrorsDashboardId]
const activeDashboardIds = new Set(backendConnectedDashboardIds)
const completedDashboardIds = new Set(backendConnectedDashboardIds)
const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://app.canerozen.com/api'
const dashboardWidgetCacheTtlMs = 30_000

type DashboardWidgetCacheEntry = {
  time: number
  data: unknown
}

const dashboardWidgetCache = new Map<string, DashboardWidgetCacheEntry>()

function readDashboardWidgetCache<T>(key: string): T | null {
  const cached = dashboardWidgetCache.get(key)
  if (!cached) return null
  if (Date.now() - cached.time > dashboardWidgetCacheTtlMs) {
    dashboardWidgetCache.delete(key)
    return null
  }
  return cached.data as T
}

function writeDashboardWidgetCache(key: string, data: unknown) {
  dashboardWidgetCache.set(key, { time: Date.now(), data })
}

type FirmStoreOption = {
  id: number
  firm_id?: number | null
  name?: string | null
  code?: string | null
  initials: string
  colorIndex: number
}

type RawFirmStore = {
  id: number
  firm_id?: number | null
  name?: string | null
  code?: string | null
}

type DashboardDefinition = {
  id: number
  platform?: string | null
  platforms?: string[] | null
  scope?: string | null
  code?: string | null
  name: string
  title?: string | null
  description?: string | null
  component_key?: string | null
  sort?: number | null
  is_default?: boolean
  supported_surfaces?: string[] | null
  settings?: Record<string, unknown> | null
  data_source?: Record<string, unknown> | null
}

type DashboardGridItemLayout = LayoutItem & {
  minW: number
  maxW: number
  minH: number
  maxH: number
}

const defaultUserDashboardGridLayouts: Record<string, Partial<DashboardGridItemLayout>> = {
  report_ev_charging_locations: {
    i: 'report_ev_charging_locations',
    x: 0,
    y: 0,
    w: 30,
    h: 21,
    minW: 30,
    maxW: 60,
    minH: 16,
    maxH: 22,
  },
  report_commerce_total_sales: {
    i: 'report_commerce_total_sales',
    x: 30,
    y: 0,
    w: 30,
    h: 21,
    minW: 24,
    maxW: 42,
    minH: 20,
    maxH: 30,
  },
  report_commerce_total_orders: {
    i: 'report_commerce_total_orders',
    x: 0,
    y: 21,
    w: 20,
    h: 18,
    minW: 18,
    maxW: 42,
    minH: 18,
    maxH: 28,
  },
  report_commerce_total_products: {
    i: 'report_commerce_total_products',
    x: 20,
    y: 21,
    w: 20,
    h: 18,
    minW: 20,
    maxW: 42,
    minH: 15,
    maxH: 28,
  },
  report_commerce_sales_statistics: {
    i: 'report_commerce_sales_statistics',
    x: 40,
    y: 21,
    w: 20,
    h: 18,
    minW: 20,
    maxW: 42,
    minH: 15,
    maxH: 28,
  },
  report_commerce_recent_month: {
    i: 'report_commerce_recent_month',
    x: 0,
    y: 39,
    w: 20,
    h: 18,
    minW: 20,
    maxW: 42,
    minH: 15,
    maxH: 28,
  },
  report_operation_flow: {
    i: 'report_operation_flow',
    x: 0,
    y: 57,
    w: 60,
    h: 22,
    minW: 30,
    maxW: 60,
    minH: 18,
    maxH: 30,
  },
  report_outbound_customer_service: {
    i: 'report_outbound_customer_service',
    x: 0,
    y: 79,
    w: 60,
    h: 20,
    minW: 30,
    maxW: 60,
    minH: 16,
    maxH: 21,
  },
  report_supply_request_trend: {
    i: 'report_supply_request_trend',
    x: 0,
    y: 99,
    w: 60,
    h: 10,
    minW: 30,
    maxW: 60,
    minH: 9,
    maxH: 16,
  },
  report_supply_radar: {
    i: 'report_supply_radar',
    x: 0,
    y: 109,
    w: 30,
    h: 18,
    minW: 24,
    maxW: 60,
    minH: 13,
    maxH: 26,
  },
  report_supply_aging: {
    i: 'report_supply_aging',
    x: 30,
    y: 109,
    w: 30,
    h: 16,
    minW: 24,
    maxW: 60,
    minH: 13,
    maxH: 24,
  },
  report_supply_performance: {
    i: 'report_supply_performance',
    x: 0,
    y: 127,
    w: 60,
    h: 12,
    minW: 30,
    maxW: 60,
    minH: 10,
    maxH: 18,
  },
  report_refund_balance_summary: {
    i: 'report_refund_balance_summary',
    x: 0,
    y: 139,
    w: 30,
    h: 18,
    minW: 20,
    maxW: 42,
    minH: 14,
    maxH: 28,
  },
  report_refund_reason_distribution: {
    i: 'report_refund_reason_distribution',
    x: 30,
    y: 139,
    w: 30,
    h: 20,
    minW: 20,
    maxW: 42,
    minH: 15,
    maxH: 30,
  },
  report_marketplace_product_errors: {
    i: 'report_marketplace_product_errors',
    x: 30,
    y: 159,
    w: 26,
    h: 17,
    minW: 20,
    maxW: 26,
    minH: 14,
    maxH: 24,
  },
}

const localDashboardDefinitions: DashboardDefinition[] = [
  {
    id: marketplaceProductErrorsDashboardId,
    code: 'report_marketplace_product_errors',
    platform: 'all',
    platforms: ['all'],
    scope: 'firm',
    name: 'Pazaryeri Ürün Hataları',
    title: 'PAZARYERİ ÜRÜN HATALARI',
    description: 'Aktif ürün hataları, son pazaryeri cevapları ve hata grupları.',
    component_key: 'marketplace_product_errors',
    sort: 68,
    is_default: true,
    supported_surfaces: ['web'],
    settings: {
      grid_w: 26,
      grid_h: 17,
      min_grid_w: 20,
      max_grid_w: 26,
      min_grid_h: 14,
      max_grid_h: 24,
    },
  },
]

type DashboardWidgetState<T> = {
  data: T | null
  loading: boolean
  error: string
}

function useDashboardInView(rootMargin = '160px') {
  const ref = useRef<HTMLElement | null>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (!element || visible) return
    if (typeof IntersectionObserver === 'undefined') {
      queueMicrotask(() => setVisible(true))
      return
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        setVisible(true)
        observer.disconnect()
      }
    }, { rootMargin })
    observer.observe(element)
    return () => observer.disconnect()
  }, [rootMargin, visible])

  return { ref, visible }
}

function useDashboardWidgetData<T>({
  endpoint,
  params,
  enabled,
}: {
  endpoint: string
  params: Record<string, string>
  enabled: boolean
}) {
  const paramKey = useMemo(() => JSON.stringify(params), [params])
  const { user } = useUser()
  const embedToken = useEmbedToken()
  const scopeKey = embedToken ? `embed:${embedToken.slice(-18)}` : `${user?.firm_id || 0}:${user?.firm_store_id || 0}`
  const cacheKey = `${scopeKey}:${endpoint}?${paramKey}`
  const [state, setState] = useState<DashboardWidgetState<T>>(() => ({
    data: readDashboardWidgetCache<T>(cacheKey),
    loading: false,
    error: '',
  }))

  useEffect(() => {
    const cached = readDashboardWidgetCache<T>(cacheKey)
    if (cached) {
      queueMicrotask(() => setState({ data: cached, loading: false, error: '' }))
      return
    }
    if (!enabled) return

    const token = embedToken || localStorage.getItem('auth_token')
    const queryParams = JSON.parse(paramKey) as Record<string, string>
    const query = new URLSearchParams(queryParams)
    const controller = new AbortController()

    queueMicrotask(() => setState({ data: null, loading: true, error: '' }))
    fetch(`${API_BASE}${endpoint}?${query.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      cache: 'no-store',
      signal: controller.signal,
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => null)
        if (!response.ok || payload?.success === false) {
          throw new Error(payload?.message || 'Dashboard verisi alınamadı')
        }
        return (payload?.data ?? payload) as T
      })
      .then((data) => {
        writeDashboardWidgetCache(cacheKey, data)
        setState({ data, loading: false, error: '' })
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return
        setState((current) => ({
          data: current.data,
          loading: false,
          error: error?.message || 'Dashboard verisi alınamadı',
        }))
      })

    return () => controller.abort()
  }, [cacheKey, embedToken, enabled, endpoint, paramKey])

  return state
}

type QuickActionCard = {
  key?: string
  label?: string
  icon?: string
  path?: string
  description?: string
  accent?: string
}

type QuickActionNav = {
  id: number
  top_id?: number | null
  platform?: string | null
  name: string
  desc?: string | null
  url?: string | null
  path?: string | null
  icon?: string | null
  sort?: number | null
  nav?: number | null
  status?: number | null
}

type KpiItem = {
  label?: string
  value?: string
  delta?: string
  icon?: string
  accent?: string
}

type TrendPoint = {
  label?: string
  value?: number
}

type ActivityItem = {
  label?: string
  meta?: string
  status?: string
  icon?: string
  accent?: string
}

type ReportKpi = {
  label: string
  value: string
  delta?: string
  tone?: 'blue' | 'green' | 'red' | 'orange' | 'purple'
}

type ReportSeriesPoint = {
  label: string
  value: number
  value2?: number
}

type ChartStudioSpec = {
  data: unknown
  code: string
}

type DashboardPreferences = {
  visible_dashboard_keys?: string[]
  completed_dashboard_keys?: string[]
  grid_layouts?: Record<string, Partial<DashboardGridItemLayout>>
  quick_actions?: Record<string, number[]>
  widget_settings?: Record<string, unknown>
}

type DashboardMainTab = 'setup' | 'statistics'

type SetupOffer = {
  id?: number
  title: string
  text?: string
  cta?: string
  logo?: string
  note?: string
  price?: string
  icon?: string
}

type SetupGuideStep = {
  title: string
  text: string
  action: string
  completed?: boolean
}

type SetupModuleApiItem = {
  id?: number | string
  name?: string
  display_name?: string
  short_desc?: string
}

type SetupStatus = {
  setupCompleted: boolean
  completedSteps: number
  totalSteps: number
  progressPercent: number
}

const setupGuideSteps: SetupGuideStep[] = [
  { title: 'Ürün Ekleme', text: 'Online mağazanızda satışa başlamak için ürünlerinizi ekleyin.', action: 'Yapılandır' },
  { title: 'Ödeme Yöntemi Belirleme', text: 'Müşterileriniz için en güvenilir ödeme yöntemlerini belirleyin.', action: 'Yapılandır' },
  { title: 'Kargo Yöntemi Ekleme', text: 'Gönderim yapacağınız kargo firmalarını seçin, entegrasyonları tamamlayın.', action: 'Yapılandır' },
  { title: 'Tema Seçimi', text: 'Zengin tema kütüphanesinden işletmenize uygun bir tema seçin.', action: 'Yapılandır' },
  { title: 'İnceleme ve Yayınlama', text: 'Alan adı, sözleşmeler, SEO vb. ayarlamaları yapıp sitenizi yayına hazırlayın.', action: 'Yapılandır' },
]

function SetupInlineOptions({ items, loading }: { items: SetupOffer[]; loading?: boolean }) {
  const [page, setPage] = useState(0)
  const pageSize = 4
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize))
  const activePage = Math.min(page, pageCount - 1)
  const visibleItems = items.slice(activePage * pageSize, activePage * pageSize + pageSize)

  if (loading) {
    return <div className="dashboard-setup-inline-empty">Modüller yükleniyor...</div>
  }

  if (!items.length) {
    return <div className="dashboard-setup-inline-empty">Tanımlı modül bulunamadı.</div>
  }

  return (
    <div className="dashboard-setup-module-slider">
      <button
        type="button"
        className="dashboard-setup-module-slider__arrow"
        onClick={() => setPage((value) => Math.max(0, value - 1))}
        disabled={activePage === 0}
        aria-label="Önceki modüller"
      >
        <i className="fa-regular fa-chevron-left" />
      </button>
      <div className="dashboard-setup-inline-options">
        {visibleItems.map((item) => (
          <article key={item.id || item.title} className="dashboard-setup-offer-card">
            <strong>
              <i className={item.icon || 'fa-regular fa-truck-fast'} />
              <span>{item.logo || item.title}</span>
            </strong>
            <button type="button" className="dashboard-setup-offer-card__add">{item.cta || 'Ekle'}</button>
          </article>
        ))}
      </div>
      <button
        type="button"
        className="dashboard-setup-module-slider__arrow"
        onClick={() => setPage((value) => Math.min(pageCount - 1, value + 1))}
        disabled={activePage >= pageCount - 1}
        aria-label="Sonraki modüller"
      >
        <i className="fa-regular fa-chevron-right" />
      </button>
    </div>
  )
}

function SetupDashboard({
  storeName,
  paymentOptions,
  cargoOptions,
  setupModulesLoading,
  setupStatus,
}: {
  storeName?: string | null
  paymentOptions: SetupOffer[]
  cargoOptions: SetupOffer[]
  setupModulesLoading?: boolean
  setupStatus?: SetupStatus | null
}) {
  const completedSteps = setupStatus?.completedSteps ?? setupGuideSteps.filter((step) => step.completed).length
  const totalSteps = setupStatus?.totalSteps ?? setupGuideSteps.length
  const progress = setupStatus?.progressPercent ?? Math.round((completedSteps / Math.max(1, totalSteps)) * 100)

  return (
    <div className="dashboard-setup-dashboard">
      <section className="dashboard-setup-trial">
        <div>
          <span className="dashboard-setup-trial__badge">Demo paketi</span>
          <strong>Kalan <b>29 Gün</b></strong>
          <small>Mağazanızı yayına hazırlamak için başlangıç süreniz devam ediyor.</small>
        </div>
        <div>
          <button type="button">Satın Al</button>
          <span>13.07.2026</span>
        </div>
      </section>

      <section className="dashboard-setup-ecosystem">
        <div>
          <span className="dashboard-setup-ecosystem__badge">Market</span>
          <h2>Uygulama Marketi</h2>
          <p>İşletmenizi büyütmek için uygulamalara, entegrasyonlara ve destekleyici servislere göz atın.</p>
          <button type="button" onClick={() => { window.location.href = '/market' }}>Markete Git</button>
        </div>
        <div className="dashboard-setup-ecosystem__preview" aria-hidden="true">
          <span>PAYTR</span>
          <span>Tema</span>
          <span>Kargo</span>
          <span>Uygulama</span>
        </div>
      </section>

      <section className="dashboard-setup-guide">
        <div className="dashboard-setup-guide__head">
          <div>
            <h2>{storeName || 'Mağazanız'} için başlangıç rehberi</h2>
            <p>Online mağazanızı hızlı ve kolay şekilde oluşturun.</p>
          </div>
          <div className="dashboard-setup-progress">
            <strong>%{progress}</strong>
            <span><i style={{ width: `${progress}%` }} /></span>
            <small>{completedSteps}/{totalSteps} adım tamamlandı</small>
          </div>
          <button type="button">Başlangıç işlemlerini atla</button>
        </div>
        <div className="dashboard-setup-guide__steps">
          {setupGuideSteps.map((step, index) => (
            <article key={step.title} className={index < completedSteps ? 'is-completed' : ''}>
              <em>{index + 1}</em>
              <div className="dashboard-setup-guide__step-main">
                <span>
                  <strong>{step.title}</strong>
                  <small>{step.text}</small>
                </span>
                {step.title === 'Ödeme Yöntemi Belirleme' && (
                  <SetupInlineOptions items={paymentOptions} loading={setupModulesLoading} />
                )}
                {step.title === 'Kargo Yöntemi Ekleme' && (
                  <SetupInlineOptions items={cargoOptions} loading={setupModulesLoading} />
                )}
              </div>
              <button type="button">{step.action}</button>
            </article>
          ))}
        </div>
      </section>
    </div>
  )
}

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/)
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase()
  return name.substring(0, 2).toUpperCase()
}

function dashboardKey(dashboard: DashboardDefinition) {
  return dashboard.code || `dashboard-${dashboard.id}`
}

function dashboardTitle(dashboard: DashboardDefinition) {
  const title = dashboard.title || dashboard.name || 'Dashboard'
  return title
}

function dashboardDescription(dashboard: DashboardDefinition) {
  if (dashboard.component_key === 'quick_actions') {
    return dashboard.description || 'Sık kullandığınız menüleri seçip ana ekranda hızlı erişim olarak kullanın.'
  }
  return dashboard.description || ''
}

function chartStudioStorageKey(componentKey: string) {
  return `panel_chart_studio_${componentKey}`
}

function stableStringify(value: unknown) {
  return JSON.stringify(value, null, 2)
}

function readStoredChartStudio<T>(componentKey: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback
  try {
    const raw = window.localStorage.getItem(chartStudioStorageKey(componentKey))
    if (!raw) return fallback
    const parsed = JSON.parse(raw)
    return (parsed?.data ?? fallback) as T
  } catch {
    return fallback
  }
}

function readStoredChartCode(componentKey: string, fallback: string) {
  if (typeof window === 'undefined') return fallback
  try {
    const raw = window.localStorage.getItem(chartStudioStorageKey(componentKey))
    if (!raw) return fallback
    const parsed = JSON.parse(raw)
    return typeof parsed?.code === 'string' ? parsed.code : fallback
  } catch {
    return fallback
  }
}

function useDashboardStudioData<T>(_componentKey: string, fallback: T): T {
  return fallback
}

function buildChartStudioCode(componentKey: string, data: unknown) {
  return [
    `// ${componentKey}`,
    '// Edit chartConfig, then Save. The dashboard reads this object immediately.',
    `const chartConfig = ${stableStringify(data)};`,
  ].join('\n')
}

function executeChartStudioCode(code: string) {
  // Admin-only dashboard studio. Prefer chartConfig object, fall back to JSON editor.
  return Function(`"use strict";\n${code}\n;return typeof chartConfig !== "undefined" ? chartConfig : null;`)()
}

function getDefaultChartStudioSpec(componentKey: string): ChartStudioSpec {
  const dataByKey: Record<string, unknown> = {
    saas_total_revenue: {
      selectedIndex: 20,
      rows: [
        { label: 'Jan', value: 1 }, { label: '', value: 2.6 }, { label: '', value: 1.6 },
        { label: 'Feb', value: 3.1 }, { label: '', value: 4.5 }, { label: '', value: 3 },
        { label: 'Mar', value: 4.6 }, { label: '', value: 10 }, { label: '', value: 18 },
        { label: 'Apr', value: 30 }, { label: '', value: 30 }, { label: '', value: 42 },
        { label: 'May', value: 30 }, { label: '', value: 30 }, { label: '', value: 14 },
        { label: 'Jun', value: 7.5 }, { label: '', value: 8.2 }, { label: '', value: 18 },
        { label: 'Jul', value: 30 }, { label: '', value: 42 }, { label: '', value: 53 },
        { label: 'Aug', value: 70 }, { label: '', value: 75 }, { label: '', value: 80 },
        { label: 'Sep', value: 66 }, { label: '', value: 52 }, { label: '', value: 50 },
        { label: 'Oct', value: 50 }, { label: '', value: 58 }, { label: '', value: 50 },
        { label: 'Nov', value: 60 }, { label: '', value: 70 }, { label: '', value: 92 },
        { label: 'Dec', value: 92 }, { label: '', value: 230 },
      ],
    },
    crm_lost_deals_analysis: {
      rows: [
        { name: 'Budget Constrains', value: 31, color: '#6fd7ea' },
        { name: 'Went with Competitor', value: 25, color: '#3f65dd' },
        { name: 'No Decision', value: 22, color: '#9b87e9' },
        { name: 'Missing Features', value: 15, color: '#e083ec' },
        { name: 'Bad Timing', value: 7, color: '#cfd8e3' },
      ],
    },
    crm_sales_leaderboard: {
      rows: [
        { name: 'Sarah Kim', value: 122000, avatar: 'SK' },
        { name: 'Daniel Porter', value: 98500, avatar: 'DP' },
        { name: 'Maya Singh', value: 85000, avatar: 'MS' },
        { name: 'Alex Boyd', value: 66500, avatar: 'AB' },
        { name: 'Rayna Rosser', value: 63700, avatar: 'RR' },
        { name: 'Talan Lubin', value: 31890, avatar: 'TL' },
        { name: 'Liam Chen', value: 22260, avatar: 'LC' },
      ],
    },
    crm_revenue_performance: {
      rows: [
        { month: 'May', revenue: 150, forecast: null },
        { month: 'Jun', revenue: 124, forecast: null },
        { month: 'Jul', revenue: 138, forecast: null },
        { month: 'Aug', revenue: 170, forecast: null },
        { month: 'Sept', revenue: 156, forecast: null },
        { month: 'Oct', revenue: 135, forecast: null },
        { month: 'Nov', revenue: 159, forecast: 159 },
        { month: 'Dec', revenue: null, forecast: 148 },
        { month: 'Jan', revenue: null, forecast: 178 },
        { month: 'Feb', revenue: null, forecast: 191 },
        { month: 'Mar', revenue: null, forecast: 171 },
        { month: 'Apr', revenue: null, forecast: 180 },
      ],
    },
  }
  const data = dataByKey[componentKey] ?? { settings: {}, rows: [] }
  return { data, code: buildChartStudioCode(componentKey, data) }
}

function defaultDashboardKeys(dashboards: DashboardDefinition[]) {
  return dashboards.map(dashboardKey)
}

function readArraySetting<T>(dashboard: DashboardDefinition, key: string): T[] {
  const value = dashboard.settings?.[key]
  return Array.isArray(value) ? value as T[] : []
}

function readNumberSetting(dashboard: DashboardDefinition, key: string, fallback: number) {
  const value = dashboard.settings?.[key]
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

function readBooleanSetting(dashboard: DashboardDefinition, key: string, fallback = true) {
  const value = dashboard.settings?.[key]
  return typeof value === 'boolean' ? value : fallback
}

function dashboardGridLimits(dashboard: DashboardDefinition) {
  const key = dashboard.component_key || ''
  if (dashboard.id === 62) {
    return {
      w: readNumberSetting(dashboard, 'grid_w', 60),
      h: Math.max(readNumberSetting(dashboard, 'grid_h', 30), 16),
      minW: Math.max(readNumberSetting(dashboard, 'min_grid_w', 30), 30),
      maxW: readNumberSetting(dashboard, 'max_grid_w', 60),
      minH: Math.min(readNumberSetting(dashboard, 'min_grid_h', 16), 16),
      maxH: Math.max(readNumberSetting(dashboard, 'max_grid_h', 60), 60),
    }
  }
  if (dashboard.id === 63) {
    return {
      w: readNumberSetting(dashboard, 'grid_w', 32),
      h: readNumberSetting(dashboard, 'grid_h', 22),
      minW: 18,
      maxW: readNumberSetting(dashboard, 'max_grid_w', 42),
      minH: 16,
      maxH: readNumberSetting(dashboard, 'max_grid_h', 30),
    }
  }
  if (dashboard.id === 64) {
    return {
      w: readNumberSetting(dashboard, 'grid_w', 32),
      h: readNumberSetting(dashboard, 'grid_h', 20),
      minW: 16,
      maxW: readNumberSetting(dashboard, 'max_grid_w', 42),
      minH: 14,
      maxH: readNumberSetting(dashboard, 'max_grid_h', 28),
    }
  }
  if (dashboard.id === 65) {
    return {
      w: 32,
      h: 18,
      minW: 20,
      maxW: 42,
      minH: 15,
      maxH: 28,
    }
  }
  if (dashboard.id === 66) {
    return {
      w: 32,
      h: 18,
      minW: 20,
      maxW: 42,
      minH: 15,
      maxH: 28,
    }
  }
  if (dashboard.id === 67) {
    return {
      w: 32,
      h: 18,
      minW: 20,
      maxW: 42,
      minH: 15,
      maxH: 28,
    }
  }
  if (dashboard.id === 58) {
    return {
      w: 42,
      h: 22,
      minW: 22,
      maxW: 60,
      minH: 16,
      maxH: 30,
    }
  }
  if (dashboard.id === 47) {
    return {
      w: 32,
      h: 18,
      minW: 16,
      maxW: 42,
      minH: 13,
      maxH: 28,
    }
  }
  if (dashboard.id === 51) {
    return {
      w: 42,
      h: 20,
      minW: 22,
      maxW: 60,
      minH: 16,
      maxH: 30,
    }
  }
  if (dashboard.id === operationFlowDashboardId || dashboard.component_key === 'operation_flow') {
    return {
      w: readNumberSetting(dashboard, 'grid_w', 60),
      h: readNumberSetting(dashboard, 'grid_h', 22),
      minW: readNumberSetting(dashboard, 'min_grid_w', 30),
      maxW: readNumberSetting(dashboard, 'max_grid_w', 60),
      minH: readNumberSetting(dashboard, 'min_grid_h', 18),
      maxH: readNumberSetting(dashboard, 'max_grid_h', 30),
    }
  }
  if (dashboard.id === outboundCustomerServiceDashboardId || dashboard.component_key === 'outbound_customer_service') {
    return {
      w: readNumberSetting(dashboard, 'grid_w', 60),
      h: Math.min(readNumberSetting(dashboard, 'grid_h', 20), 20),
      minW: readNumberSetting(dashboard, 'min_grid_w', 30),
      maxW: readNumberSetting(dashboard, 'max_grid_w', 60),
      minH: Math.min(readNumberSetting(dashboard, 'min_grid_h', 16), 16),
      maxH: Math.min(readNumberSetting(dashboard, 'max_grid_h', 21), 21),
    }
  }
  if (dashboard.id === supplyRadarDashboardId || dashboard.component_key === 'supply_radar') {
    return {
      w: readNumberSetting(dashboard, 'grid_w', 30),
      h: readNumberSetting(dashboard, 'grid_h', 18),
      minW: readNumberSetting(dashboard, 'min_grid_w', 24),
      maxW: readNumberSetting(dashboard, 'max_grid_w', 60),
      minH: readNumberSetting(dashboard, 'min_grid_h', 13),
      maxH: readNumberSetting(dashboard, 'max_grid_h', 26),
    }
  }
  if (dashboard.id === supplyAgingDashboardId || dashboard.component_key === 'supply_aging') {
    return {
      w: readNumberSetting(dashboard, 'grid_w', 30),
      h: readNumberSetting(dashboard, 'grid_h', 16),
      minW: readNumberSetting(dashboard, 'min_grid_w', 24),
      maxW: readNumberSetting(dashboard, 'max_grid_w', 60),
      minH: readNumberSetting(dashboard, 'min_grid_h', 13),
      maxH: readNumberSetting(dashboard, 'max_grid_h', 24),
    }
  }
  if (dashboard.id === supplyPerformanceDashboardId || dashboard.component_key === 'supply_performance') {
    return {
      w: readNumberSetting(dashboard, 'grid_w', 60),
      h: readNumberSetting(dashboard, 'grid_h', 12),
      minW: readNumberSetting(dashboard, 'min_grid_w', 30),
      maxW: readNumberSetting(dashboard, 'max_grid_w', 60),
      minH: readNumberSetting(dashboard, 'min_grid_h', 10),
      maxH: readNumberSetting(dashboard, 'max_grid_h', 18),
    }
  }
  if (dashboard.id === marketplaceProductErrorsDashboardId || dashboard.component_key === 'marketplace_product_errors') {
    return {
      w: readNumberSetting(dashboard, 'grid_w', 26),
      h: readNumberSetting(dashboard, 'grid_h', 17),
      minW: readNumberSetting(dashboard, 'min_grid_w', 20),
      maxW: readNumberSetting(dashboard, 'max_grid_w', 26),
      minH: readNumberSetting(dashboard, 'min_grid_h', 14),
      maxH: readNumberSetting(dashboard, 'max_grid_h', 24),
    }
  }
  if (dashboard.id === refundBalanceDashboardId || dashboard.component_key === 'refund_balance_summary') {
    return {
      w: readNumberSetting(dashboard, 'grid_w', 30),
      h: readNumberSetting(dashboard, 'grid_h', 18),
      minW: readNumberSetting(dashboard, 'min_grid_w', 20),
      maxW: readNumberSetting(dashboard, 'max_grid_w', 42),
      minH: readNumberSetting(dashboard, 'min_grid_h', 14),
      maxH: readNumberSetting(dashboard, 'max_grid_h', 28),
    }
  }
  if (dashboard.id === refundReasonDashboardId || dashboard.component_key === 'refund_reason_distribution') {
    return {
      w: readNumberSetting(dashboard, 'grid_w', 30),
      h: readNumberSetting(dashboard, 'grid_h', 20),
      minW: readNumberSetting(dashboard, 'min_grid_w', 20),
      maxW: readNumberSetting(dashboard, 'max_grid_w', 42),
      minH: readNumberSetting(dashboard, 'min_grid_h', 15),
      maxH: readNumberSetting(dashboard, 'max_grid_h', 30),
    }
  }
  if (isMockChartDashboard(dashboard)) {
    return {
      w: 32,
      h: 20,
      minW: 18,
      maxW: 52,
      minH: 16,
      maxH: 30,
    }
  }
  const reportDefaults = {
    w: 30,
    h: 15,
    minW: 18,
    maxW: 60,
    minH: 9,
    maxH: 34,
  }

  const defaults = key === 'quick_actions'
    ? { w: 24, h: 12, minW: 10, maxW: 60, minH: 7, maxH: 20 }
    : key === 'kpi_strip'
      ? { w: 30, h: 10, minW: 10, maxW: 34, minH: 8, maxH: 34 }
      : key === 'activity_list'
        ? { w: 30, h: 14, minW: 20, maxW: 60, minH: 9, maxH: 30 }
        : key === 'trend_bars'
          ? { w: 30, h: 14, minW: 20, maxW: 60, minH: 10, maxH: 28 }
          : reportDefaults

  return {
    w: readNumberSetting(dashboard, 'grid_w', defaults.w),
    h: readNumberSetting(dashboard, 'grid_h', defaults.h),
    minW: readNumberSetting(dashboard, 'min_grid_w', defaults.minW),
    maxW: readNumberSetting(dashboard, 'max_grid_w', defaults.maxW),
    minH: readNumberSetting(dashboard, 'min_grid_h', defaults.minH),
    maxH: readNumberSetting(dashboard, 'max_grid_h', defaults.maxH),
  }
}

function normalizeGridItem(dashboard: DashboardDefinition, item?: Partial<DashboardGridItemLayout> | null): DashboardGridItemLayout {
  const key = dashboardKey(dashboard)
  const limits = dashboardGridLimits(dashboard)
  const x = Number(item?.x)
  const y = Number(item?.y)
  const w = Number(item?.w)
  const h = Number(item?.h)
  const width = Math.min(Math.max(Number.isFinite(w) ? w : limits.w, limits.minW), limits.maxW, dashboardGridCols)
  const minHeight = dashboard.id === 11
    ? width <= 15
      ? 18
      : width <= 22
        ? 13
        : limits.minH
    : limits.minH
  const height = Math.min(Math.max(Number.isFinite(h) ? h : limits.h, minHeight), limits.maxH)

  return {
    i: key,
    x: Number.isFinite(x) ? Math.max(0, Math.min(x, dashboardGridCols - width)) : 0,
    y: Number.isFinite(y) ? Math.max(0, y) : 0,
    w: width,
    h: height,
    minW: limits.minW,
    maxW: Math.min(limits.maxW, dashboardGridCols),
    minH: minHeight,
    maxH: limits.maxH,
  }
}

function createDefaultGridLayouts(
  dashboards: DashboardDefinition[],
  visibleKeys: string[],
  savedLayouts: Record<string, Partial<DashboardGridItemLayout>> = {}
) {
  const byKey = new Map(dashboards.map((dashboard) => [dashboardKey(dashboard), dashboard]))
  const next: Record<string, DashboardGridItemLayout> = {}
  let nextY = 0

  visibleKeys.forEach((key) => {
    const dashboard = byKey.get(key)
    if (!dashboard) return
    const saved = savedLayouts[key]
    const item = normalizeGridItem(dashboard, saved)
    if (!saved) item.y = nextY
    next[key] = item
    nextY = Math.max(nextY, item.y + item.h)
  })

  return next
}

function parseSavedGridLayouts(value: string | null) {
  if (!value) return {}
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as Record<string, Partial<DashboardGridItemLayout>>
      : {}
  } catch {
    return {}
  }
}

function parseDashboardGridLayouts(value: unknown) {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, Partial<DashboardGridItemLayout>>
    : {}
}

function normalizeVisibleDashboardKeys(value: unknown, availableDashboards: DashboardDefinition[]) {
  if (!Array.isArray(value)) return null
  const available = new Set(availableDashboards.map(dashboardKey))
  return value.map(String).filter((key) => available.has(key))
}

function normalizeCompletedDashboardKeys(value: unknown, availableDashboards: DashboardDefinition[]) {
  if (!Array.isArray(value)) return []
  const available = new Set(availableDashboards.map(dashboardKey))
  return [...new Set(value.map(String).filter((key) => available.has(key) && completedDashboardIds.has(Number(key))))] 
}

function DashboardWorkspace({
  dashboards,
  visibleDashboards,
  visibleDashboardKeys,
  completedDashboardKeys,
  dashboardGridLayouts,
  orderedCustomizerDashboards,
  customizing,
  loading,
  error,
  onToggleDashboard,
  onLayoutChange,
  onCustomizerClose,
  onNavigate,
  quickActionNavItems,
  userId,
  canArrange,
}: {
  dashboards: DashboardDefinition[]
  visibleDashboards: DashboardDefinition[]
  visibleDashboardKeys: string[]
  completedDashboardKeys: string[]
  dashboardGridLayouts: Record<string, DashboardGridItemLayout>
  orderedCustomizerDashboards: DashboardDefinition[]
  customizing: boolean
  loading: boolean
  error: string
  onToggleDashboard: (key: string, checked: boolean) => void
  onLayoutChange: (layout: Layout) => void
  onCustomizerClose: () => void
  onNavigate: (path: string) => void
  quickActionNavItems: QuickActionNav[]
  userId?: number | string
  canArrange: boolean
}) {
  const [isMobileDashboard, setIsMobileDashboard] = useState(false)

  useEffect(() => {
    const updateMobileState = () => {
      setIsMobileDashboard(window.innerWidth <= dashboardMobileBreakpoint)
    }

    updateMobileState()
    window.addEventListener('resize', updateMobileState)
    return () => window.removeEventListener('resize', updateMobileState)
  }, [])

  const renderedLayout = useMemo(() => {
    if (!isMobileDashboard) {
      return visibleDashboards.map((dashboard) => {
        const key = dashboardKey(dashboard)
        return normalizeGridItem(dashboard, dashboardGridLayouts[key] || null)
      })
    }

    let nextY = 0
    return visibleDashboards.map((dashboard) => {
      const key = dashboardKey(dashboard)
      const item = normalizeGridItem(dashboard, dashboardGridLayouts[key] || null)
      const mobileItem: DashboardGridItemLayout = {
        ...item,
        x: 0,
        y: nextY,
        w: 1,
        minW: 1,
        maxW: 1,
      }
      nextY += mobileItem.h
      return mobileItem
    })
  }, [dashboardGridLayouts, isMobileDashboard, visibleDashboards])

  const customizerModal = customizing ? (
    <div className="panel-dashboard-customizer-backdrop" role="presentation" onMouseDown={onCustomizerClose}>
      <section
        className="panel-dashboard-customizer"
        role="dialog"
        aria-modal="true"
        aria-label="Dashboardları seç"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="panel-dashboard-customizer__head">
          <div>
            <strong>Dashboardları Seç</strong>
            <small>Dashboard ekleyip çıkarırken ekran arkada görünür kalır.</small>
          </div>
          <span>{visibleDashboardKeys.length}/{dashboards.length}</span>
          <button type="button" onClick={onCustomizerClose} aria-label="Kapat">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        {dashboards.length === 0 ? (
          <div className="panel-dashboard-customizer__empty">
            <i className="fa-duotone fa-lock" />
            <strong>Seçilebilir dashboard yok</strong>
            <span>Bu kullanıcıya veya bağlı departmanlarına dashboard yetkisi atanmamış.</span>
          </div>
        ) : (
          <div className="panel-dashboard-customizer__list">
            {orderedCustomizerDashboards.map((dashboard) => {
              const key = dashboardKey(dashboard)
              const checked = visibleDashboardKeys.includes(key)
              const completed = completedDashboardKeys.includes(key) || completedDashboardIds.has(Number(dashboard.id))
              const platforms = Array.isArray(dashboard.platforms) && dashboard.platforms.length
                ? dashboard.platforms.join(', ')
                : dashboard.platform
              return (
                <div key={key} className={`panel-dashboard-customizer__item ${completed ? 'is-completed' : ''}`}>
                  <label className="panel-dashboard-customizer__select">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(event) => onToggleDashboard(key, event.target.checked)}
                    />
                    <span>
                      <strong>
                        <em>#{dashboard.id}</em>
                        {dashboardTitle(dashboard)}
                      </strong>
                      <small>{dashboard.description || dashboard.component_key || platforms}</small>
                    </span>
                  </label>
                  <span className="panel-dashboard-customizer__controls">
                    <label className="panel-dashboard-customizer__done" title="Tamamlandı">
                      <input
                        type="checkbox"
                        checked={completed}
                        disabled
                        readOnly
                        aria-label={`${dashboardTitle(dashboard)} tamamlandı`}
                      />
                      <span>Tamam</span>
                    </label>
                    {checked && <em className="panel-dashboard-customizer__status">Ekranda</em>}
                  </span>
                </div>
              )
            })}
          </div>
        )}
        <div className="panel-dashboard-customizer__foot">
          <button type="button" onClick={onCustomizerClose}>Tamam</button>
        </div>
      </section>
    </div>
  ) : null

  if (loading) {
    return <div className="panel-dashboard-state">Dashboardlar yükleniyor...</div>
  }

  if (error) {
    return <div className="panel-dashboard-state panel-dashboard-state--error">{error}</div>
  }

  if (dashboards.length === 0 && !customizing) {
    return <div className="panel-dashboard-state">Yetkili dashboard bulunmuyor.</div>
  }

  return (
    <div className={`panel-dashboard-workspace ${canArrange ? 'can-arrange' : 'is-locked'}`}>
      {customizerModal && typeof document !== 'undefined' && createPortal(customizerModal, document.body)}

      {visibleDashboards.length > 0 ? (
        isMobileDashboard ? (
          <div className="panel-dashboard-mobile-list">
            {visibleDashboards.map((dashboard) => {
              const key = dashboardKey(dashboard)
              const layoutItem = normalizeGridItem(dashboard, dashboardGridLayouts[key] || null)
              return (
                <div
                  key={key}
                  className="panel-dashboard-mobile-item"
                >
                  <DashboardRenderer
                    dashboard={dashboard}
                    onNavigate={onNavigate}
                    quickActionNavItems={quickActionNavItems}
                    userId={userId}
                    layout={layoutItem}
                  />
                </div>
              )
            })}
          </div>
        ) : (
          <div className="panel-dashboard-grid-wrap">
          <DashboardGridLayout
            className="panel-dashboard-widget-grid"
            cols={dashboardGridCols}
            rowHeight={dashboardGridRowHeight}
            margin={[0, 0]}
            containerPadding={[0, 0]}
            isDraggable={canArrange}
            isResizable={canArrange}
            draggableHandle={canArrange ? ".panel-dashboard-block__head" : undefined}
            draggableCancel=".panel-dashboard-studio-btn, .quick-actions-drawer, .channel-orders-scroll, .channel-orders-scroll *, input, button"
            resizeHandles={['se', 'e', 's']}
            compactType="vertical"
            preventCollision={false}
            layout={renderedLayout}
            onDragStop={onLayoutChange}
            onResizeStop={onLayoutChange}
          >
            {visibleDashboards.map((dashboard) => {
              const key = dashboardKey(dashboard)
              const layoutItem = renderedLayout.find((item) => item.i === key) || normalizeGridItem(dashboard, dashboardGridLayouts[key] || null)
              return (
                <div key={key} className="panel-dashboard-widget">
                  <DashboardRenderer
                    dashboard={dashboard}
                    onNavigate={onNavigate}
                    quickActionNavItems={quickActionNavItems}
                    userId={userId}
                    layout={layoutItem}
                  />
                </div>
              )
            })}
          </DashboardGridLayout>
          </div>
        )
      ) : (
        <div className="panel-dashboard-state">
          {canArrange
            ? 'Dashboard seçili değil. Dashboardları Seç ile kendi ekranını oluşturabilirsin.'
            : 'Gösterilecek dashboard bulunmuyor.'}
        </div>
      )}
    </div>
  )
}

function DashboardBlock({
  dashboard,
  children,
  action,
  layout,
}: {
  dashboard: DashboardDefinition
  children: ReactNode
  action?: ReactNode
  layout?: DashboardGridItemLayout
}) {
  const componentKey = dashboard.component_key || dashboard.code || `dashboard-${dashboard.id}`
  const description = dashboardDescription(dashboard)

  return (
    <section className={`panel-dashboard-block panel-dashboard-block--${componentKey.replace(/[^a-zA-Z0-9_-]/g, '-')}`}>
      <div className="panel-dashboard-block__head">
        <div>
          <strong>{dashboardTitle(dashboard)}</strong>
          {description && <span>{description}</span>}
        </div>
        <div className="panel-dashboard-block__actions">
          {action}
        </div>
      </div>
      <div className="panel-dashboard-block__body">
        {children}
      </div>
    </section>
  )
}

function navTarget(nav: QuickActionNav) {
  return nav.path || nav.url || ''
}

function navToQuickActionCard(nav: QuickActionNav): QuickActionCard {
  return {
    key: `nav-${nav.id}`,
    label: nav.name,
    icon: nav.icon || 'fa-duotone fa-ellipsis',
    path: navTarget(nav),
    description: nav.desc || '',
    accent: 'blue',
  }
}

function quickActionDescription(value?: string | null) {
  const text = String(value || '').trim()
  return text || '---'
}

function QuickActionsDashboard({
  dashboard,
  onNavigate,
  navItems,
  userId,
  layout,
}: {
  dashboard: DashboardDefinition
  onNavigate: (path: string) => void
  navItems: QuickActionNav[]
  userId?: number | string
  layout?: DashboardGridItemLayout
}) {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [storedIds, setStoredIds] = useState<number[] | null>(null)
  const [preferenceLoading, setPreferenceLoading] = useState(true)
  const [draftIds, setDraftIds] = useState<number[]>([])

  useEffect(() => {
    let cancelled = false
    const loadPreference = async () => {
      setPreferenceLoading(true)
      try {
        const token = window.localStorage.getItem('auth_token')
        const res = await fetch(`${API_BASE}/auth/dashboard-preferences/${dashboard.id}/quick-actions`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const data = await res.json().catch(() => null)
        if (!res.ok || !data?.success) throw new Error(data?.message || 'Hızlı işlem ayarları alınamadı')
        if (cancelled) return
        const navIds = Array.isArray(data.data?.nav_ids)
          ? data.data.nav_ids.map((item: unknown) => Number(item)).filter((item: number) => Number.isFinite(item))
          : null
        setStoredIds(navIds)
      } catch {
        if (!cancelled) {
          setStoredIds([])
        }
      } finally {
        if (!cancelled) setPreferenceLoading(false)
      }
    }

    loadPreference()
    return () => { cancelled = true }
  }, [dashboard.id])

  const selectableNavs = useMemo(() => (
    navItems.filter((item) => navTarget(item))
  ), [navItems])
  const selectableById = useMemo(() => new Map(selectableNavs.map((item) => [Number(item.id), item])), [selectableNavs])

  const cards = preferenceLoading
    ? []
    : (storedIds || [])
      .map((id) => selectableById.get(id))
      .filter(Boolean)
      .map((item) => navToQuickActionCard(item as QuickActionNav))

  const openDrawer = () => {
    setDraftIds((storedIds || []).filter((id) => selectableById.has(id)))
    setDrawerOpen(true)
  }

  const saveDrawer = async () => {
    const clean = [...new Set(draftIds)].filter((id) => selectableById.has(id))
    try {
      const token = window.localStorage.getItem('auth_token')
      const res = await fetch(`${API_BASE}/auth/dashboard-preferences/${dashboard.id}/quick-actions`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ nav_ids: clean }),
      })
      const data = await res.json().catch(() => null)
      if (!res.ok || !data?.success) throw new Error(data?.message || 'Hızlı işlem ayarları kaydedilemedi')
      const navIds = Array.isArray(data.data?.nav_ids)
        ? data.data.nav_ids.map((item: unknown) => Number(item)).filter((item: number) => Number.isFinite(item))
        : clean
      setStoredIds(navIds)
      setPreferenceLoading(false)
      setDrawerOpen(false)
    } catch (error) {
      console.error('Quick action preference save failed:', error)
    }
  }

  const groupedNavs = useMemo(() => {
    const byId = new Map(navItems.map((item) => [Number(item.id), item]))
    const groups = new Map<string, Map<string, QuickActionNav[]>>()
    selectableNavs.forEach((item) => {
      const platform = item.platform || 'Genel'
      const parent = Number(item.top_id || 0) > 0 ? byId.get(Number(item.top_id)) : null
      const parentName = parent?.name || 'Ana Menü'
      if (!groups.has(platform)) groups.set(platform, new Map())
      const platformGroups = groups.get(platform)!
      if (!platformGroups.has(parentName)) platformGroups.set(parentName, [])
      platformGroups.get(parentName)!.push(item)
    })
    return [...groups.entries()].map(([platform, platformGroups]) => ({
      platform,
      groups: [...platformGroups.entries()].map(([name, items]) => ({ name, items }))
    }))
  }, [navItems, selectableNavs])

  const drawer = drawerOpen ? (
    <div className="quick-actions-drawer-backdrop" role="presentation" onMouseDown={() => setDrawerOpen(false)}>
      <aside className="quick-actions-drawer" role="dialog" aria-modal="true" aria-label="Hızlı işlemleri düzenle" onMouseDown={(event) => event.stopPropagation()}>
        <div className="quick-actions-drawer__head">
          <div>
            <strong>Hızlı İşlemler</strong>
            <span>Yetkili menülerden dashboard butonlarını seç</span>
          </div>
          <button type="button" onClick={() => setDrawerOpen(false)} aria-label="Kapat">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <div className="quick-actions-drawer__body">
          {groupedNavs.length === 0 ? (
            <div className="quick-actions-drawer__empty">Seçilebilir menü bulunmuyor.</div>
          ) : groupedNavs.map((platformGroup) => (
            <section key={platformGroup.platform} className="quick-actions-drawer__platform">
              <h3>{platformGroup.platform}</h3>
              {platformGroup.groups.map((group) => (
                <div key={`${platformGroup.platform}-${group.name}`} className="quick-actions-drawer__group">
                  <strong>{group.name}</strong>
                  <div>
                    {group.items.map((item) => {
                      const id = Number(item.id)
                      const checked = draftIds.includes(id)
                      return (
                        <label key={id} className="quick-actions-drawer__item">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(event) => {
                              setDraftIds((current) => event.target.checked
                                ? [...new Set([...current, id])]
                                : current.filter((value) => value !== id)
                              )
                            }}
                          />
                          <i className={item.icon || 'fa-duotone fa-grid-2'} />
                          <span>
                            <b>{item.name}</b>
                            <small>{quickActionDescription(item.desc)}</small>
                          </span>
                        </label>
                      )
                    })}
                  </div>
                </div>
              ))}
            </section>
          ))}
        </div>
        <div className="quick-actions-drawer__foot">
          <span>{draftIds.length} seçili</span>
          <button type="button" onClick={() => setDraftIds([])}>Temizle</button>
          <button type="button" onClick={saveDrawer}>Kaydet</button>
        </div>
      </aside>
    </div>
  ) : null

  return (
    <DashboardBlock
      dashboard={dashboard}
      layout={layout}
      action={(
        <button type="button" className="panel-dashboard-studio-btn" onClick={openDrawer} aria-label="Hızlı işlemleri düzenle">
          <i className="fa-solid fa-sliders" />
        </button>
      )}
    >
      {drawer && typeof document !== 'undefined' && createPortal(drawer, document.body)}
      <div className={`panel-dashboard-quick-grid ${Number(layout?.w || 0) < 12 ? 'is-compact' : ''} ${Number(layout?.w || 0) >= 32 ? 'is-wide' : ''} ${Number(layout?.h || 0) <= 14 ? 'is-short' : ''}`}>
        {cards.map((card, index) => (
          <button
            key={card.key || `${card.label}-${index}`}
            type="button"
            className={`panel-dashboard-quick panel-dashboard-accent--${card.accent || 'blue'}`}
            onClick={() => card.path && onNavigate(card.path)}
          >
            <i className={card.icon || 'fa-duotone fa-ellipsis'} />
            <span>
              <strong>{card.label || 'Aksiyon'}</strong>
              <small>{quickActionDescription(card.description)}</small>
            </span>
          </button>
        ))}
        {preferenceLoading && (
          <div className="panel-dashboard-quick-empty">Hızlı işlemler yükleniyor...</div>
        )}
        {!preferenceLoading && cards.length === 0 && (
          <div className="panel-dashboard-quick-empty">Hızlı işlem seçili değil.</div>
        )}
      </div>
    </DashboardBlock>
  )
}

function KpiStripDashboard({ dashboard, layout }: { dashboard: DashboardDefinition; layout?: DashboardGridItemLayout }) {
  const fallbackItems = readArraySetting<KpiItem>(dashboard, 'items')
  const [liveItems, setLiveItems] = useState<KpiItem[] | null>(null)
  const width = Number(layout?.w || 0)
  const height = Number(layout?.h || 0)
  const showIcons = readBooleanSetting(dashboard, 'show_icons', true)
  const showDeltas = readBooleanSetting(dashboard, 'show_deltas', true)
  const noMeta = !showIcons && !showDeltas
  const dataEndpoint = typeof dashboard.data_source?.endpoint === 'string'
    ? dashboard.data_source.endpoint
    : (dashboard.id === 11 ? `/panel/dashboard/${dashboard.id}` : '')
  const items = liveItems || fallbackItems

  useEffect(() => {
    if (!dataEndpoint) {
      setLiveItems(null)
      return
    }

    const controller = new AbortController()
    const loadLiveItems = async () => {
      try {
        const token = window.localStorage.getItem('auth_token')
        const endpoint = dataEndpoint.startsWith('/api/')
          ? `https://app.canerozen.com${dataEndpoint}`
          : `https://app.canerozen.com/api${dataEndpoint.startsWith('/') ? dataEndpoint : `/${dataEndpoint}`}`
        const res = await fetch(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal
        })
        const data = await res.json().catch(() => null)
        if (!res.ok || !data?.success || !Array.isArray(data?.data?.items)) {
          throw new Error(data?.message || 'Dashboard verisi alınamadı')
        }
        setLiveItems(data.data.items as KpiItem[])
      } catch (error) {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          setLiveItems(null)
        }
      }
    }

    loadLiveItems()
    return () => controller.abort()
  }, [dataEndpoint])

  return (
    <DashboardBlock dashboard={dashboard} layout={layout}>
      <div className={`panel-dashboard-kpi-grid ${width < 16 ? 'is-compact' : ''} ${width >= 22 ? 'is-wide' : ''} ${height <= 10 ? 'is-short' : ''} ${noMeta ? 'has-no-meta' : ''}`}>
        {items.map((item, index) => (
          <div key={`${item.label}-${index}`} className={`panel-dashboard-kpi panel-dashboard-accent--${item.accent || 'blue'}`}>
            {(showIcons || (showDeltas && item.delta)) && (
              <div>
                {showIcons && <i className={item.icon || 'fa-duotone fa-chart-line'} />}
                {showDeltas && item.delta && <em>{item.delta}</em>}
              </div>
            )}
            <strong>{item.value || '-'}</strong>
            <span>{item.label || 'Metrik'}</span>
          </div>
        ))}
      </div>
    </DashboardBlock>
  )
}

function ActivityListDashboard({ dashboard }: { dashboard: DashboardDefinition }) {
  const items = readArraySetting<ActivityItem>(dashboard, 'items')
  return (
    <DashboardBlock dashboard={dashboard}>
      <div className="panel-dashboard-activity">
        {items.map((item, index) => (
          <div key={`${item.label}-${index}`} className={`panel-dashboard-activity__item panel-dashboard-accent--${item.accent || 'blue'}`}>
            <i className={item.icon || 'fa-duotone fa-bell'} />
            <span>
              <strong>{item.label || 'Aktivite'}</strong>
              {item.meta && <small>{item.meta}</small>}
            </span>
            {item.status && <em>{item.status}</em>}
          </div>
        ))}
      </div>
    </DashboardBlock>
  )
}

function TrendBarsDashboard({ dashboard }: { dashboard: DashboardDefinition }) {
  const points = readArraySetting<TrendPoint>(dashboard, 'points')
  const max = Math.max(...points.map((point) => Number(point.value || 0)), 1)
  return (
    <DashboardBlock dashboard={dashboard}>
      <div className="panel-dashboard-trend">
        {points.map((point, index) => (
          <div key={`${point.label}-${index}`}>
            <span><i style={{ height: `${Math.max(10, (Number(point.value || 0) / max) * 100)}%` }} /></span>
            <small>{point.label}</small>
          </div>
        ))}
      </div>
    </DashboardBlock>
  )
}

const reportHours = ['00-02', '02-04', '04-06', '06-08', '08-10', '10-12', '12-14', '14-16', '16-18', '18-20', '20-22', '22-24']
const reportDays = ['Pzt', 'Salı', 'Çrş', 'Prş', 'Cuma', 'Cts', 'Pazar']
const reportPlatforms = ['Web', 'Hepsiburada', 'Trendyol', 'Çiçek Sepeti', 'N11']
const reportColors = ['#155dfc', '#f97316', '#06b6d4', '#10b981', '#a855f7', '#ef4444']

function formatCompact(value: number) {
  return new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 }).format(value)
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 }).format(value) + ' TL'
}

type MockChartKind =
  | 'dual_line'
  | 'gradient_area'
  | 'grouped_bar'
  | 'donut_split'
  | 'calendar_heatmap'
  | 'bubble_scatter'
  | 'composed_line_bar'
  | 'stacked_area'
  | 'radial_bar'
  | 'gauge_meter'
  | 'horizontal_stack'
  | 'slope_chart'
  | 'ranked_bar'
  | 'status_matrix'
  | 'waterfall'
  | 'delay_bubbles'
  | 'step_line'
  | 'funnel'
  | 'treemap'
  | 'band_line'
  | 'stream'
  | 'pie_ring'
  | 'spark_columns'
  | 'lollipop'
  | 'quadrant_scatter'
  | 'radar'
  | 'combo_stack_line'
  | 'box_plot'
  | 'bullet'
  | 'sankey_like'

type MockChartSpec = {
  id: number
  key: string
  title: string
  description: string
  domain: 'sales' | 'warehouse' | 'logistics' | 'finance' | 'customer'
  kind: MockChartKind
  seed: number
  metric: string
  metricLabel: string
  secondaryMetric?: string
  secondaryLabel?: string
  segments?: string[]
}

type MockChartPoint = {
  label: string
  value: number
  value2: number
  value3: number
  amount: number
}

const mockChartPeriodOptions = ['Son 7 Gün', 'Son 30 Gün', 'Bu Ay']

const mockChartSpecs: MockChartSpec[] = [
  { id: 9001, key: 'mock_sales_channel_revenue', title: 'KANAL CİRO TRENDİ', description: 'Kanal bazlı satış ivmesi', domain: 'sales', kind: 'dual_line', seed: 12, metric: '1.284.600 TL', metricLabel: 'Ciro', secondaryMetric: '642', secondaryLabel: 'Sipariş' },
  { id: 9002, key: 'mock_order_ageing', title: 'SİPARİŞ YAŞLANDIRMA', description: 'Bekleyen sipariş yaş dağılımı', domain: 'sales', kind: 'gradient_area', seed: 18, metric: '186', metricLabel: 'Bekleyen' },
  { id: 9003, key: 'mock_platform_margin', title: 'PLATFORM MARJ DAĞILIMI', description: 'Net marj karşılaştırması', domain: 'finance', kind: 'grouped_bar', seed: 24, metric: '%18,4', metricLabel: 'Ortalama marj' },
  { id: 9004, key: 'mock_return_reason_mix', title: 'İADE SEBEP DAĞILIMI', description: 'Dönem içi iade kırılımı', domain: 'customer', kind: 'donut_split', seed: 31, metric: '74', metricLabel: 'İade', segments: ['Beden', 'Hasar', 'Vazgeçti', 'Yanlış Ürün'] },
  { id: 9005, key: 'mock_warehouse_picking_load', title: 'DEPO TOPLAMA YÜKÜ', description: 'Saatlik toplama yoğunluğu', domain: 'warehouse', kind: 'calendar_heatmap', seed: 42, metric: '328', metricLabel: 'Ürün' },
  { id: 9006, key: 'mock_cargo_sla_scatter', title: 'KARGO SLA DAĞILIMI', description: 'Firma hız ve başarı dengesi', domain: 'logistics', kind: 'bubble_scatter', seed: 51, metric: '%92,6', metricLabel: 'Başarı' },
  { id: 9007, key: 'mock_invoice_operation', title: 'FATURA OPERASYON TRENDİ', description: 'Kesilen ve bekleyen fatura', domain: 'finance', kind: 'composed_line_bar', seed: 62, metric: '512', metricLabel: 'Fatura' },
  { id: 9008, key: 'mock_campaign_lift', title: 'KAMPANYA ETKİSİ', description: 'Kampanyalı ciro etkisi', domain: 'sales', kind: 'stacked_area', seed: 73, metric: '+%24', metricLabel: 'Lift' },
  { id: 9011, key: 'mock_packing_queue', title: 'PAKETLEME KUYRUĞU', description: 'Kuyruk durumları', domain: 'warehouse', kind: 'horizontal_stack', seed: 106, metric: '124', metricLabel: 'Paket' },
  { id: 9013, key: 'mock_city_sales', title: 'ŞEHİR SATIŞ YOĞUNLUĞU', description: 'İl bazlı sipariş ivmesi', domain: 'sales', kind: 'ranked_bar', seed: 128, metric: '18 İl', metricLabel: 'Aktif şehir' },
  { id: 9016, key: 'mock_delivery_delay', title: 'TESLİMAT GECİKME RADARI', description: 'Gecikme kümeleri', domain: 'logistics', kind: 'delay_bubbles', seed: 161, metric: '27', metricLabel: 'Geciken' },
  { id: 9017, key: 'mock_refund_cashflow', title: 'İADE NAKİT AKIŞI', description: 'İade tutarı ve adet', domain: 'finance', kind: 'step_line', seed: 172, metric: '184K TL', metricLabel: 'İade tutarı' },
  { id: 9018, key: 'mock_cart_funnel', title: 'SEPET DÖNÜŞÜM HUNİSİ', description: 'Sepetten siparişe geçiş', domain: 'customer', kind: 'funnel', seed: 183, metric: '%12,7', metricLabel: 'Dönüşüm' },
  { id: 9019, key: 'mock_product_velocity', title: 'ÜRÜN SATIŞ HIZI', description: 'Kategori bazlı hız', domain: 'sales', kind: 'treemap', seed: 194, metric: '4.8K', metricLabel: 'Satılan' },
  { id: 9022, key: 'mock_coupon_usage', title: 'KUPON KULLANIM ETKİSİ', description: 'Kuponlu sipariş davranışı', domain: 'sales', kind: 'pie_ring', seed: 227, metric: '%8,6', metricLabel: 'Kupon oranı', segments: ['Kullanıldı', 'Şart Yok', 'Süresi Doldu', 'Limit Doldu'] },
  { id: 9025, key: 'mock_pricing_pressure', title: 'FİYAT BASKISI', description: 'Rekabet fiyat sapması', domain: 'sales', kind: 'quadrant_scatter', seed: 260, metric: '%6,2', metricLabel: 'Sapma' },
  { id: 9026, key: 'mock_transfer_success', title: 'SENKRON BAŞARI TRENDİ', description: 'Stok fiyat aktarım sağlığı', domain: 'sales', kind: 'radar', seed: 271, metric: '%99,2', metricLabel: 'Başarı' },
  { id: 9027, key: 'mock_batch_printing', title: 'ETİKET BASKI AKIŞI', description: 'Baskı ve hata dağılımı', domain: 'warehouse', kind: 'combo_stack_line', seed: 282, metric: '1.2K', metricLabel: 'Etiket' },
]

const mockChartDashboards: DashboardDefinition[] = mockChartSpecs.map((spec, index) => ({
  id: spec.id,
  name: spec.title,
  title: spec.title,
  description: spec.description,
  component_key: spec.key,
  sort: 9000 + index,
  is_default: false,
  settings: { mock_chart: true },
}))

function isMockChartDashboard(dashboard: DashboardDefinition) {
  return mockChartDashboardIds.includes(Number(dashboard.id)) || Boolean(dashboard.settings?.mock_chart)
}

function mockNumber(seed: number, index: number, base: number, variance: number) {
  const wave = Math.sin((seed + index * 3.7) * 0.42) + Math.cos((seed + index * 2.1) * 0.23)
  return Math.max(0, Math.round(base + wave * variance + index * variance * 0.16))
}

function mockChartRows(spec: MockChartSpec, period: string): MockChartPoint[] {
  const count = period === 'Son 7 Gün' ? 7 : period === 'Bu Ay' ? 12 : 10
  const labels = period === 'Bu Ay'
    ? ['1', '3', '5', '7', '9', '11', '13', '15', '17', '19', '21', '23']
    : Array.from({ length: count }, (_, index) => period === 'Son 7 Gün' ? `${index + 2}/07` : `${(index + 1) * 3}g`)
  return labels.map((label, index) => {
    const value = mockNumber(spec.seed, index, 38 + (spec.seed % 28), 18)
    const value2 = mockNumber(spec.seed + 9, index, 24 + (spec.seed % 18), 12)
    const value3 = mockNumber(spec.seed + 17, index, 10 + (spec.seed % 12), 8)
    return { label, value, value2, value3, amount: value * (420 + (spec.seed % 9) * 38) }
  })
}

function mockChartSegments(spec: MockChartSpec) {
  const labels = spec.segments || ['Web', 'Pazaryeri', 'Mağaza', 'Uygulama']
  return labels.map((label, index) => ({
    label,
    value: mockNumber(spec.seed + 3, index, 28 + index * 7, 11),
    color: ['#4966ff', '#34c98b', '#ff6b45', '#8da3e8'][index % 4],
  }))
}

function MockChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ value?: number | string; name?: string; payload?: { label?: string; name?: string } }>
  label?: string | number
}) {
  if (!active || !payload?.length) return null
  const first = payload[0]
  const title = String(label || first.payload?.label || first.payload?.name || '')
  return (
    <div className="channel-orders-tooltip mock-chart-tooltip">
      <span>{title}</span>
      {payload.slice(0, 3).map((item, index) => (
        <em key={`${item.name || index}`}>{item.name || 'Değer'}: {formatCompact(Number(item.value || 0))}</em>
      ))}
    </div>
  )
}

function MockChartVisual({
  spec,
  rows,
  segments,
  total,
}: {
  spec: MockChartSpec
  rows: MockChartPoint[]
  segments: Array<{ label: string; value: number; color: string }>
  total: number
}) {
  const maxValue = Math.max(1, ...rows.flatMap((row) => [row.value, row.value2, row.value3]))
  const svgPoints = rows.map((row, index) => {
    const x = rows.length === 1 ? 50 : 8 + (index / (rows.length - 1)) * 84
    const y = 92 - (row.value / maxValue) * 74
    return { ...row, x, y }
  })

  switch (spec.kind) {
    case 'dual_line':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 16, right: 10, bottom: 10, left: 2 }}>
            <CartesianGrid vertical={false} stroke="#eef1f8" />
            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#8b95c5', fontSize: 10, fontWeight: 600 }} dy={8} />
            <YAxis hide />
            <Tooltip cursor={{ stroke: '#c7d1f5', strokeDasharray: '3 3' }} content={<MockChartTooltip />} wrapperStyle={{ outline: 'none' }} />
            <Line type="monotone" dataKey="value" name={spec.metricLabel} stroke="#4966ff" strokeWidth={2} dot={{ r: 3, fill: '#4966ff', stroke: '#4966ff' }} activeDot={{ r: 4 }} isAnimationActive={false} />
            <Line type="monotone" dataKey="value2" name={spec.secondaryLabel || 'Karşılaştırma'} stroke="#34c98b" strokeWidth={1.8} dot={{ r: 2.6, fill: '#34c98b', stroke: '#34c98b' }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      )
    case 'gradient_area':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={rows} margin={{ top: 18, right: 10, bottom: 10, left: 2 }}>
            <defs>
              <linearGradient id={`mockArea${spec.id}`} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#4966ff" stopOpacity="0.28" />
                <stop offset="100%" stopColor="#4966ff" stopOpacity="0.02" />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke="#eef1f8" />
            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#8b95c5', fontSize: 10, fontWeight: 600 }} dy={8} />
            <YAxis hide />
            <Tooltip cursor={{ stroke: '#c7d1f5', strokeDasharray: '3 3' }} content={<MockChartTooltip />} wrapperStyle={{ outline: 'none' }} />
            <Area type="monotone" dataKey="value" name={spec.metricLabel} stroke="#4966ff" strokeWidth={2} fill={`url(#mockArea${spec.id})`} isAnimationActive={false} />
            <Area type="monotone" dataKey="value2" name="Önceki dönem" stroke="#34c98b" strokeWidth={1.6} fill="transparent" isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      )
    case 'grouped_bar':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows.slice(0, 6)} margin={{ top: 20, right: 10, bottom: 10, left: 2 }} barCategoryGap="28%">
            <CartesianGrid vertical={false} stroke="#eef1f8" />
            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#8b95c5', fontSize: 10, fontWeight: 600 }} dy={8} />
            <YAxis hide />
            <Tooltip cursor={{ fill: 'rgba(73,102,255,.06)' }} content={<MockChartTooltip />} wrapperStyle={{ outline: 'none' }} />
            <Bar dataKey="value" name={spec.metricLabel} fill="#4966ff" radius={[2, 2, 0, 0]} isAnimationActive={false} />
            <Bar dataKey="value2" name="Karşılaştırma" fill="#9fb2ff" radius={[2, 2, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      )
    case 'donut_split':
    case 'pie_ring':
      return (
        <div className="mock-chart-donut">
          <ResponsiveContainer width="54%" height="100%">
            <PieChart>
              <Tooltip content={<MockChartTooltip />} wrapperStyle={{ outline: 'none' }} />
              <Pie data={segments} dataKey="value" nameKey="label" innerRadius={spec.kind === 'pie_ring' ? '36%' : '58%'} outerRadius="82%" paddingAngle={3} startAngle={90} endAngle={-270} isAnimationActive={false}>
                {segments.map((item) => <Cell key={item.label} fill={item.color} stroke="#fff" strokeWidth={3} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="mock-chart-legend">
            <strong>{formatCompact(total)} toplam</strong>
            {segments.map((item) => <span key={item.label}><i style={{ background: item.color }} />{item.label}<b>{formatCompact(item.value)}</b></span>)}
          </div>
        </div>
      )
    case 'calendar_heatmap':
    case 'status_matrix':
      return (
        <div className={`mock-chart-matrix ${spec.kind === 'status_matrix' ? 'is-status' : ''}`}>
          {reportDays.map((day) => <strong key={day}>{day}</strong>)}
          {Array.from({ length: 35 }, (_, index) => {
            const value = mockNumber(spec.seed, index, 20, 18)
            return <span key={index} title={`${value}`} style={{ background: `rgba(73, 102, 255, ${0.1 + (value / 72) * 0.72})` }} />
          })}
        </div>
      )
    case 'bubble_scatter':
    case 'delay_bubbles':
    case 'quadrant_scatter':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 18, right: 14, bottom: 14, left: 4 }}>
            <CartesianGrid stroke="#eef1f8" />
            {spec.kind === 'quadrant_scatter' && <ReferenceLine x={60} stroke="#c7d1f5" strokeDasharray="3 3" />}
            {spec.kind === 'quadrant_scatter' && <ReferenceLine y={42} stroke="#c7d1f5" strokeDasharray="3 3" />}
            <XAxis type="number" dataKey="value" name="Hacim" axisLine={false} tickLine={false} tick={{ fill: '#8b95c5', fontSize: 9, fontWeight: 600 }} />
            <YAxis type="number" dataKey="value2" name="Başarı" axisLine={false} tickLine={false} tick={{ fill: '#8b95c5', fontSize: 9, fontWeight: 600 }} />
            <ZAxis dataKey="value3" range={spec.kind === 'delay_bubbles' ? [90, 260] : [42, 180]} />
            <Tooltip cursor={{ stroke: '#c7d1f5', strokeDasharray: '3 3' }} content={<MockChartTooltip />} wrapperStyle={{ outline: 'none' }} />
            <Scatter data={rows.slice(0, 9)} fill={spec.kind === 'delay_bubbles' ? '#ff6b45' : '#4966ff'} isAnimationActive={false} />
          </ScatterChart>
        </ResponsiveContainer>
      )
    case 'composed_line_bar':
    case 'combo_stack_line':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows.slice(0, 7)} margin={{ top: 18, right: 12, bottom: 10, left: 2 }}>
            <CartesianGrid vertical={false} stroke="#eef1f8" />
            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#8b95c5', fontSize: 10, fontWeight: 600 }} dy={8} />
            <YAxis hide />
            <Tooltip cursor={{ fill: 'rgba(73,102,255,.06)' }} content={<MockChartTooltip />} wrapperStyle={{ outline: 'none' }} />
            <Bar dataKey="value2" stackId={spec.kind === 'combo_stack_line' ? 'a' : undefined} name="Bekleyen" fill="#9fb2ff" radius={[2, 2, 0, 0]} isAnimationActive={false} />
            {spec.kind === 'combo_stack_line' && <Bar dataKey="value3" stackId="a" name="Hata" fill="#ff6b45" radius={[2, 2, 0, 0]} isAnimationActive={false} />}
            <Line type="monotone" dataKey="value" name={spec.metricLabel} stroke="#4966ff" strokeWidth={2} dot={{ r: 3, fill: '#4966ff' }} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      )
    case 'stacked_area':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={rows} margin={{ top: 16, right: 10, bottom: 10, left: 2 }}>
            <CartesianGrid vertical={false} stroke="#eef1f8" />
            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#8b95c5', fontSize: 10, fontWeight: 600 }} dy={8} />
            <YAxis hide />
            <Tooltip content={<MockChartTooltip />} wrapperStyle={{ outline: 'none' }} />
            <Area type="monotone" dataKey="value" stackId="1" name="Kampanyalı" stroke="#4966ff" fill="#4966ff" fillOpacity={0.24} isAnimationActive={false} />
            <Area type="monotone" dataKey="value2" stackId="1" name="Organik" stroke="#34c98b" fill="#34c98b" fillOpacity={0.18} isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      )
    case 'radial_bar':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart data={segments} innerRadius="22%" outerRadius="92%" startAngle={180} endAngle={-180}>
            <RadialBar dataKey="value" cornerRadius={8} background />
            <Tooltip content={<MockChartTooltip />} wrapperStyle={{ outline: 'none' }} />
          </RadialBarChart>
        </ResponsiveContainer>
      )
    case 'gauge_meter': {
      const percent = Math.min(96, 48 + (spec.seed % 36))
      return (
        <div className="mock-chart-gauge">
          <svg viewBox="0 0 220 130">
            <path d="M30 110 A80 80 0 0 1 190 110" pathLength="100" />
            <path d="M30 110 A80 80 0 0 1 190 110" pathLength="100" style={{ strokeDasharray: `${percent} 100` }} />
            <text x="110" y="82">{percent}%</text>
            <text x="110" y="104">{spec.metricLabel}</text>
          </svg>
        </div>
      )
    }
    case 'horizontal_stack':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows.slice(0, 5)} layout="vertical" margin={{ top: 12, right: 12, bottom: 8, left: 42 }}>
            <CartesianGrid horizontal={false} stroke="#eef1f8" />
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#4d589f', fontSize: 10, fontWeight: 700 }} width={38} />
            <Tooltip cursor={{ fill: 'rgba(73,102,255,.06)' }} content={<MockChartTooltip />} wrapperStyle={{ outline: 'none' }} />
            <Bar dataKey="value" stackId="a" name="Tamamlanan" fill="#34c98b" isAnimationActive={false} />
            <Bar dataKey="value2" stackId="a" name="Bekleyen" fill="#4966ff" isAnimationActive={false} />
            <Bar dataKey="value3" stackId="a" name="Sorunlu" fill="#ff6b45" isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      )
    case 'ranked_bar':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={[...rows.slice(0, 6)].sort((a, b) => b.value - a.value)} layout="vertical" margin={{ top: 12, right: 12, bottom: 8, left: 42 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#4d589f', fontSize: 10, fontWeight: 700 }} width={38} />
            <Tooltip content={<MockChartTooltip />} wrapperStyle={{ outline: 'none' }} />
            <Bar dataKey="value" fill="#4966ff" radius={[0, 2, 2, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      )
    case 'step_line':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 18, right: 10, bottom: 10, left: 2 }}>
            <CartesianGrid vertical={false} stroke="#eef1f8" />
            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#8b95c5', fontSize: 10, fontWeight: 600 }} dy={8} />
            <YAxis hide />
            <Tooltip content={<MockChartTooltip />} wrapperStyle={{ outline: 'none' }} />
            <Line type="stepAfter" dataKey="value" name={spec.metricLabel} stroke="#4966ff" strokeWidth={2} dot={{ r: 3, fill: '#4966ff' }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      )
    case 'funnel':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <FunnelChart>
            <Tooltip content={<MockChartTooltip />} wrapperStyle={{ outline: 'none' }} />
            <Funnel dataKey="value" data={segments} nameKey="label" isAnimationActive={false}>
              {segments.map((item) => <Cell key={item.label} fill={item.color} />)}
            </Funnel>
          </FunnelChart>
        </ResponsiveContainer>
      )
    case 'treemap':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <Treemap data={segments.map((item) => ({ name: item.label, size: item.value, fill: item.color }))} dataKey="size" nameKey="name" stroke="#ffffff" fill="#4966ff" isAnimationActive={false} />
        </ResponsiveContainer>
      )
    case 'radar':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={segments.map((item) => ({ subject: item.label, value: item.value }))}>
            <PolarGrid stroke="#dfe5f4" />
            <PolarAngleAxis dataKey="subject" tick={{ fill: '#4d589f', fontSize: 10, fontWeight: 700 }} />
            <Radar dataKey="value" stroke="#4966ff" fill="#4966ff" fillOpacity={0.22} isAnimationActive={false} />
            <Tooltip content={<MockChartTooltip />} wrapperStyle={{ outline: 'none' }} />
          </RadarChart>
        </ResponsiveContainer>
      )
    case 'slope_chart':
    case 'waterfall':
    case 'band_line':
    case 'stream':
    case 'spark_columns':
    case 'lollipop':
    case 'box_plot':
    case 'bullet':
    case 'sankey_like':
      return (
        <div className={`mock-svg-chart mock-svg-chart--${spec.kind}`}>
          <svg viewBox="0 0 100 100" preserveAspectRatio="none">
            {spec.kind === 'band_line' && <path d={`M ${svgPoints.map((p) => `${p.x} ${Math.min(96, p.y + 12)}`).join(' L ')} L ${svgPoints[svgPoints.length - 1].x} ${Math.max(4, svgPoints[svgPoints.length - 1].y - 12)} L ${[...svgPoints].reverse().map((p) => `${p.x} ${Math.max(4, p.y - 12)}`).join(' L ')} Z`} className="band" />}
            {spec.kind === 'stream' && segments.map((item, index) => <path key={item.label} d={`M0 ${18 + index * 14} C25 ${8 + index * 20}, 45 ${36 + index * 4}, 68 ${20 + index * 14} S88 ${18 + index * 16}, 100 ${24 + index * 13} L100 ${34 + index * 13} C75 ${28 + index * 16}, 48 ${48 + index * 4}, 0 ${32 + index * 14} Z`} fill={item.color} opacity={0.2 + index * 0.12} />)}
            {(spec.kind === 'slope_chart' ? rows.slice(0, 5) : []).map((row, index) => <line key={row.label} x1="12" y1={18 + index * 15} x2="88" y2={90 - (row.value2 / maxValue) * 72} className={index % 2 ? 'line green' : 'line'} />)}
            {spec.kind === 'waterfall' && rows.slice(0, 7).map((row, index) => <rect key={row.label} x={8 + index * 12} y={92 - (row.value / maxValue) * 74} width="8" height={(row.value / maxValue) * 74} className={index % 3 === 0 ? 'bar orange' : 'bar'} />)}
            {spec.kind === 'spark_columns' && rows.map((row, index) => <rect key={row.label} x={6 + index * 8} y={92 - (row.value / maxValue) * 74} width="4" height={(row.value / maxValue) * 74} className={index % 2 ? 'bar green' : 'bar'} />)}
            {spec.kind === 'lollipop' && rows.slice(0, 7).map((row, index) => <g key={row.label}><line x1={10 + index * 13} y1="92" x2={10 + index * 13} y2={92 - (row.value / maxValue) * 74} className="stem" /><circle cx={10 + index * 13} cy={92 - (row.value / maxValue) * 74} r="3.4" className="dot" /></g>)}
            {spec.kind === 'box_plot' && rows.slice(0, 5).map((row, index) => <g key={row.label}><line x1={14 + index * 17} x2={14 + index * 17} y1="20" y2="88" className="stem" /><rect x={9 + index * 17} y={42 - index * 3} width="10" height="24" className="box" /><line x1={8 + index * 17} x2={20 + index * 17} y1={56 - index * 3} y2={56 - index * 3} className="median" /></g>)}
            {spec.kind === 'bullet' && rows.slice(0, 5).map((row, index) => <g key={row.label}><rect x="12" y={16 + index * 16} width="76" height="8" className="track" /><rect x="12" y={16 + index * 16} width={(row.value / maxValue) * 76} height="8" className="bar" /><line x1={62 + index * 5} x2={62 + index * 5} y1={14 + index * 16} y2={26 + index * 16} className="target" /></g>)}
            {spec.kind === 'sankey_like' && segments.map((item, index) => <path key={item.label} d={`M8 ${24 + index * 14} C36 ${20 + index * 10}, 58 ${54 - index * 6}, 92 ${30 + index * 12}`} className="flow" style={{ stroke: item.color, strokeWidth: 7 - index }} />)}
            {spec.kind !== 'stream' && spec.kind !== 'waterfall' && spec.kind !== 'spark_columns' && spec.kind !== 'lollipop' && spec.kind !== 'box_plot' && spec.kind !== 'bullet' && spec.kind !== 'sankey_like' && <polyline points={svgPoints.map((p) => `${p.x},${p.y}`).join(' ')} className="line" />}
          </svg>
        </div>
      )
    default:
      return null
  }
}

function MockCommerceChartDashboard({ dashboard }: { dashboard: DashboardDefinition }) {
  const spec = mockChartSpecs.find((item) => item.id === Number(dashboard.id) || item.key === dashboard.component_key) || mockChartSpecs[0]
  const [period, setPeriod] = useState('Son 7 Gün')
  const [open, setOpen] = useState(false)
  const rows = useMemo(() => mockChartRows(spec, period), [spec, period])
  const segments = useMemo(() => mockChartSegments(spec), [spec])
  const total = rows.reduce((sum, row) => sum + row.value, 0)

  return (
    <section className="panel-dashboard-block cart-member-card mock-chart-card">
      <div className="panel-dashboard-block__head cart-member-head">
        <div>
          <strong>{spec.title}</strong>
          <span>{period} <em>{spec.description}</em></span>
        </div>
        <div className="mock-chart-summary">
          <strong>{spec.metric}</strong>
          <span>{spec.metricLabel}</span>
          {spec.secondaryMetric && <strong>{spec.secondaryMetric}</strong>}
          {spec.secondaryLabel && <span>{spec.secondaryLabel}</span>}
        </div>
      </div>
      <div className="cart-member-dash" />
      <div className={`mock-chart-body mock-chart-body--${spec.kind}`}>
        <MockChartVisual spec={spec} rows={rows} segments={segments} total={total} />
      </div>
      <div className="channel-orders-foot mock-chart-foot">
        <DashboardWidgetDropdown
          value={period}
          options={mockChartPeriodOptions}
          open={open}
          onOpenChange={setOpen}
          onSelect={setPeriod}
          icon="fa-regular fa-calendar-days"
        />
      </div>
    </section>
  )
}

function ReportKpiGrid({ items }: { items: ReportKpi[] }) {
  return (
    <div className="report-dashboard-kpis">
      {items.map((item) => (
        <div key={item.label} className={`report-dashboard-kpi report-dashboard-tone--${item.tone || 'blue'}`}>
          <span>{item.label}</span>
          <strong>{item.value}</strong>
          {item.delta && <em>{item.delta}</em>}
        </div>
      ))}
    </div>
  )
}

function ReportHeatmap({ mode = 'orders' }: { mode?: 'orders' | 'supply' }) {
  return (
    <div className="report-heatmap">
      <div />
      {reportDays.map((day) => <strong key={day}>{day}</strong>)}
      {reportHours.map((hour, rowIndex) => (
        <Fragment key={hour}>
          <strong key={`${hour}-label`}>{hour}</strong>
          {reportDays.map((day, colIndex) => {
            const value = ((rowIndex + 2) * (colIndex + 3) * (mode === 'supply' ? 7 : 5)) % 96
            const opacity = 0.14 + (value / 96) * 0.72
            return (
              <span
                key={`${hour}-${day}`}
                style={{ backgroundColor: `rgba(21, 93, 252, ${opacity})` }}
                title={`${day} ${hour}: ${value}`}
              >
                {value > 72 ? value : ''}
              </span>
            )
          })}
        </Fragment>
      ))}
    </div>
  )
}

function ReportBarList({ rows, valueLabel = 'Adet' }: { rows: ReportSeriesPoint[]; valueLabel?: string }) {
  const max = Math.max(...rows.map((row) => row.value), 1)
  return (
    <div className="report-bar-list">
      {rows.map((row, index) => (
        <div key={row.label} className="report-bar-row">
          <span>{row.label}</span>
          <div><i style={{ width: `${Math.max(8, (row.value / max) * 100)}%`, backgroundColor: reportColors[index % reportColors.length] }} /></div>
          <strong>{formatCompact(row.value)} {valueLabel}</strong>
        </div>
      ))}
    </div>
  )
}

function ReportLineChart({ rows, secondary = false }: { rows: ReportSeriesPoint[]; secondary?: boolean }) {
  const width = 520
  const height = 150
  const max = Math.max(...rows.flatMap((row) => [row.value, row.value2 || 0]), 1)
  const points = rows.map((row, index) => {
    const x = rows.length === 1 ? width / 2 : (index / (rows.length - 1)) * width
    const y = height - (row.value / max) * (height - 20) - 10
    return `${x},${y}`
  }).join(' ')
  const points2 = rows.map((row, index) => {
    const x = rows.length === 1 ? width / 2 : (index / (rows.length - 1)) * width
    const y = height - ((row.value2 || 0) / max) * (height - 20) - 10
    return `${x},${y}`
  }).join(' ')
  return (
    <div className="report-line-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Rapor grafiği">
        <polyline points={points} fill="none" stroke="#155dfc" strokeWidth="4" strokeLinejoin="round" strokeLinecap="round" />
        {secondary && <polyline points={points2} fill="none" stroke="#10b981" strokeWidth="4" strokeLinejoin="round" strokeLinecap="round" />}
        {rows.map((row, index) => {
          const x = rows.length === 1 ? width / 2 : (index / (rows.length - 1)) * width
          const y = height - (row.value / max) * (height - 20) - 10
          return <circle key={row.label} cx={x} cy={y} r="4" fill="#155dfc" />
        })}
      </svg>
      <div>{rows.map((row) => <span key={row.label}>{row.label}</span>)}</div>
    </div>
  )
}

function ReportSplitTable({ rows }: { rows: ReportSeriesPoint[] }) {
  const max = Math.max(...rows.flatMap((row) => [row.value, row.value2 || 0]), 1)
  return (
    <div className="report-split-table">
      <div className="report-split-table__head"><span>Platform</span><span>Sipariş</span><span>Tutar</span><span>Oran</span></div>
      {rows.map((row, index) => (
        <div key={row.label} className="report-split-table__row">
          <strong>{row.label}</strong>
          <span>{formatCompact(row.value)}</span>
          <span>{formatCurrency(row.value2 || 0)}</span>
          <div><i style={{ width: `${Math.max(8, (row.value / max) * 100)}%`, backgroundColor: reportColors[index % reportColors.length] }} /></div>
        </div>
      ))}
    </div>
  )
}

const evPalette = {
  blue: '#4368f3',
  purple: '#8a4fe8',
  pink: '#d145d2',
  red: '#df4d42',
  orange: '#f6ad38',
  grey: '#9aa0af',
  soft: '#efe6ff',
}

function EvLegend({ items }: { items: Array<{ label: string; color: string }> }) {
  return (
    <div className="ev-legend">
      {items.map((item) => (
        <span key={item.label}><i style={{ background: item.color }} />{item.label}</span>
      ))}
    </div>
  )
}

function EvAgeUsersDashboard({ dashboard }: { dashboard: DashboardDefinition }) {
  const rows = [
    { label: '18 - 24', value: 14.5 },
    { label: '25 - 34', value: 28 },
    { label: '35 - 44', value: 22 },
    { label: '45 - 56', value: 16.2 },
    { label: '56 - 65', value: 11.7 },
    { label: '65+', value: 4.6 },
  ]

  return (
    <DashboardBlock dashboard={dashboard}>
      <div className="ev-age-card">
        <div className="ev-meta">
          <span>People born between</span>
          <strong>1946 - 1996</strong>
        </div>
        <div className="ev-chart ev-chart--age">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 26, right: 8, left: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="evAgeSelected" x1="0" x2="1" y1="0" y2="1">
                  <stop offset="0%" stopColor="#d9a7ff" />
                  <stop offset="52%" stopColor="#8b55ec" />
                  <stop offset="100%" stopColor="#753de1" />
                </linearGradient>
                <linearGradient id="evAgeSoft" x1="0" x2="1" y1="0" y2="1">
                  <stop offset="0%" stopColor="#f8f1ff" />
                  <stop offset="100%" stopColor="#ded0f2" />
                </linearGradient>
              </defs>
              <XAxis dataKey="label" hide />
              <YAxis hide domain={[0, 32]} />
              <Bar dataKey="value" radius={[10, 10, 10, 10]} barSize={70}>
                <LabelList
                  dataKey="value"
                  position="top"
                  formatter={(value: unknown) => `${String(value).replace('.', ',')}%`}
                  className="ev-age-label"
                />
                {rows.map((row) => (
                  <Cell key={row.label} fill={row.label === '25 - 34' ? 'url(#evAgeSelected)' : 'url(#evAgeSoft)'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="ev-age-axis">
          {rows.map((row) => <span key={row.label} className={row.label === '25 - 34' ? 'is-active' : ''}>{row.label}</span>)}
        </div>
      </div>
    </DashboardBlock>
  )
}

function EvAdoptionDashboard({ dashboard }: { dashboard: DashboardDefinition }) {
  const bands = [
    { label: 'China', color: evPalette.blue, y: [238, 192, 126], h: [40, 56, 78] },
    { label: 'EU', color: evPalette.purple, y: [200, 160, 84], h: [28, 44, 66] },
    { label: 'USA', color: evPalette.pink, y: [172, 134, 54], h: [20, 30, 42] },
    { label: 'Canada', color: evPalette.red, y: [150, 114, 30], h: [13, 20, 28] },
    { label: 'Others', color: evPalette.orange, y: [134, 96, 14], h: [8, 12, 15] },
  ]
  const legend = bands.map(({ label, color }) => ({ label, color }))
  const years = [
    { year: '2020', x: 30, bottom: '3.1 M' },
    { year: '2021', x: 360, bottom: '6.6 M' },
    { year: '2022', x: 690, bottom: '10.5 M' },
  ]

  return (
    <DashboardBlock dashboard={dashboard}>
      <div className="ev-adoption-card">
        <EvLegend items={legend} />
        <svg className="ev-stream" viewBox="0 0 760 310" role="img" aria-label="EV adoption country stream chart">
          <defs>
            {bands.map((band) => (
              <linearGradient key={band.label} id={`evStream${band.label}`} x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor={band.color} stopOpacity="0.9" />
                <stop offset="100%" stopColor={band.color} stopOpacity="0.14" />
              </linearGradient>
            ))}
          </defs>
          {years.map((item) => (
            <g key={item.year}>
              <text x={item.x + 46} y={item.year === '2020' ? 78 : item.year === '2021' ? 34 : 0} className="ev-stream-year">{item.year}</text>
              <text x={item.x + 64} y="298" className={item.year === '2022' ? 'ev-stream-bottom is-active' : 'ev-stream-bottom'}>{item.bottom}</text>
            </g>
          ))}
          {bands.map((band) => {
            const [y0, y1, y2] = band.y
            const [h0, h1, h2] = band.h
            const path = [
              `M 0 ${y0}`,
              `L 92 ${y0}`,
              `C 160 ${y0 - 8}, 240 ${y1 - 38}, 330 ${y1}`,
              `L 430 ${y1}`,
              `C 520 ${y1 - 20}, 600 ${y2 - 52}, 680 ${y2}`,
              `L 760 ${y2}`,
              `L 760 ${y2 + h2}`,
              `L 680 ${y2 + h2}`,
              `C 600 ${y2 + h2 - 4}, 520 ${y1 + h1 + 12}, 430 ${y1 + h1}`,
              `L 330 ${y1 + h1}`,
              `C 240 ${y1 + h1 + 18}, 160 ${y0 + h0 + 6}, 92 ${y0 + h0}`,
              `L 0 ${y0 + h0}`,
              'Z',
            ].join(' ')
            return <path key={band.label} d={path} fill={`url(#evStream${band.label})`} stroke="#fff" strokeWidth="5" />
          })}
          {bands.map((band) => (
            <g key={`${band.label}-blocks`}>
              {years.map((year, index) => (
                <rect
                  key={year.year}
                  x={year.x}
                  y={band.y[index]}
                  width={92}
                  height={band.h[index]}
                  rx="8"
                  fill={`url(#evStream${band.label})`}
                  stroke="#fff"
                  strokeWidth="5"
                />
              ))}
            </g>
          ))}
        </svg>
      </div>
    </DashboardBlock>
  )
}

type EvBubblePayload = {
  name: string
  visits: number
  size: number
  color: string
  light: string
}

function EvBubbleShape(props: { cx?: number; cy?: number; payload?: EvBubblePayload }) {
  const { cx, cy, payload } = props
  if (!payload || cx == null || cy == null) return null
  const size = payload.size
  return (
    <g>
      <defs>
        <linearGradient id={`evBubble${payload.name.replace(/\s/g, '')}`} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor={payload.light} />
          <stop offset="100%" stopColor={payload.color} />
        </linearGradient>
      </defs>
      <rect
        x={cx - size / 2}
        y={cy - size / 2}
        width={size}
        height={size}
        rx="14"
        fill={`url(#evBubble${payload.name.replace(/\s/g, '')})`}
        filter="drop-shadow(0 8px 16px rgba(92, 76, 180, 0.16))"
      />
      <text x={cx} y={cy + 5} textAnchor="middle" fill="#fff" fontSize="16" fontWeight="700">{payload.visits}</text>
    </g>
  )
}

function EvAppPopularityDashboard({ dashboard }: { dashboard: DashboardDefinition }) {
  const rows = [
    { name: 'Shell Recharge', visits: 210, rating: 4.2, size: 58, color: evPalette.red, light: '#ff9d8e' },
    { name: 'Charge Point', visits: 189, rating: 3.95, size: 50, color: evPalette.orange, light: '#ffd786' },
    { name: 'EVgo', visits: 216, rating: 3.95, size: 58, color: evPalette.pink, light: '#ef75ec' },
    { name: 'Plug Share', visits: 528, rating: 4.62, size: 80, color: evPalette.purple, light: '#c291ff' },
    { name: 'ChargeHub', visits: 118, rating: 4.6, size: 43, color: evPalette.grey, light: '#d9d9e5' },
    { name: 'Electricity America', visits: 453, rating: 4.55, size: 70, color: evPalette.blue, light: '#8a95ff' },
  ]

  return (
    <DashboardBlock dashboard={dashboard}>
      <div className="ev-app-card">
        <div className="ev-app-top">
          <EvLegend items={rows.map((row) => ({ label: row.name, color: row.color }))} />
          <span>↑ rating <i /> visits/month</span>
        </div>
        <div className="ev-chart ev-chart--scatter">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 18, right: 18, bottom: 20, left: 0 }}>
              <CartesianGrid stroke="#dedee7" strokeDasharray="12 10" vertical={false} />
              <XAxis
                type="number"
                dataKey="visits"
                domain={[90, 610]}
                ticks={[120, 240, 360, 480, 600]}
                tickFormatter={(value) => `${value}K`}
                tickLine={false}
                axisLine={false}
                tick={{ fill: '#7f8498', fontSize: 14 }}
              />
              <YAxis
                type="number"
                dataKey="rating"
                domain={[3.72, 5.04]}
                ticks={[3.8, 4.2, 4.6, 5.0]}
                tickLine={false}
                axisLine={false}
                tick={{ fill: '#7f8498', fontSize: 14 }}
              />
              <ZAxis dataKey="size" range={[48, 88]} />
              <Scatter data={rows} shape={<EvBubbleShape />} />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>
    </DashboardBlock>
  )
}

function EvChargingLocationsDashboard({ dashboard }: { dashboard: DashboardDefinition }) {
  const rows = [
    { name: 'Residential areas', value: 38, color: evPalette.red },
    { name: 'Public parking lots and garages', value: 10.9, color: evPalette.orange },
    { name: 'Public transportation hubs', value: 7.4, color: evPalette.pink },
    { name: 'Commercial and retail locations', value: 4.1, color: evPalette.purple },
    { name: 'Workplace', value: 2.7, color: evPalette.blue },
  ]

  return (
    <DashboardBlock dashboard={dashboard}>
      <div className="ev-location-card">
        <div className="ev-location-side">
          <span>Total residential areas</span>
          <strong>64 M</strong>
          <EvLegend items={rows.map((row) => ({ label: row.name, color: row.color }))} />
        </div>
        <div className="ev-donut-wrap">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <defs>
                {rows.map((row) => (
                  <linearGradient key={row.name} id={`evDonut${row.name.replace(/\W/g, '')}`} x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0%" stopColor={row.color} stopOpacity="0.42" />
                    <stop offset="100%" stopColor={row.color} />
                  </linearGradient>
                ))}
              </defs>
              <Pie
                data={rows}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius="48%"
                outerRadius="78%"
                startAngle={86}
                endAngle={-274}
                cornerRadius={10}
                paddingAngle={4}
                labelLine={false}
                label={({ cx, cy, midAngle, outerRadius, value }) => {
                  const RADIAN = Math.PI / 180
                  const radius = Number(outerRadius) - 24
                  const angle = Number(midAngle || 0)
                  const x = Number(cx) + radius * Math.cos(-angle * RADIAN)
                  const y = Number(cy) + radius * Math.sin(-angle * RADIAN)
                  return (
                    <text
                      x={x}
                      y={y}
                      textAnchor="middle"
                      dominantBaseline="central"
                      className={Number(value) === 38 ? 'ev-donut-label is-active' : 'ev-donut-label'}
                    >
                      {String(value).replace('.', ',')}
                    </text>
                  )
                }}
              >
                {rows.map((row) => (
                  <Cell key={row.name} fill={`url(#evDonut${row.name.replace(/\W/g, '')})`} stroke="#fff" strokeWidth={5} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </DashboardBlock>
  )
}

const commercePalette = {
  navy: '#444d67',
  green: '#19b86a',
  red: '#c93434',
  orange: '#ffad1d',
  teal: '#51b7cc',
  purple: '#5938f2',
  cyan: '#18bce3',
  yellow: '#dddd15',
}

function CommerceKpiDashboard({ dashboard, type }: { dashboard: DashboardDefinition; type: 'sales' | 'orders' | 'products' }) {
  const map = {
    sales: {
      label: 'Total Sales',
      value: '$19,626',
      delta: '+12.00%',
      tone: 'up',
      icon: 'fa-solid fa-trophy',
      accent: commercePalette.orange,
    },
    orders: {
      label: 'Total Orders',
      value: '3,290',
      delta: '-3.00%',
      tone: 'down',
      icon: 'fa-solid fa-bag-shopping',
      accent: commercePalette.green,
    },
    products: {
      label: 'Total Products',
      value: '322',
      delta: '+2.00%',
      tone: 'up',
      icon: 'fa-solid fa-tag',
      accent: commercePalette.teal,
    },
  }[type]

  return (
    <DashboardBlock dashboard={dashboard}>
      <div className="commerce-kpi-card">
        <div className="commerce-kpi-card__top">
          <span>{map.label}</span>
          <i className={map.icon} style={{ background: map.accent }} />
        </div>
        <strong>{map.value}</strong>
        <div className={`commerce-kpi-card__delta commerce-kpi-card__delta--${map.tone}`}>
          <em>{map.tone === 'up' ? '↑' : '↓'}</em>
          <b>{map.delta}</b>
          <span>this week</span>
        </div>
      </div>
    </DashboardBlock>
  )
}

function CommerceSalesStatisticsDashboard({ dashboard }: { dashboard: DashboardDefinition }) {
  const rows = [
    { name: 'Jun', label: 'Jun', value: 24000 },
    { name: 'Jun+', label: '', value: 29500 },
    { name: 'Jun++', label: '', value: 20500 },
    { name: 'Jul', label: 'Jul', value: 25000 },
    { name: 'Jul 15', label: '', value: 40567 },
    { name: 'Aug', label: 'Aug', value: 15500 },
    { name: 'Aug+', label: '', value: 30000 },
    { name: 'Sep', label: 'Sep', value: 25000 },
    { name: 'Sep+', label: '', value: 35000 },
    { name: 'Oct', label: 'Oct', value: 46500 },
    { name: 'Oct+', label: '', value: 33500 },
    { name: 'Nov', label: 'Nov', value: 26500 },
    { name: 'Nov+', label: '', value: 38000 },
    { name: 'Dec', label: 'Dec', value: 30500 },
  ]
  const markerIndex = 4

  return (
    <DashboardBlock dashboard={dashboard}>
      <div className="commerce-sales-card">
        <div className="commerce-sales-card__head">
          <div>
            <span>Sales statistics</span>
            <strong>$110,854.21 <em>+10%</em></strong>
          </div>
          <button type="button">This Year <i className="fa-solid fa-chevron-down" /></button>
        </div>
        <div className="commerce-sales-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 30, right: 18, bottom: 8, left: 4 }}>
              <CartesianGrid stroke="#e9edf2" vertical={false} />
              <XAxis
                dataKey="label"
                interval={0}
                tickLine={false}
                axisLine={false}
                tick={{ fill: '#596176', fontSize: 13 }}
                padding={{ left: 8, right: 8 }}
              />
              <YAxis
                domain={[10000, 50000]}
                ticks={[10000, 20000, 30000, 40000, 50000]}
                tickFormatter={(value) => `$${Number(value) / 1000}k`}
                tickLine={false}
                axisLine={false}
                tick={{ fill: '#596176', fontSize: 13 }}
                width={50}
              />
              <defs>
                <filter id="commerceLineGlow" x="-20%" y="-20%" width="140%" height="160%">
                  <feDropShadow dx="0" dy="8" stdDeviation="8" floodColor="#5fc58b" floodOpacity="0.22" />
                </filter>
              </defs>
              <Line
                type="monotone"
                dataKey="value"
                stroke="#65bf87"
                strokeWidth={2.2}
                dot={(props) => {
                  if (props.index !== markerIndex) return <g />
                  const cx = Number(props.cx)
                  const cy = Number(props.cy)
                  return (
                    <g>
                      <line x1={cx} x2={cx} y1={cy} y2={246} stroke="#cbd0d8" strokeDasharray="7 6" />
                      <path d={`M ${cx - 6} 250 L ${cx} 244 L ${cx + 6} 250 Z`} fill="#17182c" />
                      <circle cx={cx} cy={cy} r="8" fill="#e5fff1" />
                      <circle cx={cx} cy={cy} r="4.5" fill="#58c783" />
                    </g>
                  )
                }}
                activeDot={false}
                filter="url(#commerceLineGlow)"
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="commerce-sales-tooltip">
            <span>Jul, 15</span>
            <strong><i />Sales: $40,567</strong>
          </div>
        </div>
      </div>
    </DashboardBlock>
  )
}

function CommerceRecentMonthDashboard({ dashboard }: { dashboard: DashboardDefinition }) {
  const rows = [
    { name: 'Social media', value: 55, color: commercePalette.purple },
    { name: 'Purchased visitors', value: 25, color: commercePalette.cyan },
    { name: 'Affiliate visitors', value: 15, color: commercePalette.yellow },
    { name: 'By advertisement', value: 5, color: '#ff7a0b' },
  ]

  return (
    <DashboardBlock dashboard={dashboard}>
      <div className="commerce-recent-card">
        <div className="commerce-recent-card__title">
          <strong>Recent month</strong>
          <span>Visitors</span>
        </div>
        <div className="commerce-donut">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={rows}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius="72%"
                outerRadius="84%"
                startAngle={76}
                endAngle={-284}
                cornerRadius={8}
                paddingAngle={9}
                stroke="none"
              >
                {rows.map((row) => <Cell key={row.name} fill={row.color} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="commerce-donut__center">
            <span>Website<br />growth</span>
            <strong>97,14%</strong>
          </div>
        </div>
        <div className="commerce-recent-list">
          {rows.map((row) => (
            <div key={row.name}>
              <span><i style={{ background: row.color }} />{row.name}</span>
              <strong>{row.value}%</strong>
            </div>
          ))}
        </div>
      </div>
    </DashboardBlock>
  )
}

function IncomeWaveCard({ tone, offset = 0 }: { tone: 'yellow' | 'blue' | 'green'; offset?: number }) {
  const colors = {
    yellow: { base: '#ffad17', mid: '#ffc33c', pale: '#ffe7a1', pill: '#fff4d9', text: '#eda91c' },
    blue: { base: '#1689e9', mid: '#35b7ef', pale: '#bfe1ff', pill: '#edf6ff', text: '#2d82cf' },
    green: { base: '#42c916', mid: '#66df3e', pale: '#c4f7af', pill: '#edffe9', text: '#38b713' },
  }[tone]
  const rows = Array.from({ length: 26 }, (_, index) => {
    const v1 = [16, 8, 20, 34, 12, 48, 15, 41, 56, 36, 30, 44, 18, 51, 24, 39, 22, 18, 26, 14, 20, 12, 16, 23, 10, 21][(index + offset) % 26]
    const v2 = [7, 14, 11, 23, 9, 12, 6, 28, 34, 26, 18, 9, 21, 29, 12, 17, 8, 20, 15, 10, 13, 9, 11, 15, 8, 10][(index + offset * 2) % 26]
    const v3 = [28, 17, 36, 25, 42, 18, 14, 34, 26, 48, 33, 26, 39, 18, 45, 28, 19, 36, 22, 30, 18, 24, 15, 28, 20, 35][(index + offset * 3) % 26]
    return {
      name: index,
      lowTop: 50 + v1 * 0.42,
      midTop: 50 + v2 * 0.72,
      highTop: 50 + v3,
      lowBottom: 50 - v1 * 0.42,
      midBottom: 50 - v2 * 0.72,
      highBottom: 50 - v3,
    }
  })

  return (
    <div className={`income-wave-card income-wave-card--${tone}`}>
      <div className="income-wave-card__chart">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={rows} margin={{ top: 0, right: -4, bottom: 0, left: -4 }}>
            <defs>
              <linearGradient id={`income-${tone}-pale`} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={colors.pale} stopOpacity="0.92" />
                <stop offset="100%" stopColor={colors.pale} stopOpacity="0.5" />
              </linearGradient>
              <linearGradient id={`income-${tone}-mid`} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={colors.mid} stopOpacity="0.82" />
                <stop offset="100%" stopColor={colors.mid} stopOpacity="0.5" />
              </linearGradient>
              <linearGradient id={`income-${tone}-base`} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={colors.base} stopOpacity="0.88" />
                <stop offset="100%" stopColor={colors.base} stopOpacity="0.62" />
              </linearGradient>
            </defs>
            <YAxis hide domain={[0, 100]} />
            <XAxis hide dataKey="name" />
            <Area type="monotone" dataKey="highTop" stroke="none" fill={`url(#income-${tone}-pale)`} baseValue={50} isAnimationActive={false} />
            <Area type="monotone" dataKey="highBottom" stroke="none" fill={`url(#income-${tone}-pale)`} baseValue={50} isAnimationActive={false} />
            <Area type="monotone" dataKey="midTop" stroke="none" fill={`url(#income-${tone}-mid)`} baseValue={50} isAnimationActive={false} />
            <Area type="monotone" dataKey="midBottom" stroke="none" fill={`url(#income-${tone}-mid)`} baseValue={50} isAnimationActive={false} />
            <Area type="monotone" dataKey="lowTop" stroke="none" fill={`url(#income-${tone}-base)`} baseValue={50} isAnimationActive={false} />
            <Area type="monotone" dataKey="lowBottom" stroke="none" fill={`url(#income-${tone}-base)`} baseValue={50} isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
        <div className="income-wave-card__axis" style={{ background: colors.base }} />
        <div className="income-wave-card__bubble" style={{ background: colors.pill }}>
          $850
          <i style={{ borderTopColor: colors.pill }} />
        </div>
      </div>
      <div className="income-wave-card__body">
        <button type="button" aria-label="More options"><i /><i /><i /></button>
        <h3>Income intensity</h3>
        <p>Exchange-Traded Funds - 55%</p>
        <div className="income-wave-card__dots">
          {[0, 1, 2, 3, 4, 5].map((item) => (
            <span key={item} style={{ background: item < 5 ? colors.base : colors.pale, opacity: 1 - item * 0.08 }} />
          ))}
        </div>
        <div className="income-wave-card__foot">
          <strong>91%</strong>
          <span style={{ background: colors.pill, color: colors.text }}>vs last month</span>
        </div>
      </div>
    </div>
  )
}

function IncomeIntensityDashboard({ dashboard }: { dashboard: DashboardDefinition }) {
  return (
    <DashboardBlock dashboard={dashboard}>
      <div className="income-intensity-board">
        <IncomeWaveCard tone="yellow" />
        <IncomeWaveCard tone="blue" offset={4} />
        <IncomeWaveCard tone="green" offset={8} />
      </div>
    </DashboardBlock>
  )
}

function SaasTotalRevenueDashboard({ dashboard }: { dashboard: DashboardDefinition }) {
  const fallback = getDefaultChartStudioSpec('saas_total_revenue').data as { selectedIndex: number; rows: Array<{ label: string; value: number }> }
  const studio = useDashboardStudioData('saas_total_revenue', fallback)
  const rows = Array.isArray(studio.rows) ? studio.rows : fallback.rows
  const selectedIndex = Number.isFinite(Number(studio.selectedIndex)) ? Number(studio.selectedIndex) : fallback.selectedIndex

  return (
    <DashboardBlock dashboard={dashboard}>
      <div className="saas-revenue-card">
        <div className="saas-revenue-card__top">
          <div>
            <span>Total Revenue</span>
            <strong>$1,469,324,230</strong>
            <p><b>+22.325 (12.2%)</b><i />last 12 months</p>
          </div>
          <div className="saas-revenue-card__actions">
            <button type="button"><i className="fa-solid fa-calendar-days" />Last 12 months<i className="fa-solid fa-chevron-down" /></button>
            <button type="button" aria-label="Fullscreen"><i className="fa-solid fa-expand" /></button>
          </div>
        </div>
        <div className="saas-revenue-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={rows} margin={{ top: 18, right: 10, bottom: 12, left: 12 }}>
              <defs>
                <linearGradient id="saasRevenueFill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#4ec6ae" stopOpacity="0.28" />
                  <stop offset="72%" stopColor="#4ec6ae" stopOpacity="0.08" />
                  <stop offset="100%" stopColor="#4ec6ae" stopOpacity="0" />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#cfcfd8" strokeDasharray="1 26" vertical horizontal />
              <XAxis
                dataKey="label"
                interval={0}
                tickLine={false}
                axisLine={false}
                tick={{ fill: '#3f3f45', fontSize: 16 }}
                minTickGap={0}
                padding={{ left: 6, right: 4 }}
              />
              <YAxis
                type="number"
                domain={[0, 250]}
                ticks={[1, 10, 50, 100, 250]}
                tickFormatter={(value) => value === 250 ? '$1B' : value === 100 ? '$100M' : value === 50 ? '$50M' : value === 10 ? '$10M' : '$1M'}
                tickLine={false}
                axisLine={false}
                tick={{ fill: '#3f3f45', fontSize: 16 }}
                width={70}
              />
              <Area
                type="linear"
                dataKey="value"
                stroke="#4ec6ae"
                strokeWidth={2.2}
                fill="url(#saasRevenueFill)"
                dot={(props) => {
                  if (props.index !== selectedIndex) return <g />
                  const cx = Number(props.cx)
                  const cy = Number(props.cy)
                  return (
                    <g>
                      <line x1={cx} x2={cx} y1={72} y2={356} stroke="#5fbfae" strokeWidth="4" opacity="0.5" />
                      <circle cx={cx} cy={cy} r="27" fill="#e8faf6" stroke="#50bfae" strokeWidth="3" />
                      <circle cx={cx} cy={cy} r="10" fill="#ffffff" stroke="#9bd9ce" strokeWidth="2" />
                    </g>
                  )
                }}
                activeDot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
          <div className="saas-revenue-tooltip">
            <span>24 Jul 2024, 9:23 pm</span>
            <strong>Cumulative: $103,56M</strong>
          </div>
        </div>
      </div>
    </DashboardBlock>
  )
}

function LostDealsAnalysisDashboard({ dashboard }: { dashboard: DashboardDefinition }) {
  const fallback = getDefaultChartStudioSpec('crm_lost_deals_analysis').data as { rows: Array<{ name: string; value: number; color: string }> }
  const studio = useDashboardStudioData('crm_lost_deals_analysis', fallback)
  const rows = Array.isArray(studio.rows) ? studio.rows : fallback.rows

  return (
    <DashboardBlock dashboard={dashboard}>
      <div className="crm-card crm-card--lost">
        <h3>Lost Deals Analysis</h3>
        <p>Learn why deals are lost and spot recurring patterns</p>
        <div className="crm-lost-chart">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={rows}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius="50%"
                outerRadius="78%"
                startAngle={92}
                endAngle={-268}
                paddingAngle={4}
                cornerRadius={6}
                stroke="#ffffff"
                strokeWidth={4}
              >
                {rows.map((row) => <Cell key={row.name} fill={row.color} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="crm-lost-center"><span>Lost Revenue</span><strong>$160k</strong></div>
          <div className="crm-lost-tooltip"><strong>31%</strong><span>12 lost deals</span></div>
        </div>
        <div className="crm-legend">
          {rows.map((row) => <span key={row.name}><i style={{ background: row.color }} />{row.name}</span>)}
        </div>
      </div>
    </DashboardBlock>
  )
}

function SalesLeaderboardDashboard({ dashboard }: { dashboard: DashboardDefinition }) {
  const fallback = getDefaultChartStudioSpec('crm_sales_leaderboard').data as { rows: Array<{ name: string; value: number; avatar: string }> }
  const studio = useDashboardStudioData('crm_sales_leaderboard', fallback)
  const rows = Array.isArray(studio.rows) ? studio.rows : fallback.rows

  return (
    <DashboardBlock dashboard={dashboard}>
      <div className="crm-card crm-card--leaderboard">
        <h3>Sales Leaderboard</h3>
        <p>See who&apos;s closing deals and how each rep is performing</p>
        <div className="crm-leader-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} layout="vertical" margin={{ top: 2, right: 78, bottom: 0, left: 4 }} barCategoryGap={12}>
              <XAxis type="number" hide domain={[0, 130000]} />
              <YAxis type="category" dataKey="name" hide />
              <Bar dataKey="value" radius={[8, 8, 8, 8]} fill="#e8defc" isAnimationActive={false}>
                <LabelList
                  dataKey="value"
                  position="right"
                  formatter={(value: unknown) => `$${Number(value).toLocaleString('en-US')}`}
                  className="crm-leader-value"
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="crm-leader-overlay">
            {rows.map((row, index) => (
              <div key={row.name} style={{ top: `${index * 44 + 5}px` }}>
                <span>{row.avatar}</span>
                <strong>{row.name}</strong>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardBlock>
  )
}

function RevenuePerformanceDashboard({ dashboard }: { dashboard: DashboardDefinition }) {
  const fallback = getDefaultChartStudioSpec('crm_revenue_performance').data as { rows: Array<{ month: string; revenue: number | null; forecast: number | null }> }
  const studio = useDashboardStudioData('crm_revenue_performance', fallback)
  const rows = Array.isArray(studio.rows) ? studio.rows : fallback.rows

  return (
    <DashboardBlock dashboard={dashboard}>
      <div className="crm-card crm-card--performance">
        <div className="crm-performance-head">
          <div>
            <h3>Revenue Performance</h3>
            <p>Track actual revenue and upcoming forecasts by month</p>
          </div>
          <strong><span>$</span>159,5k</strong>
        </div>
        <div className="crm-performance-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 28, right: 24, bottom: 4, left: 0 }}>
              <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fill: '#666', fontSize: 14 }} />
              <YAxis domain={[100, 200]} ticks={[100, 120, 140, 160, 180, 200]} tickFormatter={(value) => `$${value}k`} tickLine={false} axisLine={false} tick={{ fill: '#666', fontSize: 14 }} width={64} />
              <Line type="monotone" dataKey="revenue" connectNulls={false} stroke="#3f45c4" strokeWidth={2.4} dot={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="forecast" connectNulls={false} stroke="#c4c1f7" strokeWidth={2.4} dot={(props) => {
                if (props.payload?.month !== 'Nov') return <g />
                return <circle cx={Number(props.cx)} cy={Number(props.cy)} r="6" fill="#5a51cf" />
              }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
          <div className="crm-performance-marker" />
        </div>
        <div className="crm-performance-legend"><span><i />Revenue</span><span><i />Forecast</span></div>
      </div>
    </DashboardBlock>
  )
}

type ChannelOrderRow = {
  platform: string
  orders: string
  products: string
  total: string
  ratio: string
  previous: string
  delta: string
  color: string
  points: ChannelOrderPoint[]
}

type ChannelOrderPoint = {
  label: string
  value: number
}

type ChannelOrderSummaryItem = {
  value: string
  sub: string
  delta: string
}

type ChannelOrderPaymentBreakdownItem = {
  key?: string
  label: string
  value: string
}

type ChannelOrdersPayload = {
  summary: {
    orders: ChannelOrderSummaryItem
    products: ChannelOrderSummaryItem
    total: ChannelOrderSummaryItem
  }
  dateLabel: string
  paymentBreakdown?: ChannelOrderPaymentBreakdownItem[]
  rows: ChannelOrderRow[]
}

type LegacyChannelOrdersPayload = {
  name?: string
  orderCount?: number
  revenue?: number
  percent?: number
  todayCount?: number
  todayRevenue?: number
}[]

function channelOrderFallbackLabels(period: string, count: number) {
  const now = new Date()
  const isHourly = period === 'Bugün' || period === 'Dün' || period === 'Son 24 Saat'
  if (isHourly) {
    const start = new Date(now)
    if (period === 'Bugün') start.setHours(0, 0, 0, 0)
    else if (period === 'Dün') {
      start.setDate(start.getDate() - 1)
      start.setHours(0, 0, 0, 0)
    } else {
      start.setHours(start.getHours() - Math.max(count - 1, 0), 0, 0, 0)
    }
    return Array.from({ length: count }, (_, index) => {
      const date = new Date(start)
      date.setHours(start.getHours() + index)
      return new Intl.DateTimeFormat('tr-TR', { hour: '2-digit', minute: '2-digit' }).format(date)
    })
  }

  const start = new Date(now)
  if (period === 'Son 30 Gün') start.setDate(start.getDate() - Math.max(count - 1, 0))
  else if (period === 'Bu Ay') start.setDate(1)
  else start.setDate(start.getDate() - Math.max(count - 1, 0))
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(start)
    date.setDate(start.getDate() + index)
    return new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: 'short' }).format(date)
  })
}

function channelOrderPoints(values: number[], period = 'Son 7 Gün') {
  const labels = channelOrderFallbackLabels(period, values.length)
  return values.map((value, index) => ({ label: labels[index] || `${index + 1}`, value }))
}

function normalizeChannelOrderPoints(points: ChannelOrderPoint[] | number[] | undefined, period: string) {
  if (!Array.isArray(points)) return []
  const labels = channelOrderFallbackLabels(period, points.length)
  return points.map((point, index) => (
    typeof point === 'number'
      ? { label: labels[index] || `${index + 1}`, value: point }
      : {
        label: /^\d+$/.test(String(point.label || '')) ? (labels[index] || String(point.label)) : String(point.label || labels[index] || index + 1),
        value: Number(point.value || 0),
      }
  ))
}

function normalizeChannelOrderRow(row: ChannelOrderRow, period: string) {
  return {
    ...row,
    points: normalizeChannelOrderPoints(row.points, period),
  }
}

const channelOrderRows: ChannelOrderRow[] = []

const channelOrderPeriodOptions = ['Bugün', 'Dün', 'Son 24 Saat', 'Son 7 Gün', 'Son 30 Gün', 'Bu Ay']
const channelOrderCompareOptions = ['Fiyata Göre', 'Sipariş Adedine Göre', 'Ürün Adedine Göre']
const channelOrderPeriodValues: Record<string, string> = {
  'Bugün': 'today',
  'Dün': 'yesterday',
  'Son 24 Saat': 'last_24_hours',
  'Son 7 Gün': 'last_7_days',
  'Son 30 Gün': 'last_30_days',
  'Bu Ay': 'this_month',
}
const channelOrderCompareValues: Record<string, string> = {
  'Fiyata Göre': 'price',
  'Sipariş Adedine Göre': 'orders',
  'Ürün Adedine Göre': 'products',
}

function formatChannelOrderMoney(value: number) {
  return `${value.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`
}

function channelOrderDeltaClass(delta?: string) {
  const value = String(delta || '').trim()
  if (value.startsWith('-')) return 'is-down'
  if (value.startsWith('+')) return 'is-up'
  return ''
}

function dashboardLongDate(date: Date) {
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date)
}

function dashboardDateLabelForPeriod(period: string) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const end = new Date(today)
  const start = new Date(today)

  if (period === 'Dün') {
    start.setDate(start.getDate() - 1)
    end.setDate(end.getDate() - 1)
  } else if (period === 'Son 24 Saat') {
    start.setDate(start.getDate() - 1)
  } else if (period === 'Son 30 Gün') {
    start.setDate(start.getDate() - 29)
  } else if (period === 'Bu Ay') {
    start.setDate(1)
  } else if (period === 'Son 7 Gün') {
    start.setDate(start.getDate() - 7)
  }

  return `${dashboardLongDate(start)} - ${dashboardLongDate(end)}`
}

type MarketplaceProductErrorsSummary = {
  active: number
  archived: number
  ignored: number
  productCount: number
  marketplaceCount: number
  lastLogAt?: string | null
  lastCheckedAt?: string | null
  firmCode?: string
  firmName?: string
  healthLabel?: string
  healthTone?: 'green' | 'orange' | 'red'
}

type MarketplaceProductErrorsGroup = {
  errorCode: string
  errorLabel: string
  total: number
  productCount: number
  marketplaces: string
}

type MarketplaceProductErrorsMarket = {
  marketplace: string
  total: number
  productCount: number
  lastLogAt?: string | null
}

type MarketplaceProductErrorsRecent = {
  id: number
  marketplace: string
  itemBarcode: string
  errorLabel: string
  errorMessage: string
  logAt?: string | null
}

type MarketplaceProductErrorsPayload = {
  summary: MarketplaceProductErrorsSummary
  groups: MarketplaceProductErrorsGroup[]
  marketplaces: MarketplaceProductErrorsMarket[]
  recent: MarketplaceProductErrorsRecent[]
}

function formatDashboardCount(value?: number | string | null) {
  const parsed = Number(value || 0)
  return Number.isFinite(parsed) ? parsed.toLocaleString('tr-TR') : '0'
}

function dashboardShortDateTime(value?: string | null) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

function marketplaceDashboardLabel(value?: string | null) {
  const text = String(value || '').trim()
  const key = text.toLowerCase()
  const labels: Record<string, string> = {
    trendyol: 'Trendyol',
    hepsiburada: 'HepsiBurada',
    hepsiburadastock: 'HB Stok',
    hepsiburadaprice: 'HB Fiyat',
    boyner: 'Boyner',
    pazarama: 'Pazarama',
    lcwaikiki: 'LC Waikiki',
    idefix: 'İdefix',
    'n11-new': 'N11',
  }
  return labels[key] || text || 'Pazaryeri'
}

function MarketplaceProductErrorsDashboard() {
  const { ref, visible } = useDashboardInView()
  const { data, loading, error } = useDashboardWidgetData<MarketplaceProductErrorsPayload>({
    endpoint: '/panel/dashboard-widgets/marketplace-product-errors',
    params: {},
    enabled: visible,
  })
  const summary = data?.summary || {
    active: 0,
    archived: 0,
    ignored: 0,
    productCount: 0,
    marketplaceCount: 0,
    lastLogAt: null,
    lastCheckedAt: null,
    healthLabel: 'Kayıt yok',
    healthTone: 'green' as const,
  }
  const groups = data?.groups || []
  const markets = data?.marketplaces || []
  const recent = data?.recent || []
  const tickerRows = recent.slice(0, 6)
  const maxMarketTotal = Math.max(1, ...markets.map((item) => Number(item.total || 0)))
  const healthTone = summary.healthTone || 'green'

  return (
    <section ref={ref} className={`panel-dashboard-block marketplace-errors-card ${loading ? 'is-loading' : ''}`}>
      <div className="panel-dashboard-block__head marketplace-errors-head">
        <div>
          <strong>PAZARYERİ ÜRÜN HATALARI</strong>
          <span>{summary.firmName || summary.firmCode || 'Aktif firma'} için canlı hata özeti</span>
        </div>
      </div>

      <div className="marketplace-errors-body">
        {error && !data ? (
          <div className="marketplace-errors-empty">
            <i className="fa-duotone fa-triangle-exclamation" />
            <span>{error}</span>
          </div>
        ) : (
          <>
            <div className="marketplace-errors-topline">
              <div className={`marketplace-errors-health is-${healthTone}`}>
                <span>{summary.healthLabel || 'Genel durum'}</span>
                <strong>{formatDashboardCount(summary.active)} aktif hata</strong>
              </div>
              <div className="marketplace-errors-stats">
                <span><b>{formatDashboardCount(summary.productCount)}</b> Ürün</span>
                <span><b>{formatDashboardCount(summary.marketplaceCount)}</b> Pazaryeri</span>
                <span><b>{dashboardShortDateTime(summary.lastCheckedAt)}</b> Son kontrol</span>
              </div>
            </div>

            <div className="marketplace-errors-latest" aria-label="Son pazaryeri hata cevabı">
              {tickerRows.length ? (
                <div className="marketplace-errors-marquee">
                  <div className="marketplace-errors-marquee-track">
                    {[...tickerRows, ...tickerRows].map((item, index) => (
                      <span className="marketplace-errors-ticker-item" key={`${item.id}-${index}`}>
                        <span>Son hata</span>
                        <strong>{marketplaceDashboardLabel(item.marketplace)}</strong>
                        <em>{item.itemBarcode || '-'}</em>
                        <b>{item.errorLabel || item.errorMessage || 'Hata'}</b>
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <span>Son hata kaydı bulunmuyor</span>
              )}
            </div>

            <div className="marketplace-errors-grid">
              <div className="marketplace-errors-market-list">
                <div className="marketplace-errors-section-title">
                  <strong>Pazaryeri</strong>
                </div>
                {markets.length ? markets.slice(0, 4).map((item) => (
                  <div key={item.marketplace} className="marketplace-errors-market-row">
                    <span>{marketplaceDashboardLabel(item.marketplace)}</span>
                    <strong>{formatDashboardCount(item.total)}</strong>
                    <em style={{ width: `${Math.max(8, (Number(item.total || 0) / maxMarketTotal) * 100)}%` }} />
                  </div>
                )) : (
                  <div className="marketplace-errors-muted">Aktif pazaryeri hatası yok.</div>
                )}
              </div>

              <div className="marketplace-errors-group-list">
                <div className="marketplace-errors-section-title">
                  <strong>Hata grupları</strong>
                  <span>İlk 3 sorun</span>
                </div>
                {groups.length ? groups.slice(0, 3).map((group) => (
                  <div key={group.errorCode} className="marketplace-errors-group-row">
                    <div>
                      <strong>{group.errorLabel}</strong>
                      <span>{formatDashboardCount(group.productCount)} ürün · {group.marketplaces || '-'}</span>
                    </div>
                    <b>{formatDashboardCount(group.total)}</b>
                  </div>
                )) : (
                  <div className="marketplace-errors-muted">Hata grubu oluşmadı.</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  )
}

function zeroMetric(metric: CartMemberMetric): CartMemberMetric {
  return { ...metric, value: 0 }
}

function channelOrderCompareMetric(compareBy: string) {
  if (compareBy === 'Fiyata Göre') return 'price'
  if (compareBy === 'Ürün Adedine Göre') return 'products'
  return 'orders'
}

function formatChannelCompareValue(value: number, compareBy: string) {
  if (channelOrderCompareMetric(compareBy) === 'price') return formatChannelOrderMoney(value)
  return `${value} Adet`
}

function legacyChannelOrdersToPayload(rows: LegacyChannelOrdersPayload | null, compareBy: string): ChannelOrdersPayload | null {
  if (!Array.isArray(rows) || rows.length === 0) return null
  const totalOrders = rows.reduce((sum, row) => sum + Number(row.orderCount || 0), 0)
  const totalRevenue = rows.reduce((sum, row) => sum + Number(row.revenue || 0), 0)
  return {
    dateLabel: 'Son 7 Gün',
    summary: {
      orders: { value: `${totalOrders} Sipariş`, sub: `${rows.reduce((sum, row) => sum + Number(row.todayCount || 0), 0)} Adet`, delta: '%0' },
      products: { value: `${totalOrders} Ürün`, sub: `${rows.reduce((sum, row) => sum + Number(row.todayCount || 0), 0)} Adet`, delta: '%0' },
      total: { value: formatChannelOrderMoney(totalRevenue), sub: formatChannelOrderMoney(rows.reduce((sum, row) => sum + Number(row.todayRevenue || 0), 0)), delta: '%0' },
    },
    paymentBreakdown: [],
    rows: rows.slice(0, 8).map((row, index) => {
      const orderCount = Number(row.orderCount || 0)
      const revenue = Number(row.revenue || 0)
      const metric = channelOrderCompareMetric(compareBy)
      const compareValue = metric === 'price' ? revenue : orderCount
      const totalCompareValue = metric === 'price' ? totalRevenue : totalOrders
      const previousValue = metric === 'price' ? Number(row.todayRevenue || 0) : Number(row.todayCount || 0)
      return {
        platform: row.name || `Kanal ${index + 1}`,
        orders: String(orderCount),
        products: String(orderCount),
        total: formatChannelOrderMoney(revenue),
        ratio: `%${String(totalCompareValue > 0 ? Number(((compareValue / totalCompareValue) * 100).toFixed(2)) : 0).replace('.', ',')}`,
        previous: formatChannelCompareValue(previousValue, compareBy),
        delta: '%0',
        color: ['#ff321a', '#ff7b0a', '#33b6d1', '#10b981', '#4966ff', '#8b5cf6', '#f59e0b', '#ef4444'][index % 8],
        points: channelOrderPoints(Array(14).fill(compareValue)),
      }
    }),
  }
}

function ChannelSparkTooltip({
  active,
  payload,
  label,
  row,
  compareBy,
}: {
  active?: boolean
  payload?: { value?: number | string }[]
  label?: string | number
  row: ChannelOrderRow
  compareBy: string
}) {
  if (!active || !payload?.length) return null
  const value = Number(payload[0]?.value || 0)
  return (
    <div className="channel-orders-tooltip">
      <span>{label}</span>
      <strong>{row.platform}</strong>
      <em>{formatChannelCompareValue(value, compareBy)}</em>
    </div>
  )
}

function ChannelSparkline({ row, compareBy }: { row: ChannelOrderRow; compareBy: string }) {
  return (
    <div className="channel-orders-spark">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={row.points} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
          <XAxis dataKey="label" hide />
          <YAxis hide domain={['dataMin', 'dataMax']} />
          <Tooltip
            cursor={{ stroke: '#b8c2ff', strokeDasharray: '2 3', strokeWidth: 1 }}
            content={<ChannelSparkTooltip row={row} compareBy={compareBy} />}
            wrapperStyle={{ outline: 'none' }}
          />
          <Line type="monotone" dataKey="value" stroke={row.color} strokeWidth={1.8} dot={false} activeDot={{ r: 3, fill: row.color, stroke: '#fff', strokeWidth: 1.5 }} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

function DashboardWidgetDropdown({
  value,
  options,
  open,
  onOpenChange,
  onSelect,
  icon,
  suffix,
}: {
  value: string
  options: string[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (value: string) => void
  icon?: string
  suffix?: string
}) {
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const [placement, setPlacement] = useState({ vertical: 'down', align: 'left' })

  useEffect(() => {
    if (!open) return
    const updatePlacement = () => {
      const trigger = triggerRef.current
      if (!trigger) return
      const bounds = trigger.getBoundingClientRect()
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth
      const menuHeight = Math.min(260, Math.max(46, options.length * 24 + 12))
      const menuWidth = Math.max(118, ...options.map((option) => option.length * 7 + 38))
      const dashboardBounds = trigger.closest('.panel-dashboard-block')?.getBoundingClientRect()
      const lowerBoundary = dashboardBounds ? Math.min(dashboardBounds.bottom, viewportHeight) : viewportHeight
      const upperBoundary = dashboardBounds ? Math.max(dashboardBounds.top, 0) : 0
      const spaceBelow = lowerBoundary - bounds.bottom
      const spaceAbove = bounds.top - upperBoundary
      setPlacement({
        vertical: spaceBelow < menuHeight + 12 && spaceAbove > spaceBelow ? 'up' : 'down',
        align: bounds.left + menuWidth > viewportWidth - 10 ? 'right' : 'left',
      })
    }

    updatePlacement()
    window.addEventListener('resize', updatePlacement)
    window.addEventListener('scroll', updatePlacement, true)
    return () => {
      window.removeEventListener('resize', updatePlacement)
      window.removeEventListener('scroll', updatePlacement, true)
    }
  }, [open, options])

  return (
    <div className="channel-orders-dropdown">
      <button
        ref={triggerRef}
        type="button"
        className="channel-orders-dropdown__trigger"
        onClick={() => onOpenChange(!open)}
      >
        {icon && <i className={icon} />}
        <span>{value}</span>
        {suffix && <em>{suffix}</em>}
        <i className="fa-regular fa-chevron-down" />
      </button>
      {open && (
        <div className={`channel-orders-dropdown__menu ${placement.vertical === 'up' ? 'is-up' : ''} ${placement.align === 'right' ? 'is-right' : ''}`}>
          {options.map((option) => (
            <button
              key={option}
              type="button"
              className={option === value ? 'is-selected' : ''}
              onClick={() => {
                onSelect(option)
                onOpenChange(false)
              }}
            >
              {option === value && <i className="fa-regular fa-check" />}
              <span>{option}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function ChannelOrdersDashboard({ dashboard }: { dashboard: DashboardDefinition }) {
  const [period, setPeriod] = useState('Bugün')
  const [compareBy, setCompareBy] = useState('Fiyata Göre')
  const [includePendingTransfers, setIncludePendingTransfers] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<'period' | 'compare' | null>(null)
  const tableScrollRef = useRef<HTMLDivElement | null>(null)
  const tableDragRef = useRef({
    active: false,
    pointerId: -1,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  })
  const [isTableDragging, setIsTableDragging] = useState(false)
  const { ref, visible } = useDashboardInView()
  const widgetParams = useMemo(() => ({
    period: channelOrderPeriodValues[period] || 'last_7_days',
    compare_by: channelOrderCompareValues[compareBy] || 'orders',
    include_pending_transfers: includePendingTransfers ? '1' : '0',
  }), [compareBy, includePendingTransfers, period])
  const { data, loading, error } = useDashboardWidgetData<ChannelOrdersPayload>({
    endpoint: '/panel/dashboard-widgets/orders-by-channel',
    params: widgetParams,
    enabled: visible,
  })
  const {
    data: legacyRows,
    loading: legacyLoading,
    error: legacyError,
  } = useDashboardWidgetData<LegacyChannelOrdersPayload>({
    endpoint: '/panel/order-dashboard/channels',
    params: { include_pending_transfers: includePendingTransfers ? '1' : '0' },
    enabled: visible && Boolean(error),
  })
  const legacyData = useMemo(() => legacyChannelOrdersToPayload(legacyRows, compareBy), [compareBy, legacyRows])
  const displayData = data || legacyData
  const rows = (displayData?.rows?.length ? displayData.rows : channelOrderRows)
    .map((row) => normalizeChannelOrderRow(row, period))
  const summary = displayData?.summary || {
    orders: { value: '0 Sipariş', sub: '0 Adet', delta: '%0' },
    products: { value: '0 Ürün', sub: '0 Adet', delta: '%0' },
    total: { value: formatChannelOrderMoney(0), sub: formatChannelOrderMoney(0), delta: '%0' },
  }
  const paymentBreakdown = Array.isArray(displayData?.paymentBreakdown) ? displayData.paymentBreakdown : []
  const displayError = displayData ? '' : (legacyError || error)
  const handleTablePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse') return
    if (event.button !== 0) return
    const element = tableScrollRef.current
    if (!element) return

    tableDragRef.current = {
      active: true,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: element.scrollLeft,
      scrollTop: element.scrollTop,
    }
    setIsTableDragging(true)
    event.currentTarget.setPointerCapture(event.pointerId)
  }
  const handleTablePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const drag = tableDragRef.current
    const element = tableScrollRef.current
    if (!drag.active || !element) return

    element.scrollLeft = drag.scrollLeft - (event.clientX - drag.startX)
    element.scrollTop = drag.scrollTop - (event.clientY - drag.startY)
    event.preventDefault()
  }
  const stopTableDrag = (event: PointerEvent<HTMLDivElement>) => {
    const drag = tableDragRef.current
    if (!drag.active) return
    if (event.currentTarget.hasPointerCapture(drag.pointerId)) {
      event.currentTarget.releasePointerCapture(drag.pointerId)
    }
    tableDragRef.current = { ...drag, active: false }
    setIsTableDragging(false)
  }

  return (
    <section ref={ref} className={`panel-dashboard-block channel-orders-card ${loading || legacyLoading ? 'is-loading' : ''}`}>
      <div className="panel-dashboard-block__head channel-orders-card__top">
        <div className="channel-orders-title">
          <strong>TOPLAM SİPARİŞLER</strong>
          <span>{displayData?.dateLabel || dashboardDateLabelForPeriod(period)}</span>
        </div>
        <button
          type="button"
          className={`channel-orders-transfer-toggle ${includePendingTransfers ? 'is-active' : ''}`}
          role="switch"
          aria-checked={includePendingTransfers}
          onClick={() => setIncludePendingTransfers((active) => !active)}
        >
          <span className="channel-orders-transfer-toggle__track">
            <span className="channel-orders-transfer-toggle__thumb" />
          </span>
          <span>Ödenmemiş Havaleleri Dahil Et</span>
        </button>
        <div className="channel-orders-summary" aria-label={dashboardTitle(dashboard)}>
          <div>
            <strong>{summary.orders.value}</strong>
            <em className={channelOrderDeltaClass(summary.orders.delta)}>{summary.orders.delta}</em>
          </div>
          <div>
            <strong>{summary.products.value}</strong>
            <em className={channelOrderDeltaClass(summary.products.delta)}>{summary.products.delta}</em>
          </div>
          <div>
            <span className="channel-orders-total-value">
              <strong>{summary.total.value}</strong>
              {paymentBreakdown.length > 0 && (
                <span className="channel-orders-total-info">
                  <button type="button" aria-label="Toplam tutar kırılımı">?</button>
                  <span className="channel-orders-total-tooltip" role="tooltip">
                    {paymentBreakdown.map((item) => (
                      <span key={item.key || item.label}>
                        <b>{item.label}</b>
                        <em>{item.value}</em>
                      </span>
                    ))}
                  </span>
                </span>
              )}
            </span>
            <em className={channelOrderDeltaClass(summary.total.delta)}>{summary.total.delta}</em>
          </div>
        </div>
      </div>
      {displayError && <div className="channel-orders-error">{displayError}</div>}
      <div className="channel-orders-dash" />
      <div
        ref={tableScrollRef}
        className={`channel-orders-scroll ${isTableDragging ? 'is-dragging' : ''}`}
        onPointerDown={handleTablePointerDown}
        onPointerMove={handleTablePointerMove}
        onPointerUp={stopTableDrag}
        onPointerCancel={stopTableDrag}
        onLostPointerCapture={stopTableDrag}
      >
        <div className="channel-orders-table">
          <div className="channel-orders-row channel-orders-row--head">
            <span>Platform</span>
            <span>Sipariş</span>
            <span>Ürün</span>
            <span>Toplam Tutar</span>
            <span>% Oran</span>
            <span>Grafik</span>
            <span>Önceki Dönem</span>
          </div>
          {rows.map((row) => (
            <div key={row.platform} className="channel-orders-row">
              <strong>{row.platform}</strong>
              <span>{row.orders}</span>
              <span>{row.products}</span>
              <span>{row.total}</span>
              <span>{row.ratio}</span>
              <ChannelSparkline row={row} compareBy={compareBy} />
              <span className="channel-orders-prev">
                <b>{row.previous}</b>
                <em className={channelOrderDeltaClass(row.delta)}>{row.delta}</em>
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="channel-orders-foot">
        <DashboardWidgetDropdown
          value={period}
          options={channelOrderPeriodOptions}
          open={openDropdown === 'period'}
          onOpenChange={(nextOpen) => setOpenDropdown(nextOpen ? 'period' : null)}
          onSelect={setPeriod}
          icon="fa-regular fa-calendar-days"
        />
        <DashboardWidgetDropdown
          value={compareBy}
          options={channelOrderCompareOptions}
          open={openDropdown === 'compare'}
          onOpenChange={(nextOpen) => setOpenDropdown(nextOpen ? 'compare' : null)}
          onSelect={setCompareBy}
          suffix="Kıyasla"
        />
      </div>
    </section>
  )
}

type SalesReportPoint = {
  label: string
  orderTotal: number
  orderCount: number
}

type SalesReportPayload = {
  dateLabel: string
  totalAmount: number
  totalOrders: number
  points: SalesReportPoint[]
}

const salesReportPeriodOptions = ['Son 7 Gün', 'Son 30 Gün', 'Bu Ay']
const salesReportPeriodValues: Record<string, string> = {
  'Son 7 Gün': 'last_7_days',
  'Son 30 Gün': 'last_30_days',
  'Bu Ay': 'this_month',
}

function salesReportFallbackPayload(period: string): SalesReportPayload {
  const pointCount = period === 'Son 30 Gün'
    ? 30
    : period === 'Bu Ay'
      ? Math.max(1, new Date().getDate())
      : 8
  const labels = channelOrderFallbackLabels(period, pointCount)
  return {
    dateLabel: dashboardDateLabelForPeriod(period),
    totalAmount: 0,
    totalOrders: 0,
    points: labels.map((label) => ({ label, orderTotal: 0, orderCount: 0 })),
  }
}

function SalesReportTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: { dataKey?: string | number; value?: number | string }[]
  label?: string | number
}) {
  if (!active || !payload?.length) return null
  const total = payload.find((item) => item.dataKey === 'orderTotal')
  const count = payload.find((item) => item.dataKey === 'orderCount')
  return (
    <div className="channel-orders-tooltip sales-report-tooltip">
      <span>{label}</span>
      <strong>{formatChannelOrderMoney(Number(total?.value || 0))}</strong>
      <em>{formatCompact(Number(count?.value || 0))} Sipariş</em>
    </div>
  )
}

function salesReportAxisMax(points: SalesReportPoint[]) {
  const maxValue = Math.max(0, ...points.map((point) => Number(point.orderTotal || 0)))
  if (maxValue <= 0) return 1
  const magnitude = 10 ** Math.floor(Math.log10(maxValue))
  const ratio = maxValue / magnitude
  const step = ratio <= 2 ? magnitude / 5 : ratio <= 5 ? magnitude / 2 : magnitude
  return Math.ceil(maxValue / step) * step
}

function formatSalesReportAxisTick(value: number | string) {
  const amount = Number(value || 0)
  const absolute = Math.abs(amount)
  if (absolute >= 1_000_000) {
    return `${(amount / 1_000_000).toLocaleString('tr-TR', {
      maximumFractionDigits: absolute >= 10_000_000 ? 0 : 1,
    })} Mn`
  }
  if (absolute >= 1_000) return `${Math.round(amount / 1_000).toLocaleString('tr-TR')} B`
  return amount.toLocaleString('tr-TR')
}

function SalesReportDashboard() {
  const [period, setPeriod] = useState('Son 7 Gün')
  const [openDropdown, setOpenDropdown] = useState(false)
  const { ref, visible } = useDashboardInView()
  const params = useMemo(() => ({
    period: salesReportPeriodValues[period] || 'last_7_days',
  }), [period])
  const { data, loading } = useDashboardWidgetData<SalesReportPayload>({
    endpoint: '/panel/dashboard-widgets/order-sales-report',
    params,
    enabled: visible,
  })
  const fallback = useMemo(() => salesReportFallbackPayload(period), [period])
  const payload = data || fallback
  const points = Array.isArray(payload.points) && payload.points.length ? payload.points : fallback.points
  const amountAxisMax = salesReportAxisMax(points)
  const countAxisMax = Math.max(1, ...points.map((point) => Number(point.orderCount || 0)))

  return (
    <section ref={ref} className={`panel-dashboard-block cart-member-card sales-report-card ${loading ? 'is-loading' : ''}`}>
      <div className="panel-dashboard-block__head cart-member-head sales-report-head">
        <div className="sales-report-title">
          <strong>SATIŞ RAPORU</strong>
          <span>{payload.dateLabel}</span>
        </div>
        <div className="sales-report-summary">
          <div>
            <strong>{formatChannelOrderMoney(Number(payload.totalAmount || 0))}</strong>
            <em><i style={{ background: '#4966ff' }} />Sipariş Tutarı</em>
          </div>
          <div>
            <strong>{formatCompact(Number(payload.totalOrders || 0))} Adet</strong>
            <em><i style={{ background: '#34c98b' }} />Sipariş Adedi</em>
          </div>
        </div>
      </div>
      <div className="cart-member-dash" />
      <div className="sales-report-body">
        <div className="sales-report-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 10, right: 8, bottom: 18, left: 0 }}>
              <CartesianGrid vertical={false} stroke="#eef1f8" />
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#8b95c5', fontSize: 10, fontWeight: 600 }} dy={10} />
              <YAxis
                yAxisId="amount"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#8b95c5', fontSize: 9, fontWeight: 600 }}
                tickFormatter={formatSalesReportAxisTick}
                domain={[0, amountAxisMax]}
                tickCount={5}
                width={44}
              />
              <YAxis yAxisId="count" hide orientation="right" domain={[0, countAxisMax]} />
              <Tooltip cursor={{ stroke: '#c7d1f5', strokeDasharray: '3 3', strokeWidth: 1 }} content={<SalesReportTooltip />} wrapperStyle={{ outline: 'none' }} />
              <Line yAxisId="amount" type="monotone" dataKey="orderTotal" name="Sipariş Tutarı" stroke="#4966ff" strokeWidth={1.8} dot={{ r: 3, fill: '#4966ff', stroke: '#4966ff' }} activeDot={{ r: 4, fill: '#4966ff', stroke: '#fff', strokeWidth: 1.5 }} isAnimationActive={false} />
              <Line yAxisId="count" type="monotone" dataKey="orderCount" name="Sipariş Adedi" stroke="#34c98b" strokeWidth={1.8} dot={{ r: 3, fill: '#34c98b', stroke: '#34c98b' }} activeDot={{ r: 4, fill: '#34c98b', stroke: '#fff', strokeWidth: 1.5 }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="channel-orders-foot sales-report-foot">
        <DashboardWidgetDropdown
          value={period}
          options={salesReportPeriodOptions}
          open={openDropdown}
          onOpenChange={setOpenDropdown}
          onSelect={setPeriod}
          icon="fa-regular fa-calendar-days"
        />
      </div>
    </section>
  )
}

type HourlyOrdersCell = {
  day: string
  dayIndex?: number
  slot: string
  orders: number
  products: number
  total: number
  intensity: number
}

type HourlyOrdersPayload = {
  dateLabel: string
  totalLabel: string
  days: string[]
  slots: string[]
  cells: HourlyOrdersCell[]
  maxOrders: number
  platforms?: DashboardPlatformOption[]
}

type DashboardPlatformOption = { label: string; value: string }

const hourlyFallbackSlots = ['00-02', '02-04', '04-06', '06-08', '08-10', '10-12', '12-14', '14-16', '16-18', '18-20', '20-22', '22-24']
const hourlyDefaultPlatformOption = { label: 'Tüm Platformlar', value: 'all' }

function normalizeDashboardPlatformOptions(options?: DashboardPlatformOption[]) {
  if (!Array.isArray(options) || !options.length) return [hourlyDefaultPlatformOption]
  const seen = new Set<string>()
  const normalized = [hourlyDefaultPlatformOption]
  options.forEach((option) => {
    const value = String(option?.value || '').trim() || 'all'
    const label = String(option?.label || '').trim() || value
    if (!value || value === 'all' || seen.has(value)) return
    seen.add(value)
    normalized.push({ label, value })
  })
  return normalized
}

function hourlyDayLabel(date: Date) {
  return ['Pazar', 'Pzt', 'Salı', 'Çrş', 'Prş', 'Cuma', 'Cts'][date.getDay()] || ''
}

function hourlyRollingDays() {
  const end = new Date()
  const start = new Date(end)
  start.setDate(start.getDate() - 7)
  start.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)
  const days: string[] = []
  for (const cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
    days.push(hourlyDayLabel(cursor))
  }
  return days
}

function hourlyFallbackPayload(): HourlyOrdersPayload {
  const days = hourlyRollingDays()
  const cells = days.flatMap((day, dayIndex) => hourlyFallbackSlots.map((slot) => ({ day, dayIndex, slot, orders: 0, products: 0, total: 0, intensity: 0 })))
  return { dateLabel: dashboardDateLabelForPeriod('Son 7 Gün'), totalLabel: '0 Adet', days, slots: hourlyFallbackSlots, cells, maxOrders: 1, platforms: [hourlyDefaultPlatformOption] }
}

function normalizeHourlyPayload(payload: HourlyOrdersPayload): HourlyOrdersPayload {
  const days = hourlyRollingDays()
  const cells = (payload.cells || []).map((cell) => {
    const sourceIndex = typeof cell.dayIndex === 'number' ? cell.dayIndex : days.indexOf(cell.day)
    const dayIndex = sourceIndex >= 0 && sourceIndex < days.length ? sourceIndex : 0
    return {
      ...cell,
      day: days[dayIndex] || cell.day,
      dayIndex,
    }
  })
  const platforms = Array.isArray(payload.platforms) && payload.platforms.length
    ? normalizeDashboardPlatformOptions(payload.platforms)
    : [hourlyDefaultPlatformOption]
  return { ...payload, days, cells, platforms }
}

function HourlyHeatmapTooltip({ cell }: { cell: HourlyOrdersCell | null }) {
  if (!cell || !cell.orders) return null
  return (
    <div className="hourly-orders-tooltip">
      <span>{cell.day} · {cell.slot}</span>
      <strong>{cell.orders} Sipariş</strong>
      <em>{cell.products} Ürün</em>
      <em>{formatChannelOrderMoney(cell.total)}</em>
    </div>
  )
}

function HourlyOrdersDashboard() {
  const [platformValue, setPlatformValue] = useState(hourlyDefaultPlatformOption.value)
  const [open, setOpen] = useState(false)
  const [hovered, setHovered] = useState<HourlyOrdersCell | null>(null)
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 })
  const [tooltipSide, setTooltipSide] = useState<'right' | 'left'>('right')
  const { ref, visible } = useDashboardInView()
  const params = useMemo(() => ({ platform: platformValue }), [platformValue])
  const { data, loading } = useDashboardWidgetData<HourlyOrdersPayload>({
    endpoint: '/panel/dashboard-widgets/orders-by-hour',
    params,
    enabled: visible,
  })
  const fallback = useMemo(() => hourlyFallbackPayload(), [])
  const payload = useMemo(() => normalizeHourlyPayload(data || fallback), [data, fallback])
  const platformOptions = useMemo(() => payload.platforms || [hourlyDefaultPlatformOption], [payload.platforms])
  const selectedPlatform = platformOptions.find((option) => option.value === platformValue) || hourlyDefaultPlatformOption
  const cellsByKey = useMemo(() => {
    const map = new Map<string, HourlyOrdersCell>()
    payload.cells.forEach((cell) => map.set(`${cell.dayIndex ?? payload.days.indexOf(cell.day)}-${cell.slot}`, cell))
    return map
  }, [payload.cells, payload.days])

  return (
    <section ref={ref} className={`panel-dashboard-block hourly-orders-card ${loading ? 'is-loading' : ''}`}>
      <div className="panel-dashboard-block__head hourly-orders-head">
        <div>
          <strong>GÜNÜN SAATLERİNE GÖRE SİPARİŞLER</strong>
          <span>{payload.dateLabel}</span>
        </div>
        <b>{payload.totalLabel}</b>
      </div>
      <div className="hourly-orders-dash" />
      <div className="hourly-orders-grid" style={{ '--hourly-day-count': payload.days.length } as CSSProperties} onMouseLeave={() => setHovered(null)}>
        <div className="hourly-orders-grid__corner" />
        {payload.days.map((day, dayIndex) => <div key={`${day}-${dayIndex}`} className="hourly-orders-grid__day">{day}</div>)}
        {payload.slots.map((slot) => (
          <Fragment key={slot}>
            <div className="hourly-orders-grid__slot">{slot}</div>
            {payload.days.map((day, dayIndex) => {
              const cell = cellsByKey.get(`${dayIndex}-${slot}`) || { day, dayIndex, slot, orders: 0, products: 0, total: 0, intensity: 0 }
              const alpha = cell.orders ? Math.max(0.18, Math.min(1, cell.intensity)) : 0
              return (
                <button
                  key={`${day}-${dayIndex}-${slot}`}
                  type="button"
                  className="hourly-orders-cell"
                  style={{ backgroundColor: alpha ? `rgba(35, 96, 255, ${alpha})` : 'transparent' }}
                  onMouseMove={(event) => {
                    const bounds = event.currentTarget.parentElement?.getBoundingClientRect()
                    setHovered(cell)
                    if (bounds) {
                      const localX = event.clientX - bounds.left
                      const localY = event.clientY - bounds.top
                      const tooltipWidth = 118
                      const tooltipHeight = 84
                      const leftSpace = localX
                      const rightSpace = bounds.width - localX
                      const shouldOpenLeft = rightSpace < tooltipWidth + 18 && leftSpace > rightSpace
                      const x = shouldOpenLeft
                        ? Math.max(tooltipWidth + 12, localX - 12)
                        : Math.max(12, Math.min(bounds.width - tooltipWidth - 12, localX + 12))
                      const y = Math.max(tooltipHeight + 12, Math.min(bounds.height - 4, localY))
                      setTooltipSide(shouldOpenLeft ? 'left' : 'right')
                      setTooltipPos({ x, y })
                    }
                  }}
                  aria-label={`${day} ${slot}: ${cell.orders} sipariş`}
                />
              )
            })}
          </Fragment>
        ))}
        {hovered?.orders ? (
          <div className={`hourly-orders-tooltip-wrap is-${tooltipSide}`} style={{ left: tooltipPos.x, top: tooltipPos.y }}>
            <HourlyHeatmapTooltip cell={hovered} />
          </div>
        ) : null}
      </div>
      <div className="hourly-orders-foot">
        <DashboardWidgetDropdown
          value={selectedPlatform.label}
          options={platformOptions.map((option) => option.label)}
          open={open}
          onOpenChange={setOpen}
          onSelect={(label) => {
            const option = platformOptions.find((item) => item.label === label)
            setPlatformValue(option?.value || hourlyDefaultPlatformOption.value)
          }}
          icon="fa-regular fa-layer-group"
        />
      </div>
    </section>
  )
}

type PaymentTypeOrderRow = {
  key: string
  label: string
  orders: number
  products: number
  total: number
  percent: number
  color: string
  paymentStatuses?: {
    paid?: number
    pending?: number
    paidTotal?: number
    pendingTotal?: number
  }
}

type PaymentTypeOrdersPayload = {
  dateLabel: string
  totalLabel: string
  rows: PaymentTypeOrderRow[]
  maxOrders: number
  platforms?: DashboardPlatformOption[]
}

type PaymentTypeBarLabelProps = {
  x?: number | string
  y?: number | string
  width?: number | string
  index?: number | string
}

const paymentTypeFallbackRows: PaymentTypeOrderRow[] = [
  { key: 'transfer', label: 'Havale / Eft', orders: 0, products: 0, total: 0, percent: 0, color: '#a9c1e8' },
  { key: 'cod', label: 'Kapıda Ödeme', orders: 0, products: 0, total: 0, percent: 0, color: '#6699ec' },
  { key: 'free_delivery', label: 'Ücretsiz Teslimat', orders: 0, products: 0, total: 0, percent: 0, color: '#d2dcf4' },
  { key: 'card', label: 'Kredi Kartı', orders: 0, products: 0, total: 0, percent: 0, color: '#1f73f2' },
]

function paymentTypeFallbackPayload(period: string): PaymentTypeOrdersPayload {
  return {
    dateLabel: dashboardDateLabelForPeriod(period),
    totalLabel: '0 Adet',
    rows: paymentTypeFallbackRows,
    maxOrders: 1,
    platforms: [hourlyDefaultPlatformOption],
  }
}

function PaymentTypeTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: { payload?: PaymentTypeOrderRow }[]
}) {
  const row = payload?.[0]?.payload
  if (!active || !row) return null
  const paidOrders = Number(row.paymentStatuses?.paid || 0)
  const pendingOrders = Number(row.paymentStatuses?.pending || 0)
  const paidTotal = Number(row.paymentStatuses?.paidTotal || 0)
  const pendingTotal = Number(row.paymentStatuses?.pendingTotal || 0)

  return (
    <div className="channel-orders-tooltip payment-type-orders-tooltip">
      <span>{row.label}</span>
      <strong>{row.orders} Sipariş</strong>
      <em>{formatChannelOrderMoney(row.total)}</em>
      {(paidOrders > 0 || pendingOrders > 0) && (
        <div className="payment-type-orders-tooltip__statuses">
          <small><span>Ödenen:</span><b>{paidOrders} / {formatChannelOrderMoney(paidTotal)}</b></small>
          <small><span>Ödeme Bekleyen:</span><b>{pendingOrders} / {formatChannelOrderMoney(pendingTotal)}</b></small>
        </div>
      )}
    </div>
  )
}

function PaymentTypeOrdersDashboard() {
  const [period, setPeriod] = useState('Bugün')
  const [platformValue, setPlatformValue] = useState(hourlyDefaultPlatformOption.value)
  const [openDropdown, setOpenDropdown] = useState<'period' | 'platform' | null>(null)
  const { ref, visible } = useDashboardInView()
  const params = useMemo(() => ({
    period: channelOrderPeriodValues[period] || 'last_7_days',
    platform: platformValue,
  }), [period, platformValue])
  const { data, loading } = useDashboardWidgetData<PaymentTypeOrdersPayload>({
    endpoint: '/panel/dashboard-widgets/orders-by-payment-type',
    params,
    enabled: visible,
  })
  const fallback = useMemo(() => paymentTypeFallbackPayload(period), [period])
  const payload = data || fallback
  const platformOptions = useMemo(() => normalizeDashboardPlatformOptions(payload.platforms), [payload.platforms])
  const selectedPlatform = platformOptions.find((option) => option.value === platformValue) || hourlyDefaultPlatformOption
  const rows = payload.rows?.length ? payload.rows : fallback.rows
  const positiveRows = rows.filter((row) => Number(row.orders || 0) > 0)
  const chartRows = positiveRows.length ? positiveRows : rows
  const yMax = Math.max(...chartRows.map((row) => Number(row.orders || 0)), 1)
  const renderBarLabel = ({ x, y, width, index }: PaymentTypeBarLabelProps) => {
    const row = chartRows[Number(index || 0)]
    if (!row) return null
    if (Number(row.orders || 0) <= 0) return null
    const centerX = Number(x || 0) + Number(width || 0) / 2
    return (
      <g className="payment-type-orders-label">
        <text x={centerX} y={Number(y || 0) - 23} textAnchor="middle">{row.label}: {row.orders}</text>
        <text x={centerX} y={Number(y || 0) - 7} textAnchor="middle" className="payment-type-orders-label__percent">{row.percent.toFixed(1)}%</text>
      </g>
    )
  }

  return (
    <section ref={ref} className={`panel-dashboard-block payment-type-orders-card ${loading ? 'is-loading' : ''}`}>
      <div className="panel-dashboard-block__head payment-type-orders-head">
        <div>
          <strong>ÖDEME TİPİNE GÖRE SİPARİŞLER</strong>
          <span>{payload.dateLabel}</span>
        </div>
        <b>{payload.totalLabel}</b>
      </div>
      <div className="payment-type-orders-dash" />
      <div className="payment-type-orders-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartRows} margin={{ top: 34, right: 18, bottom: 18, left: 18 }} barCategoryGap="12%" barGap={4}>
            <CartesianGrid vertical={false} stroke="#edf0f7" />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              interval={0}
              height={18}
              tick={{ fill: '#5963a7', fontSize: 10, fontWeight: 700 }}
            />
            <YAxis hide domain={[0, yMax]} />
            <Tooltip cursor={false} content={<PaymentTypeTooltip />} wrapperStyle={{ outline: 'none' }} />
            <Bar dataKey="orders" radius={[0, 0, 0, 0]} maxBarSize={92} minPointSize={2} isAnimationActive={false}>
              {chartRows.map((row) => <Cell key={row.key} fill={row.color} />)}
              <LabelList
                dataKey="orders"
                position="top"
                content={renderBarLabel}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="payment-type-orders-foot">
        <DashboardWidgetDropdown
          value={period}
          options={channelOrderPeriodOptions}
          open={openDropdown === 'period'}
          onOpenChange={(open) => setOpenDropdown(open ? 'period' : null)}
          onSelect={setPeriod}
          icon="fa-regular fa-calendar-days"
        />
        <DashboardWidgetDropdown
          value={selectedPlatform.label}
          options={platformOptions.map((option) => option.label)}
          open={openDropdown === 'platform'}
          onOpenChange={(open) => setOpenDropdown(open ? 'platform' : null)}
          onSelect={(label) => {
            const option = platformOptions.find((item) => item.label === label)
            setPlatformValue(option?.value || hourlyDefaultPlatformOption.value)
          }}
          icon="fa-regular fa-layer-group"
        />
      </div>
    </section>
  )
}

type CartMemberMetric = {
  key: string
  label: string
  value: number
  type: 'number' | 'money'
  description: string
}

type CartMemberStatsPayload = {
  dateLabel: string
  metrics: CartMemberMetric[]
  platforms?: DashboardPlatformOption[]
}

const cartMemberDashboardId = 65
const cartMemberDefaultMetricKeys = ['cart_count', 'products', 'abandoned_carts', 'member_sessions', 'guest_carts']
const cartMemberMetricOptions: CartMemberMetric[] = [
  { key: 'cart_count', label: 'Sepet Adedi', value: 0, type: 'number', description: 'Müşteri bazlı oluşan sepet sayısı' },
  { key: 'products', label: 'Ürünler', value: 0, type: 'number', description: 'Sepetlerde oluşan ürün adedi' },
  { key: 'abandoned_carts', label: 'Terkedilmiş Sepetler', value: 0, type: 'number', description: 'Gün içinde terkedilmiş sepet sayısı' },
  { key: 'active_cart_amount', label: 'Aktif Sepet Tutarı', value: 0, type: 'money', description: 'Aktif sepetlerin toplam tutarı' },
  { key: 'abandoned_cart_amount', label: 'Terkedilmiş Sepet Tutarı', value: 0, type: 'money', description: 'Terkedilmiş sepetlerin toplam tutarı' },
  { key: 'campaign_amount', label: 'Kampanya Tutarı', value: 0, type: 'money', description: 'Sepetlerdeki toplam kampanya/indirim tutarı' },
  { key: 'member_sessions', label: 'Üyelikli Oturumlar', value: 0, type: 'number', description: 'Gün içinde web sitesine üye olan kullanıcı sayısı' },
  { key: 'guest_carts', label: 'Misafir Sepetleri', value: 0, type: 'number', description: 'Üyeliksiz oluşturulan sepet sayısı' },
  { key: 'average_cart_amount', label: 'Sepet Ortalama Tutarı', value: 0, type: 'money', description: 'Oluşturulan sepetlerin ortalama tutarı' },
  { key: 'unique_products', label: 'Benzersiz Ürün Adedi', value: 0, type: 'number', description: 'Sepetlere eklenen benzersiz ürün sayısı' },
]
const cartMemberEmbedMetricKeys = cartMemberMetricOptions.map((metric) => metric.key)

function formatCartMemberValue(metric: CartMemberMetric) {
  return metric.type === 'money' ? formatChannelOrderMoney(metric.value) : String(metric.value)
}

function cartMemberFallbackPayload(): CartMemberStatsPayload {
  return {
    dateLabel: `${dashboardLongDate(new Date())} (Bugün Tarihli)`,
    metrics: cartMemberMetricOptions.map(zeroMetric),
  }
}

function normalizeCartMemberMetricKeys(value: unknown) {
  if (!Array.isArray(value)) return cartMemberDefaultMetricKeys
  const available = new Set(cartMemberMetricOptions.map((metric) => metric.key))
  const next = [...new Set(value.map(String).filter((key) => available.has(key)))]
  return next.length ? next : cartMemberDefaultMetricKeys
}

function CartMemberSettingsDrawer({
  visibleMetricKeys,
  onToggleMetric,
  onClose,
}: {
  visibleMetricKeys: string[]
  onToggleMetric: (key: string, checked: boolean) => void
  onClose: () => void
}) {
  return createPortal(
    <div className="cart-member-settings-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="cart-member-settings" role="dialog" aria-modal="true" aria-label="Sepet istatistikleri ayarları" onMouseDown={(event) => event.stopPropagation()}>
        <div className="cart-member-settings__head">
          <div>
            <strong>Sepet İstatistikleri</strong>
            <span>Gösterilecek alanları seçin.</span>
          </div>
          <button type="button" onClick={onClose} aria-label="Kapat">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <div className="cart-member-settings__list">
          {cartMemberMetricOptions.map((metric) => (
            <label key={metric.key} className="cart-member-settings__item">
              <input
                type="checkbox"
                checked={visibleMetricKeys.includes(metric.key)}
                onChange={(event) => onToggleMetric(metric.key, event.target.checked)}
              />
              <span>
                <strong>{metric.label}</strong>
                <small>{metric.description}</small>
              </span>
            </label>
          ))}
        </div>
      </section>
    </div>,
    document.body,
  )
}

function CartMemberStatsDashboard() {
  const embedToken = useEmbedToken()
  const isEmbed = Boolean(embedToken)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [visibleMetricKeys, setVisibleMetricKeys] = useState(() => (isEmbed ? cartMemberEmbedMetricKeys : cartMemberDefaultMetricKeys))
  const { ref, visible } = useDashboardInView()
  const params = useMemo(() => ({}), [])
  const { data, loading } = useDashboardWidgetData<CartMemberStatsPayload>({
    endpoint: '/panel/dashboard-widgets/cart-member-stats',
    params,
    enabled: visible,
  })
  const fallback = useMemo(() => cartMemberFallbackPayload(), [])
  const payload = data || fallback
  const metricByKey = useMemo(() => {
    const map = new Map<string, CartMemberMetric>()
    cartMemberMetricOptions.forEach((metric) => map.set(metric.key, metric))
    ;(payload.metrics || []).forEach((metric) => map.set(metric.key, metric))
    return map
  }, [payload.metrics])
  const rows = visibleMetricKeys
    .map((key) => metricByKey.get(key))
    .filter(Boolean) as CartMemberMetric[]

  useEffect(() => {
    if (isEmbed) {
      queueMicrotask(() => setVisibleMetricKeys(cartMemberEmbedMetricKeys))
      return
    }

    const localKey = `dashboard_widget_${cartMemberDashboardId}_visible_metrics`
    const localValue = localStorage.getItem(localKey)
    if (localValue) {
      try {
        const normalized = normalizeCartMemberMetricKeys(JSON.parse(localValue))
        queueMicrotask(() => setVisibleMetricKeys(normalized))
      } catch {
        queueMicrotask(() => setVisibleMetricKeys(cartMemberDefaultMetricKeys))
      }
    }

    const token = localStorage.getItem('auth_token')
    if (!token) return
    fetch(`${API_BASE}/auth/dashboard-preferences`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => response.json())
      .then((response) => {
        const settings = response?.data?.preferences?.widget_settings
        const saved = settings?.[String(cartMemberDashboardId)]?.visible_metrics
        if (Array.isArray(saved)) {
          const normalized = normalizeCartMemberMetricKeys(saved)
          setVisibleMetricKeys(normalized)
          localStorage.setItem(localKey, JSON.stringify(normalized))
        }
      })
      .catch(() => {})
  }, [isEmbed])

  const saveVisibleMetrics = async (nextKeys: string[]) => {
    if (isEmbed) return
    const localKey = `dashboard_widget_${cartMemberDashboardId}_visible_metrics`
    localStorage.setItem(localKey, JSON.stringify(nextKeys))
    const token = localStorage.getItem('auth_token')
    if (!token) return
    try {
      const currentResponse = await fetch(`${API_BASE}/auth/dashboard-preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const current = await currentResponse.json().catch(() => null)
      const preferences = current?.data?.preferences || {}
      const widgetSettings = preferences.widget_settings && typeof preferences.widget_settings === 'object'
        ? preferences.widget_settings
        : {}
      await fetch(`${API_BASE}/auth/dashboard-preferences`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          widget_settings: {
            ...widgetSettings,
            [String(cartMemberDashboardId)]: {
              ...(widgetSettings[String(cartMemberDashboardId)] || {}),
              visible_metrics: nextKeys,
            },
          },
        }),
      })
    } catch (error) {
      console.error('Cart member dashboard settings save failed:', error)
    }
  }

  const toggleMetric = (key: string, checked: boolean) => {
    setVisibleMetricKeys((current) => {
      const next = checked
        ? [...current, key].filter((item, index, array) => array.indexOf(item) === index)
        : current.filter((item) => item !== key)
      const normalized = normalizeCartMemberMetricKeys(next)
      void saveVisibleMetrics(normalized)
      return normalized
    })
  }

  return (
    <section ref={ref} className={`panel-dashboard-block cart-member-card ${loading ? 'is-loading' : ''}`}>
      <div className="panel-dashboard-block__head cart-member-head">
        <div>
          <strong>SEPET İSTATİSTİKLERİ</strong>
          <span>{payload.dateLabel} <em>(Bugün Tarihli)</em></span>
        </div>
        {!isEmbed && (
          <button type="button" onClick={() => setSettingsOpen(true)} aria-label="Sepet istatistikleri ayarları">
            <i className="fa-regular fa-sliders" />
          </button>
        )}
      </div>
      <div className="cart-member-dash" />
      <div className="cart-member-table">
        {rows.map((metric) => (
          <div key={metric.key} className="cart-member-row">
            <strong>{metric.label}</strong>
            <b>{formatCartMemberValue(metric)}</b>
            <span>{metric.description}</span>
          </div>
        ))}
      </div>
      {!isEmbed && settingsOpen && typeof document !== 'undefined' && (
        <CartMemberSettingsDrawer
          visibleMetricKeys={visibleMetricKeys}
          onToggleMetric={toggleMetric}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </section>
  )
}

const generalStatsDashboardId = 66
const generalStatsDefaultMetricKeys = ['available_stock', 'categories', 'members', 'payment_pending', 'outbound_requests']
const generalStatsMetricOptions: CartMemberMetric[] = [
  { key: 'available_stock', label: 'Mevcut Stok', value: 0, type: 'number', description: 'Satılabilir mevcut stok adedi' },
  { key: 'categories', label: 'Kategoriler', value: 0, type: 'number', description: 'Aktif ürünlerdeki kategori sayısı' },
  { key: 'members', label: 'Üye', value: 0, type: 'number', description: 'Aktif kayıtlı üye sayısı' },
  { key: 'product_groups', label: 'Ürün Grubu', value: 0, type: 'number', description: 'Aktif ürünlerdeki ürün grubu sayısı' },
  { key: 'payment_pending', label: 'Ödeme Beklenen', value: 0, type: 'number', description: 'Ödeme sonucu bekleyen sipariş sayısı' },
  { key: 'outbound_requests', label: 'Dış Arama Talepleri', value: 0, type: 'number', description: 'Tamamlanmamış dış arama talebi sayısı' },
]
const generalStatsEmbedMetricKeys = generalStatsMetricOptions.map((metric) => metric.key)

function generalStatsFallbackPayload(): CartMemberStatsPayload {
  return { dateLabel: '', metrics: generalStatsMetricOptions }
}

function normalizeGeneralStatsMetricKeys(value: unknown) {
  if (!Array.isArray(value)) return generalStatsDefaultMetricKeys
  const available = new Set(generalStatsMetricOptions.map((metric) => metric.key))
  const next = [...new Set(value.map(String).filter((key) => available.has(key)))]
  return next.length ? next : generalStatsDefaultMetricKeys
}

function GeneralStatsSettingsDrawer({
  visibleMetricKeys,
  onToggleMetric,
  onClose,
}: {
  visibleMetricKeys: string[]
  onToggleMetric: (key: string, checked: boolean) => void
  onClose: () => void
}) {
  return createPortal(
    <div className="cart-member-settings-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="cart-member-settings" role="dialog" aria-modal="true" aria-label="Genel istatistik ayarları" onMouseDown={(event) => event.stopPropagation()}>
        <div className="cart-member-settings__head">
          <div>
            <strong>Genel İstatistikler</strong>
            <span>Gösterilecek alanları seçin.</span>
          </div>
          <button type="button" onClick={onClose} aria-label="Kapat">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <div className="cart-member-settings__list">
          {generalStatsMetricOptions.map((metric) => (
            <label key={metric.key} className="cart-member-settings__item">
              <input
                type="checkbox"
                checked={visibleMetricKeys.includes(metric.key)}
                onChange={(event) => onToggleMetric(metric.key, event.target.checked)}
              />
              <span>
                <strong>{metric.label}</strong>
                <small>{metric.description}</small>
              </span>
            </label>
          ))}
        </div>
      </section>
    </div>,
    document.body,
  )
}

function GeneralStatsDashboard() {
  const embedToken = useEmbedToken()
  const isEmbed = Boolean(embedToken)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [visibleMetricKeys, setVisibleMetricKeys] = useState(() => (isEmbed ? generalStatsEmbedMetricKeys : generalStatsDefaultMetricKeys))
  const { ref, visible } = useDashboardInView()
  const params = useMemo(() => ({}), [])
  const { data, loading } = useDashboardWidgetData<CartMemberStatsPayload>({
    endpoint: '/panel/dashboard-widgets/general-stats',
    params,
    enabled: visible,
  })
  const fallback = useMemo(() => generalStatsFallbackPayload(), [])
  const payload = data || fallback
  const metricByKey = useMemo(() => {
    const map = new Map<string, CartMemberMetric>()
    generalStatsMetricOptions.forEach((metric) => map.set(metric.key, metric))
    ;(payload.metrics || []).forEach((metric) => map.set(metric.key, metric))
    return map
  }, [payload.metrics])
  const rows = visibleMetricKeys
    .map((key) => metricByKey.get(key))
    .filter(Boolean) as CartMemberMetric[]

  useEffect(() => {
    if (isEmbed) {
      queueMicrotask(() => setVisibleMetricKeys(generalStatsEmbedMetricKeys))
      return
    }

    const localKey = `dashboard_widget_${generalStatsDashboardId}_visible_metrics`
    const localValue = localStorage.getItem(localKey)
    if (localValue) {
      try {
        const normalized = normalizeGeneralStatsMetricKeys(JSON.parse(localValue))
        queueMicrotask(() => setVisibleMetricKeys(normalized))
      } catch {
        queueMicrotask(() => setVisibleMetricKeys(generalStatsDefaultMetricKeys))
      }
    }

    const token = localStorage.getItem('auth_token')
    if (!token) return
    fetch(`${API_BASE}/auth/dashboard-preferences`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => response.json())
      .then((response) => {
        const settings = response?.data?.preferences?.widget_settings
        const saved = settings?.[String(generalStatsDashboardId)]?.visible_metrics
        if (Array.isArray(saved)) {
          const normalized = normalizeGeneralStatsMetricKeys(saved)
          setVisibleMetricKeys(normalized)
          localStorage.setItem(localKey, JSON.stringify(normalized))
        }
      })
      .catch(() => {})
  }, [isEmbed])

  const saveVisibleMetrics = async (nextKeys: string[]) => {
    if (isEmbed) return
    const localKey = `dashboard_widget_${generalStatsDashboardId}_visible_metrics`
    localStorage.setItem(localKey, JSON.stringify(nextKeys))
    const token = localStorage.getItem('auth_token')
    if (!token) return
    try {
      const currentResponse = await fetch(`${API_BASE}/auth/dashboard-preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const current = await currentResponse.json().catch(() => null)
      const preferences = current?.data?.preferences || {}
      const widgetSettings = preferences.widget_settings && typeof preferences.widget_settings === 'object'
        ? preferences.widget_settings
        : {}
      await fetch(`${API_BASE}/auth/dashboard-preferences`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          widget_settings: {
            ...widgetSettings,
            [String(generalStatsDashboardId)]: {
              ...(widgetSettings[String(generalStatsDashboardId)] || {}),
              visible_metrics: nextKeys,
            },
          },
        }),
      })
    } catch (error) {
      console.error('General stats dashboard settings save failed:', error)
    }
  }

  const toggleMetric = (key: string, checked: boolean) => {
    setVisibleMetricKeys((current) => {
      const next = checked
        ? [...current, key].filter((item, index, array) => array.indexOf(item) === index)
        : current.filter((item) => item !== key)
      const normalized = normalizeGeneralStatsMetricKeys(next)
      void saveVisibleMetrics(normalized)
      return normalized
    })
  }

  return (
    <section ref={ref} className={`panel-dashboard-block cart-member-card general-stats-card ${loading ? 'is-loading' : ''}`}>
      <div className="panel-dashboard-block__head cart-member-head">
        <div>
          <strong>GENEL İSTATİSTİKLER</strong>
        </div>
        {!isEmbed && (
          <button type="button" onClick={() => setSettingsOpen(true)} aria-label="Genel istatistik ayarları">
            <i className="fa-regular fa-sliders" />
          </button>
        )}
      </div>
      <div className="cart-member-dash" />
      <div className="cart-member-table">
        {rows.map((metric) => (
          <div key={metric.key} className="cart-member-row">
            <strong>{metric.label}</strong>
            <b>{formatCartMemberValue(metric)}</b>
            <span>{metric.description}</span>
          </div>
        ))}
      </div>
      {!isEmbed && settingsOpen && typeof document !== 'undefined' && (
        <GeneralStatsSettingsDrawer
          visibleMetricKeys={visibleMetricKeys}
          onToggleMetric={toggleMetric}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </section>
  )
}

const orderStatusDashboardId = 67
const orderStatusDefaultMetricKeys = ['new_orders', 'addressed', 'invoiced', 'shipping', 'cancel_requests', 'return_requests']
const orderStatusMetricOptions: CartMemberMetric[] = [
  { key: 'new_orders', label: 'Onay Bekleyen', value: 0, type: 'number', description: 'Henüz paket/adresleme kaydı oluşmamış siparişler' },
  { key: 'addressed', label: 'Tedarik', value: 0, type: 'number', description: 'Fatura ve kargo öncesi operasyon bekleyen paketler' },
  { key: 'invoiced', label: 'Kargo Teslimatı', value: 0, type: 'number', description: 'Faturası oluşmuş, kargoya çıkışı bekleyen paketler' },
  { key: 'shipping', label: 'Kargoda', value: 0, type: 'number', description: 'Kargoya verilmiş paketler' },
  { key: 'cancel_requests', label: 'İptal Talebi', value: 0, type: 'number', description: 'İptal talebi bekleyen siparişler' },
  { key: 'return_requests', label: 'İade Talebi', value: 0, type: 'number', description: 'İade işaretli siparişler' },
]
const orderStatusEmbedMetricKeys = orderStatusMetricOptions.map((metric) => metric.key)

function orderStatusFallbackPayload(): CartMemberStatsPayload {
  return { dateLabel: 'Mevcut Durum', metrics: orderStatusMetricOptions }
}

function normalizeOrderStatusMetricKeys(value: unknown) {
  if (!Array.isArray(value)) return orderStatusDefaultMetricKeys
  const available = new Set(orderStatusMetricOptions.map((metric) => metric.key))
  const next = [...new Set(value.map(String).filter((key) => available.has(key)))]
  return next.length ? next : orderStatusDefaultMetricKeys
}

function OrderStatusSettingsDrawer({
  visibleMetricKeys,
  onToggleMetric,
  onClose,
}: {
  visibleMetricKeys: string[]
  onToggleMetric: (key: string, checked: boolean) => void
  onClose: () => void
}) {
  return createPortal(
    <div className="cart-member-settings-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="cart-member-settings" role="dialog" aria-modal="true" aria-label="Sipariş durum ayarları" onMouseDown={(event) => event.stopPropagation()}>
        <div className="cart-member-settings__head">
          <div>
            <strong>Sipariş Durum Özeti</strong>
            <span>Gösterilecek alanları seçin.</span>
          </div>
          <button type="button" onClick={onClose} aria-label="Kapat">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <div className="cart-member-settings__list">
          {orderStatusMetricOptions.map((metric) => (
            <label key={metric.key} className="cart-member-settings__item">
              <input
                type="checkbox"
                checked={visibleMetricKeys.includes(metric.key)}
                onChange={(event) => onToggleMetric(metric.key, event.target.checked)}
              />
              <span>
                <strong>{metric.label}</strong>
                <small>{metric.description}</small>
              </span>
            </label>
          ))}
        </div>
      </section>
    </div>,
    document.body,
  )
}

function OrderStatusDonutTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: { payload?: CartMemberMetric & { value: number } }[]
}) {
  if (!active || !payload?.length) return null
  const metric = payload[0]?.payload
  if (!metric || metric.key === 'empty') return null
  return (
    <div className="channel-orders-tooltip order-status-donut-tooltip">
      <span>{metric.label}</span>
      <strong>{formatCompact(Number(metric.value || 0))} Sipariş</strong>
    </div>
  )
}

function OrderStatusSummaryDashboard() {
  const embedToken = useEmbedToken()
  const isEmbed = Boolean(embedToken)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [platformValue, setPlatformValue] = useState(hourlyDefaultPlatformOption.value)
  const [platformOpen, setPlatformOpen] = useState(false)
  const [visibleMetricKeys, setVisibleMetricKeys] = useState(() => (isEmbed ? orderStatusEmbedMetricKeys : orderStatusDefaultMetricKeys))
  const { ref, visible } = useDashboardInView()
  const params = useMemo(() => ({ platform: platformValue }), [platformValue])
  const { data, loading } = useDashboardWidgetData<CartMemberStatsPayload>({
    endpoint: '/panel/dashboard-widgets/order-status-summary',
    params,
    enabled: visible,
  })
  const fallback = useMemo(() => orderStatusFallbackPayload(), [])
  const payload = data || fallback
  const platformOptions = useMemo(() => normalizeDashboardPlatformOptions(payload.platforms), [payload.platforms])
  const selectedPlatform = platformOptions.find((option) => option.value === platformValue) || hourlyDefaultPlatformOption
  const metricByKey = useMemo(() => {
    const map = new Map<string, CartMemberMetric>()
    orderStatusMetricOptions.forEach((metric) => map.set(metric.key, metric))
    ;(payload.metrics || []).forEach((metric) => map.set(metric.key, metric))
    return map
  }, [payload.metrics])
  const rows = visibleMetricKeys
    .map((key) => metricByKey.get(key))
    .filter(Boolean) as CartMemberMetric[]
  const totalVisibleValue = rows.reduce((total, metric) => total + Number(metric.value || 0), 0)
  const statusColorByKey: Record<string, string> = {
    new_orders: '#2360ff',
    addressed: '#5a83f2',
    invoiced: '#84a6ea',
    shipping: '#a9bee4',
    cancel_requests: '#ff6b45',
    return_requests: '#d94b6b',
  }
  const chartRows = rows.map((metric, index) => ({
    ...metric,
    value: Number(metric.value || 0),
    percent: totalVisibleValue > 0 ? (Number(metric.value || 0) / totalVisibleValue) * 100 : 0,
    fill: statusColorByKey[metric.key] || ['#2360ff', '#5a83f2', '#84a6ea', '#a9bee4'][index % 4],
  }))
  const pieRows = chartRows.some((metric) => metric.value > 0)
    ? chartRows.filter((metric) => metric.value > 0)
    : [{ key: 'empty', label: 'Veri Yok', value: 1, percent: 0, fill: '#e8ecf8', type: 'number' as const, description: '' }]

  useEffect(() => {
    if (isEmbed) {
      queueMicrotask(() => setVisibleMetricKeys(orderStatusEmbedMetricKeys))
      return
    }

    const localKey = `dashboard_widget_${orderStatusDashboardId}_visible_metrics`
    const localValue = localStorage.getItem(localKey)
    if (localValue) {
      try {
        const normalized = normalizeOrderStatusMetricKeys(JSON.parse(localValue))
        queueMicrotask(() => setVisibleMetricKeys(normalized))
      } catch {
        queueMicrotask(() => setVisibleMetricKeys(orderStatusDefaultMetricKeys))
      }
    }

    const token = localStorage.getItem('auth_token')
    if (!token) return
    fetch(`${API_BASE}/auth/dashboard-preferences`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => response.json())
      .then((response) => {
        const settings = response?.data?.preferences?.widget_settings
        const saved = settings?.[String(orderStatusDashboardId)]?.visible_metrics
        if (Array.isArray(saved)) {
          const normalized = normalizeOrderStatusMetricKeys(saved)
          setVisibleMetricKeys(normalized)
          localStorage.setItem(localKey, JSON.stringify(normalized))
        }
      })
      .catch(() => {})
  }, [isEmbed])

  const saveVisibleMetrics = async (nextKeys: string[]) => {
    if (isEmbed) return
    const localKey = `dashboard_widget_${orderStatusDashboardId}_visible_metrics`
    localStorage.setItem(localKey, JSON.stringify(nextKeys))
    const token = localStorage.getItem('auth_token')
    if (!token) return
    try {
      const currentResponse = await fetch(`${API_BASE}/auth/dashboard-preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const current = await currentResponse.json().catch(() => null)
      const preferences = current?.data?.preferences || {}
      const widgetSettings = preferences.widget_settings && typeof preferences.widget_settings === 'object'
        ? preferences.widget_settings
        : {}
      await fetch(`${API_BASE}/auth/dashboard-preferences`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          widget_settings: {
            ...widgetSettings,
            [String(orderStatusDashboardId)]: {
              ...(widgetSettings[String(orderStatusDashboardId)] || {}),
              visible_metrics: nextKeys,
            },
          },
        }),
      })
    } catch (error) {
      console.error('Order status dashboard settings save failed:', error)
    }
  }

  const toggleMetric = (key: string, checked: boolean) => {
    setVisibleMetricKeys((current) => {
      const next = checked
        ? [...current, key].filter((item, index, array) => array.indexOf(item) === index)
        : current.filter((item) => item !== key)
      const normalized = normalizeOrderStatusMetricKeys(next)
      void saveVisibleMetrics(normalized)
      return normalized
    })
  }

  return (
    <section ref={ref} className={`panel-dashboard-block cart-member-card order-status-card ${loading ? 'is-loading' : ''}`}>
      <div className="panel-dashboard-block__head cart-member-head">
        <div>
          <strong>SİPARİŞ DURUM ÖZETİ</strong>
          <span>{payload.dateLabel}</span>
        </div>
        {!isEmbed && (
          <button type="button" onClick={() => setSettingsOpen(true)} aria-label="Sipariş durum ayarları">
            <i className="fa-regular fa-sliders" />
          </button>
        )}
      </div>
      <div className="cart-member-dash" />
      <div className="order-status-single">
        <article className="order-status-option order-status-option--single">
          <div className="order-status-option__head">
            <strong>Durum Dağılımı</strong>
            <span>Dağılım: görünür tüm durumlar</span>
          </div>
          <div className="order-status-donut">
            <div className="order-status-donut-chart">
              <ResponsiveContainer width="100%" height={148}>
                <PieChart>
                  <Pie data={pieRows} dataKey="value" nameKey="label" innerRadius={42} outerRadius={62} paddingAngle={2}>
                    {pieRows.map((metric) => (
                      <Cell key={metric.key} fill={metric.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    cursor={false}
                    content={<OrderStatusDonutTooltip />}
                    wrapperStyle={{ outline: 'none' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="order-status-donut-center">
                <strong>{formatCompact(totalVisibleValue)}</strong>
                <span>Aksiyon</span>
              </div>
            </div>
            <div className="order-status-legend">
              {chartRows.map((metric) => (
                <div key={metric.key}>
                  <span style={{ background: metric.fill }} />
                  <strong>{metric.label}</strong>
                  <b>{formatCompact(metric.value)}</b>
                </div>
              ))}
            </div>
          </div>
        </article>
      </div>
      <div className="channel-orders-foot order-status-foot">
        <DashboardWidgetDropdown
          value={selectedPlatform.label}
          options={platformOptions.map((option) => option.label)}
          open={platformOpen}
          onOpenChange={setPlatformOpen}
          onSelect={(label) => {
            const option = platformOptions.find((item) => item.label === label)
            setPlatformValue(option?.value || hourlyDefaultPlatformOption.value)
          }}
          icon="fa-regular fa-layer-group"
        />
      </div>
      {!isEmbed && settingsOpen && typeof document !== 'undefined' && (
        <OrderStatusSettingsDrawer
          visibleMetricKeys={visibleMetricKeys}
          onToggleMetric={toggleMetric}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </section>
  )
}

type CargoStatusMetric = {
  key: string
  label: string
  value: number
  color: string
}

type CargoStatusTrendPoint = {
  label: string
  value: number
}

type CargoStatusReportPayload = {
  dateLabel: string
  total: number
  metrics: CargoStatusMetric[]
  trend: CargoStatusTrendPoint[]
  platforms?: DashboardPlatformOption[]
}

const cargoStatusFallbackPayload = (): CargoStatusReportPayload => ({
  dateLabel: 'Anlık Durum',
  total: 0,
  metrics: [
    { key: 'in_transit', label: 'Kargoda', value: 0, color: '#2360ff' },
    { key: 'delivered', label: 'Teslim Edildi', value: 0, color: '#5a83f2' },
    { key: 'undelivered', label: 'Teslim Edilemedi', value: 0, color: '#ff6b45' },
    { key: 'delayed', label: 'Geciken', value: 0, color: '#d94b6b' },
  ],
  trend: channelOrderPoints([0, 0, 0, 0, 0, 0, 0]).map((point) => ({ label: point.label, value: point.value })),
  platforms: [hourlyDefaultPlatformOption],
})

const cargoStatusPeriodOptions = ['Bugün', 'Son 7 Gün']
const cargoStatusPeriodValues: Record<string, string> = {
  'Bugün': 'today',
  'Son 7 Gün': 'last_7_days',
}

function CargoStatusTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: { payload?: CargoStatusMetric }[]
}) {
  if (!active || !payload?.length) return null
  const metric = payload[0]?.payload
  if (!metric || metric.key === 'empty') return null
  return (
    <div className="channel-orders-tooltip order-status-donut-tooltip">
      <span>{metric.label}</span>
      <strong>{formatCompact(Number(metric.value || 0))} Gönderi</strong>
    </div>
  )
}

function CargoTrendTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: { value?: number | string }[]
  label?: string | number
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="channel-orders-tooltip sales-report-tooltip">
      <span>{label}</span>
      <strong>{formatCompact(Number(payload[0]?.value || 0))} Gönderi</strong>
    </div>
  )
}

function CargoStatusReportDashboard() {
  const [period, setPeriod] = useState('Son 7 Gün')
  const [platformValue, setPlatformValue] = useState(hourlyDefaultPlatformOption.value)
  const [openDropdown, setOpenDropdown] = useState<'period' | 'platform' | null>(null)
  const { ref, visible } = useDashboardInView()
  const params = useMemo(() => ({
    period: cargoStatusPeriodValues[period] || 'last_7_days',
    platform: platformValue,
  }), [period, platformValue])
  const { data, loading } = useDashboardWidgetData<CargoStatusReportPayload>({
    endpoint: '/panel/dashboard-widgets/cargo-status-report',
    params,
    enabled: visible,
  })
  const payload = data || cargoStatusFallbackPayload()
  const platformOptions = useMemo(() => normalizeDashboardPlatformOptions(payload.platforms), [payload.platforms])
  const selectedPlatform = platformOptions.find((option) => option.value === platformValue) || hourlyDefaultPlatformOption
  const metrics = Array.isArray(payload.metrics) && payload.metrics.length ? payload.metrics : cargoStatusFallbackPayload().metrics
  const total = Number(payload.total || metrics.reduce((sum, metric) => sum + Number(metric.value || 0), 0))
  const pieRows = metrics.some((metric) => Number(metric.value || 0) > 0)
    ? metrics.filter((metric) => Number(metric.value || 0) > 0)
    : [{ key: 'empty', label: 'Veri Yok', value: 1, color: '#e8ecf8' }]
  const trend = Array.isArray(payload.trend) && payload.trend.length ? payload.trend : cargoStatusFallbackPayload().trend

  return (
    <section ref={ref} className={`panel-dashboard-block cart-member-card cargo-status-card ${loading ? 'is-loading' : ''}`}>
      <div className="panel-dashboard-block__head cart-member-head">
        <div>
          <strong>KARGO DURUM RAPORU</strong>
          <span>{payload.dateLabel || 'Anlık Durum'}</span>
        </div>
      </div>
      <div className="cart-member-dash" />
      <div className="cargo-status-body">
        <div className="cargo-status-main">
          <div className="order-status-donut-chart cargo-status-donut-chart">
            <ResponsiveContainer width="100%" height={142}>
              <PieChart>
                <Pie data={pieRows} dataKey="value" nameKey="label" innerRadius={40} outerRadius={60} paddingAngle={2}>
                  {pieRows.map((metric) => (
                    <Cell key={metric.key} fill={metric.color} />
                  ))}
                </Pie>
                <Tooltip cursor={false} content={<CargoStatusTooltip />} wrapperStyle={{ outline: 'none' }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="order-status-donut-center">
              <strong>{formatCompact(total)}</strong>
              <span>Gönderi</span>
            </div>
          </div>
          <div className="order-status-legend cargo-status-legend">
            {metrics.map((metric) => (
              <div key={metric.key}>
                <span style={{ background: metric.color }} />
                <strong>{metric.label}</strong>
                <b>{formatCompact(Number(metric.value || 0))}</b>
              </div>
            ))}
          </div>
        </div>
        <div className="cargo-status-trend">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend} margin={{ top: 6, right: 6, bottom: 2, left: 6 }}>
              <XAxis dataKey="label" hide />
              <YAxis hide domain={['dataMin', 'dataMax']} />
              <Tooltip cursor={{ stroke: '#c7d1f5', strokeDasharray: '3 3', strokeWidth: 1 }} content={<CargoTrendTooltip />} wrapperStyle={{ outline: 'none' }} />
              <Line type="monotone" dataKey="value" stroke="#4966ff" strokeWidth={1.8} dot={false} activeDot={{ r: 4, fill: '#4966ff', stroke: '#fff', strokeWidth: 1.5 }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="channel-orders-foot cargo-status-foot">
        <DashboardWidgetDropdown
          value={period}
          options={cargoStatusPeriodOptions}
          open={openDropdown === 'period'}
          onOpenChange={(open) => setOpenDropdown(open ? 'period' : null)}
          onSelect={setPeriod}
          icon="fa-regular fa-calendar-days"
        />
        <DashboardWidgetDropdown
          value={selectedPlatform.label}
          options={platformOptions.map((option) => option.label)}
          open={openDropdown === 'platform'}
          onOpenChange={(open) => setOpenDropdown(open ? 'platform' : null)}
          onSelect={(label) => {
            const option = platformOptions.find((item) => item.label === label)
            setPlatformValue(option?.value || hourlyDefaultPlatformOption.value)
          }}
          icon="fa-regular fa-layer-group"
        />
      </div>
    </section>
  )
}

type CargoCompanyPerformanceRow = {
  cargoId: number
  name: string
  total: number
  delivered: number
  pending: number
  undelivered: number
  successRate: number
}

type CargoCompanyPerformancePayload = {
  dateLabel: string
  rows: CargoCompanyPerformanceRow[]
}

function cargoCompanyFallbackPayload(): CargoCompanyPerformancePayload {
  return {
    dateLabel: 'Son 7 Gün',
    rows: [],
  }
}

function CargoCompanyPerformanceDashboard() {
  const [hoveredRow, setHoveredRow] = useState<(CargoCompanyPerformanceRow & { shareRate: number }) | null>(null)
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 })
  const [tooltipSide, setTooltipSide] = useState<'right' | 'left'>('right')
  const { ref, visible } = useDashboardInView()
  const params = useMemo(() => ({
    period: 'last_7_days',
  }), [])
  const { data, loading } = useDashboardWidgetData<CargoCompanyPerformancePayload>({
    endpoint: '/panel/dashboard-widgets/cargo-company-performance',
    params,
    enabled: visible,
  })
  const payload = data || cargoCompanyFallbackPayload()
  const rows = Array.isArray(payload.rows) ? payload.rows : []
  const maxTotal = Math.max(1, ...rows.map((item) => Number(item.total || 0)))
  const grandTotal = rows.reduce((sum, row) => sum + Number(row.total || 0), 0)

  return (
    <section ref={ref} className={`panel-dashboard-block cart-member-card cargo-company-card ${loading ? 'is-loading' : ''}`}>
      <div className="panel-dashboard-block__head cart-member-head">
        <div>
          <strong>KARGO FİRMASI PERFORMANSI</strong>
          <span>{payload.dateLabel || 'Son 7 Gün'}</span>
        </div>
      </div>
      <div className="cart-member-dash" />
      <div className="cargo-company-body">
        <div className="cargo-company-legend">
          <span><i className="is-delivered" />Teslim</span>
          <span><i className="is-pending" />Beklemede</span>
          <span><i className="is-undelivered" />Edilemedi</span>
        </div>
        <div className="cargo-company-bars" onMouseLeave={() => setHoveredRow(null)}>
          {rows.map((row) => {
            const width = Math.max(4, (Number(row.total || 0) / maxTotal) * 100)
            const successRate = Number(row.successRate || 0)
            const shareRate = grandTotal > 0 ? (Number(row.total || 0) / grandTotal) * 100 : 0
            const deliveredRatio = row.total > 0 ? (row.delivered / row.total) * 100 : 0
            const pendingRatio = row.total > 0 ? (row.pending / row.total) * 100 : 0
            const undeliveredRatio = row.total > 0 ? (row.undelivered / row.total) * 100 : 0
            return (
              <div key={`${row.cargoId}-${row.name}`} className="cargo-company-row">
                <strong>{row.name}</strong>
                <span className="cargo-company-share">%{shareRate.toFixed(1).replace('.', ',')}</span>
                <div
                  className="cargo-company-track"
                  onMouseMove={(event) => {
                    const bounds = event.currentTarget.closest('.cargo-company-bars')?.getBoundingClientRect()
                    setHoveredRow({ ...row, shareRate })
                    if (bounds) {
                      const localX = event.clientX - bounds.left
                      const localY = event.clientY - bounds.top
                      const tooltipWidth = 124
                      const tooltipHeight = 92
                      const rightSpace = bounds.width - localX
                      const shouldOpenLeft = rightSpace < tooltipWidth + 18 && localX > rightSpace
                      const x = shouldOpenLeft
                        ? Math.max(tooltipWidth + 12, localX - 12)
                        : Math.max(12, Math.min(bounds.width - tooltipWidth - 12, localX + 12))
                      const y = Math.max(tooltipHeight + 12, Math.min(bounds.height - 4, localY))
                      setTooltipSide(shouldOpenLeft ? 'left' : 'right')
                      setTooltipPos({ x, y })
                    }
                  }}
                >
                  <div className="cargo-company-stack" style={{ width: `${width}%` }}>
                    {row.delivered > 0 && <span className="is-delivered" style={{ width: `${deliveredRatio}%` }} />}
                    {row.pending > 0 && <span className="is-pending" style={{ width: `${pendingRatio}%` }} />}
                    {row.undelivered > 0 && <span className="is-undelivered" style={{ width: `${undeliveredRatio}%` }} />}
                  </div>
                </div>
                <b>{formatCompact(row.total)}</b>
                <small>%{successRate.toFixed(1).replace('.', ',')}</small>
              </div>
            )
          })}
          {hoveredRow ? (
            <div className={`cargo-company-tooltip-wrap is-${tooltipSide}`} style={{ left: tooltipPos.x, top: tooltipPos.y }}>
              <div className="channel-orders-tooltip cargo-company-tooltip">
                <span>{hoveredRow.name} · %{hoveredRow.shareRate.toFixed(1).replace('.', ',')} pay</span>
                <strong>{formatCompact(hoveredRow.total)} Gönderi</strong>
                <em>Teslim: {formatCompact(hoveredRow.delivered)}</em>
                <em>Beklemede: {formatCompact(hoveredRow.pending)}</em>
                <em>Edilemedi: {formatCompact(hoveredRow.undelivered)}</em>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}

type OperationFlowBucket = {
  key: string
  label: string
  orders: number
  products: number
}

type OperationFlowQueue = {
  key: string
  label: string
  color: string
  orders: number
  products: number
  oldestDate?: string
  oldestAgeDays?: number
  buckets: OperationFlowBucket[]
}

type OperationFlowPoint = {
  date: string
  label: string
  orders: number
  products: number
}

type OperationFlowCompletedProcess = {
  key: string
  label: string
  orders: number
  products: number
  todayOrders: number
  todayProducts: number
  points: OperationFlowPoint[]
}

type OperationFlowTone = 'blue' | 'green' | 'orange' | 'red' | 'purple'

type OperationFlowMetric = {
  key: string
  label: string
  value?: number
  orders?: number
  products?: number
  tone?: OperationFlowTone
  description?: string
}

type OperationFlowPayload = {
  dateLabel: string
  scope?: 'store' | 'firm'
  scopeLabel?: string
  updatedAt?: string
  totals?: {
    pendingOrders?: number
    pendingProducts?: number
  }
  pendingQueues: OperationFlowQueue[]
  completed: {
    dateLabel: string
    processes: OperationFlowCompletedProcess[]
    points: OperationFlowPoint[]
  }
  riskCards: OperationFlowMetric[]
  integration: OperationFlowMetric[]
  alerts: OperationFlowMetric[]
}

const operationFlowSectionOptions = [
  { key: 'pending', label: 'Bekleyen işler', description: 'Tedarik, fatura ve kargo açık kuyrukları' },
  { key: 'completed', label: 'Tamamlanan işler', description: 'Son 7 gün biten tedarik, fatura ve kargo akışı' },
  { key: 'alerts', label: 'Uyarılar', description: 'Eski panel hızlı kontrol sayımları' },
]
const operationFlowDefaultSections = operationFlowSectionOptions.map((item) => item.key)
const operationFlowScopeOptions = [
  { key: 'store', label: 'Seçili mağaza' },
  { key: 'firm', label: 'Firma geneli' },
] as const

function operationFlowFallbackPayload(): OperationFlowPayload {
  return {
    dateLabel: 'Mevcut Durum',
    scope: 'store',
    scopeLabel: 'Seçili mağaza',
    totals: { pendingOrders: 0, pendingProducts: 0 },
    pendingQueues: [
      { key: 'supply', label: 'Tedarik', color: '#2360ff', orders: 0, products: 0, buckets: [] },
      { key: 'invoice', label: 'Fatura', color: '#16a085', orders: 0, products: 0, buckets: [] },
      { key: 'cargo', label: 'Kargo Teslimatı', color: '#f59e0b', orders: 0, products: 0, buckets: [] },
    ],
    completed: { dateLabel: 'Son 7 Gün', processes: [], points: [] },
    riskCards: [],
    integration: [],
    alerts: [],
  }
}

function normalizeOperationFlowSections(value: unknown) {
  if (!Array.isArray(value)) return operationFlowDefaultSections
  const available = new Set(operationFlowSectionOptions.map((item) => item.key))
  const next = [...new Set(value.map(String).filter((key) => available.has(key)))]
  return next.length ? next : operationFlowDefaultSections
}

function normalizeOperationFlowScope(value: unknown): 'store' | 'firm' {
  return String(value || '').toLowerCase() === 'firm' ? 'firm' : 'store'
}

function OperationFlowSettingsDrawer({
  visibleSections,
  scope,
  onToggleSection,
  onScopeChange,
  onClose,
}: {
  visibleSections: string[]
  scope: 'store' | 'firm'
  onToggleSection: (key: string, checked: boolean) => void
  onScopeChange: (scope: 'store' | 'firm') => void
  onClose: () => void
}) {
  return createPortal(
    <div className="cart-member-settings-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="cart-member-settings operation-flow-settings" role="dialog" aria-modal="true" aria-label="Operasyon akışı ayarları" onMouseDown={(event) => event.stopPropagation()}>
        <div className="cart-member-settings__head">
          <div>
            <strong>Operasyon Akışı</strong>
            <span>Gösterilecek alanları seçin.</span>
          </div>
          <button type="button" onClick={onClose} aria-label="Kapat">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <div className="operation-flow-settings__scope">
          {operationFlowScopeOptions.map((item) => (
            <label key={item.key}>
              <input
                type="radio"
                name="operation-flow-scope"
                checked={scope === item.key}
                onChange={() => onScopeChange(item.key)}
              />
              <span>{item.label}</span>
            </label>
          ))}
        </div>
        <div className="cart-member-settings__list">
          {operationFlowSectionOptions.map((section) => (
            <label key={section.key} className="cart-member-settings__item">
              <input
                type="checkbox"
                checked={visibleSections.includes(section.key)}
                onChange={(event) => onToggleSection(section.key, event.target.checked)}
              />
              <span>
                <strong>{section.label}</strong>
                <small>{section.description}</small>
              </span>
            </label>
          ))}
        </div>
      </section>
    </div>,
    document.body,
  )
}

function OperationFlowTrendTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: { value?: number | string; name?: string }[]
  label?: string | number
}) {
  if (!active || !payload?.length) return null
  const orders = Number(payload.find((item) => item.name === 'orders')?.value || payload[0]?.value || 0)
  const products = Number(payload.find((item) => item.name === 'products')?.value || 0)
  return (
    <div className="channel-orders-tooltip operation-flow-tooltip">
      <span>{label}</span>
      <strong>{formatCompact(orders)} Sipariş</strong>
      <em>{formatCompact(products)} Ürün</em>
    </div>
  )
}

function OperationFlowDashboard() {
  const embedToken = useEmbedToken()
  const isEmbed = Boolean(embedToken)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [visibleSections, setVisibleSections] = useState(operationFlowDefaultSections)
  const [scope, setScope] = useState<'store' | 'firm'>('store')
  const { ref, visible } = useDashboardInView()
  const params = useMemo(() => ({
    scope,
    sections: visibleSections.join(','),
  }), [scope, visibleSections])
  const { data, loading } = useDashboardWidgetData<OperationFlowPayload>({
    endpoint: '/panel/dashboard-widgets/operation-flow',
    params,
    enabled: visible,
  })
  const fallback = useMemo(() => operationFlowFallbackPayload(), [])
  const payload = data || fallback
  const pendingQueues = Array.isArray(payload.pendingQueues) ? payload.pendingQueues : []
  const completedProcesses = Array.isArray(payload.completed?.processes) ? payload.completed.processes : []
  const completedPoints = Array.isArray(payload.completed?.points) ? payload.completed.points : []
  const alerts = Array.isArray(payload.alerts) ? payload.alerts : []
  const totalPendingOrders = Number(payload.totals?.pendingOrders ?? pendingQueues.reduce((sum, item) => sum + Number(item.orders || 0), 0))
  const totalPendingProducts = Number(payload.totals?.pendingProducts ?? pendingQueues.reduce((sum, item) => sum + Number(item.products || 0), 0))
  const activeAlertCount = alerts.filter((item) => Number(item.value || 0) > 0).length

  useEffect(() => {
    if (isEmbed) return
    const localKey = `dashboard_widget_${operationFlowDashboardId}_settings`
    const localValue = localStorage.getItem(localKey)
    if (localValue) {
      try {
        const parsed = JSON.parse(localValue)
        queueMicrotask(() => {
          setVisibleSections(normalizeOperationFlowSections(parsed?.visible_sections))
          setScope(normalizeOperationFlowScope(parsed?.scope))
        })
      } catch {
        queueMicrotask(() => {
          setVisibleSections(operationFlowDefaultSections)
          setScope('store')
        })
      }
    }

    const token = localStorage.getItem('auth_token')
    if (!token) return
    fetch(`${API_BASE}/auth/dashboard-preferences`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => response.json())
      .then((response) => {
        const settings = response?.data?.preferences?.widget_settings?.[String(operationFlowDashboardId)]
        if (settings && typeof settings === 'object') {
          const next = {
            visible_sections: normalizeOperationFlowSections(settings.visible_sections),
            scope: normalizeOperationFlowScope(settings.scope),
          }
          setVisibleSections(next.visible_sections)
          setScope(next.scope)
          localStorage.setItem(localKey, JSON.stringify(next))
        }
      })
      .catch(() => {})
  }, [isEmbed])

  const saveSettings = async (nextSections: string[], nextScope: 'store' | 'firm') => {
    if (isEmbed) return
    const normalized = {
      visible_sections: normalizeOperationFlowSections(nextSections),
      scope: normalizeOperationFlowScope(nextScope),
    }
    const localKey = `dashboard_widget_${operationFlowDashboardId}_settings`
    localStorage.setItem(localKey, JSON.stringify(normalized))
    const token = localStorage.getItem('auth_token')
    if (!token) return
    try {
      const currentResponse = await fetch(`${API_BASE}/auth/dashboard-preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const current = await currentResponse.json().catch(() => null)
      const preferences = current?.data?.preferences || {}
      const widgetSettings = preferences.widget_settings && typeof preferences.widget_settings === 'object'
        ? preferences.widget_settings
        : {}
      await fetch(`${API_BASE}/auth/dashboard-preferences`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          widget_settings: {
            ...widgetSettings,
            [String(operationFlowDashboardId)]: {
              ...(widgetSettings[String(operationFlowDashboardId)] || {}),
              ...normalized,
            },
          },
        }),
      })
    } catch (error) {
      console.error('Operation flow dashboard settings save failed:', error)
    }
  }

  const toggleSection = (key: string, checked: boolean) => {
    setVisibleSections((current) => {
      const next = checked
        ? [...current, key].filter((item, index, array) => array.indexOf(item) === index)
        : current.filter((item) => item !== key)
      const normalized = normalizeOperationFlowSections(next)
      void saveSettings(normalized, scope)
      return normalized
    })
  }

  const changeScope = (nextScope: 'store' | 'firm') => {
    setScope(nextScope)
    void saveSettings(visibleSections, nextScope)
  }

  return (
    <section ref={ref} className={`panel-dashboard-block cart-member-card operation-flow-card ${loading ? 'is-loading' : ''}`}>
      <div className="panel-dashboard-block__head cart-member-head">
        <div>
          <strong>OPERASYON AKIŞI</strong>
          <span>{payload.dateLabel || 'Mevcut Durum'} · {payload.scopeLabel || 'Seçili mağaza'}</span>
        </div>
        {!isEmbed && (
          <button type="button" onClick={() => setSettingsOpen(true)} aria-label="Operasyon akışı ayarları">
            <i className="fa-regular fa-sliders" />
          </button>
        )}
      </div>
      <div className="cart-member-dash" />
      <div className="operation-flow-body">
        {visibleSections.includes('pending') && pendingQueues.length > 0 && (
          <div className="operation-flow-summary">
            <div className="operation-flow-summary__main">
              <span>Mevcut iş yükü</span>
              <strong>{formatCompact(totalPendingOrders)}</strong>
              <small>{formatCompact(totalPendingProducts)} ürün · {activeAlertCount} aktif başlık</small>
            </div>
            <div className="operation-flow-summary__pills">
              {pendingQueues.map((queue) => {
                const share = totalPendingOrders > 0 ? Math.round((Number(queue.orders || 0) / totalPendingOrders) * 100) : 0
                return (
                  <div key={queue.key} className="operation-flow-summary__pill" style={{ '--operation-flow-accent': queue.color || '#2360ff' } as CSSProperties}>
                    <span><i />{queue.label}</span>
                    <strong>{formatCompact(Number(queue.orders || 0))}</strong>
                    <small>%{share} pay</small>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {visibleSections.includes('pending') && (
          <section className="operation-flow-section operation-flow-section--pending">
            <div className="operation-flow-section__head">
              <strong>Bekleyen işler</strong>
              <span>{formatCompact(totalPendingOrders)} sipariş · {formatCompact(totalPendingProducts)} ürün</span>
            </div>
            <div className="operation-flow-queues">
              {pendingQueues.map((queue) => {
                const maxBucket = Math.max(1, ...((queue.buckets || []).map((bucket) => Number(bucket.orders || 0))))
                return (
                  <article key={queue.key} className="operation-flow-queue" style={{ '--operation-flow-accent': queue.color || '#2360ff' } as CSSProperties}>
                    <div className="operation-flow-queue__top">
                      <span />
                      <strong>{queue.label}</strong>
                      <b>{formatCompact(Number(queue.orders || 0))}</b>
                    </div>
                    <div className="operation-flow-queue__meta">
                      <span>{formatCompact(Number(queue.products || 0))} ürün</span>
                      <em>{queue.oldestDate ? `${queue.oldestAgeDays || 0} gün` : 'Tarihsiz yok'}</em>
                    </div>
                    <div className="operation-flow-aging">
                      {(queue.buckets || []).map((bucket) => (
                        <div key={bucket.key}>
                          <small>{bucket.label}</small>
                          <span><i style={{ width: `${Math.max(4, (Number(bucket.orders || 0) / maxBucket) * 100)}%` }} /></span>
                          <b>{formatCompact(Number(bucket.orders || 0))}</b>
                        </div>
                      ))}
                    </div>
                  </article>
                )
              })}
            </div>
          </section>
        )}

        {visibleSections.includes('completed') && (
          <section className="operation-flow-section operation-flow-section--completed">
            <div className="operation-flow-section__head">
              <strong>Tamamlanan akış</strong>
              <span>{payload.completed?.dateLabel || 'Son 7 Gün'}</span>
            </div>
            <div className="operation-flow-completed">
              <div className="operation-flow-completed__chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={completedPoints} margin={{ top: 8, right: 10, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke="#edf1fa" vertical={false} />
                    <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#8791bc' }} />
                    <YAxis hide domain={['dataMin', 'dataMax']} />
                    <Tooltip cursor={{ stroke: '#c7d1f5', strokeDasharray: '3 3', strokeWidth: 1 }} content={<OperationFlowTrendTooltip />} wrapperStyle={{ outline: 'none' }} />
                    <Line name="orders" type="monotone" dataKey="orders" stroke="#2360ff" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: '#2360ff', stroke: '#fff', strokeWidth: 1.5 }} isAnimationActive={false} />
                    <Line name="products" type="monotone" dataKey="products" stroke="#16a085" strokeWidth={1.6} dot={false} activeDot={{ r: 3, fill: '#16a085', stroke: '#fff', strokeWidth: 1.5 }} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="operation-flow-completed__list">
                {completedProcesses.map((item) => (
                  <div key={item.key}>
                    <strong>{item.label}</strong>
                    <b>{formatCompact(Number(item.orders || 0))}</b>
                    <span>{formatCompact(Number(item.products || 0))} ürün</span>
                    <em>Bugün {formatCompact(Number(item.todayOrders || 0))}</em>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {visibleSections.includes('alerts') && (
          <section className="operation-flow-section operation-flow-section--alerts">
            <div className="operation-flow-section__head">
              <strong>Uyarılar</strong>
              <span>{activeAlertCount} aktif başlık</span>
            </div>
            <div className="operation-flow-alerts">
              {alerts.map((item) => (
                <div key={item.key} className={`operation-flow-alert is-${item.tone || 'blue'}`}>
                  <span>{item.label}</span>
                  <b>{formatCompact(Number(item.value || 0))}</b>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
      {!isEmbed && settingsOpen && typeof document !== 'undefined' && (
        <OperationFlowSettingsDrawer
          visibleSections={visibleSections}
          scope={scope}
          onToggleSection={toggleSection}
          onScopeChange={changeScope}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </section>
  )
}

type OutboundFlowTypeMetric = {
  key: string
  legacyType: number
  label: string
  color: string
  total: number
  pending: number
  processing: number
  completed: number
  cancelled: number
  completedPeriod: number
  completedToday: number
  attemptedToday: number
}

const outboundFlowListTypeByKey: Record<string, string> = {
  cod_approval: 'delivery_door_success',
  missing_product: 'missing_product',
  iban_request: 'iban_request',
  return_exchange: 'return_exchange',
  duplicate: 'order_duplicate_review',
}

const outboundFlowLegacyPanelPathByType: Record<number, string> = {
  1: '/Page/Customer_Service/OutBand/?TYPE=1&STATUS=0',
  2: '/Page/Customer_Service/OutBand/?TYPE=2&STATUS=0',
  3: '/Page/Customer_Service/OutBand/?TYPE=3&STATUS=0',
  5: '/Page/Customer_Service/OutBand/?TYPE=5&STATUS=0',
  8: '/Page/Customer_Service/OutBand/?TYPE=8&STATUS=0',
}

type OutboundFlowPoint = {
  key: string
  label: string
  total: number
  [key: string]: string | number
}

type OutboundFlowPersonnel = {
  userId: string
  userName: string
  completed: number
  completedToday: number
  lastCompletedAt?: string | null
}

type OutboundCustomerServicePayload = {
  dateLabel: string
  period: string
  updatedAt?: string
  legacyPanel?: boolean
  totals: {
    total: number
    pending: number
    processing: number
    completed: number
    completedPeriod: number
    completedToday: number
    attemptedToday: number
  }
  types: OutboundFlowTypeMetric[]
  trend: {
    granularity: 'hour' | 'day'
    points: OutboundFlowPoint[]
  }
  personnel: OutboundFlowPersonnel[]
}

const outboundFlowPeriodOptions = ['Günlük', 'Haftalık', 'Aylık']
const outboundFlowPeriodValues: Record<string, string> = {
  'Günlük': 'daily',
  'Haftalık': 'weekly',
  'Aylık': 'monthly',
}

const outboundFlowFallbackTypes: OutboundFlowTypeMetric[] = [
  { key: 'cod_approval', legacyType: 1, label: 'Kapıda ödeme onay', color: '#2360ff', total: 0, pending: 0, processing: 0, completed: 0, cancelled: 0, completedPeriod: 0, completedToday: 0, attemptedToday: 0 },
  { key: 'missing_product', legacyType: 2, label: 'Eksik ürün', color: '#f59e0b', total: 0, pending: 0, processing: 0, completed: 0, cancelled: 0, completedPeriod: 0, completedToday: 0, attemptedToday: 0 },
  { key: 'iban_request', legacyType: 3, label: 'IBAN talep', color: '#16a085', total: 0, pending: 0, processing: 0, completed: 0, cancelled: 0, completedPeriod: 0, completedToday: 0, attemptedToday: 0 },
  { key: 'return_exchange', legacyType: 5, label: 'İade & değişim', color: '#ef476f', total: 0, pending: 0, processing: 0, completed: 0, cancelled: 0, completedPeriod: 0, completedToday: 0, attemptedToday: 0 },
  { key: 'duplicate', legacyType: 8, label: 'Mükerrer kayıt', color: '#8b5cf6', total: 0, pending: 0, processing: 0, completed: 0, cancelled: 0, completedPeriod: 0, completedToday: 0, attemptedToday: 0 },
]

function outboundFlowFallbackPayload(period: string): OutboundCustomerServicePayload {
  return {
    dateLabel: period,
    period: outboundFlowPeriodValues[period] || 'weekly',
    legacyPanel: false,
    totals: { total: 0, pending: 0, processing: 0, completed: 0, completedPeriod: 0, completedToday: 0, attemptedToday: 0 },
    types: outboundFlowFallbackTypes,
    trend: { granularity: 'day', points: [] },
    personnel: [],
  }
}

function outboundFlowCallsPath(item: OutboundFlowTypeMetric) {
  const params = new URLSearchParams({ tab: 'pending' })
  const type = outboundFlowListTypeByKey[item.key]
  if (type) params.set('type', type)
  return `/pages/customer_service/outbound?${params.toString()}`
}

function outboundFlowLegacyPanelPath(item: OutboundFlowTypeMetric) {
  return outboundFlowLegacyPanelPathByType[Number(item.legacyType || 0)] || '/Page/Customer_Service/OutBand/?STATUS=0'
}

const legacyPanelFallbackOrigin = 'https://panel.farktor.com'

function dashboardFrameHostOrigin() {
  if (typeof window === 'undefined') return legacyPanelFallbackOrigin

  const ancestorOrigins = window.location.ancestorOrigins
  if (ancestorOrigins?.length) {
    const origin = ancestorOrigins[0]
    if (origin) return origin
  }

  try {
    if (document.referrer) {
      const origin = new URL(document.referrer, window.location.href).origin
      if (origin && origin !== 'null') return origin
    }
  } catch {}

  return legacyPanelFallbackOrigin
}

function dashboardFrameExitUrl(path: string) {
  if (typeof window === 'undefined') return path
  try {
    const trimmed = path.trim()
    if (/^[a-z][a-z\d+\-.]*:/i.test(trimmed)) return trimmed
    return new URL(trimmed, dashboardFrameHostOrigin()).toString()
  } catch {
    return `${legacyPanelFallbackOrigin}${path.startsWith('/') ? path : `/${path}`}`
  }
}

function navigateOutsideDashboardFrame(path: string) {
  if (typeof window === 'undefined') return
  const targetUrl = dashboardFrameExitUrl(path)
  try {
    if (window.top && window.top !== window.self) {
      window.top.location.href = targetUrl
      return
    }
  } catch {}
  window.open(targetUrl, '_top')
}

function OutboundFlowTrendTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: { value?: number | string; name?: string; color?: string }[]
  label?: string | number
}) {
  if (!active || !payload?.length) return null
  const positiveItems = payload
    .filter((item) => Number(item.value || 0) > 0)
    .slice(0, 6)
  return (
    <div className="channel-orders-tooltip outbound-flow-tooltip">
      <span>{label}</span>
      {positiveItems.length ? positiveItems.map((item) => (
        <em key={item.name || String(item.value)}>
          {item.name || 'Arama'}: {formatCompact(Number(item.value || 0))}
        </em>
      )) : <em>Kayıt yok</em>}
    </div>
  )
}

function OutboundCustomerServiceDashboard() {
  const router = useRouter()
  const embedToken = useEmbedToken()
  const isEmbed = Boolean(embedToken)
  const [period, setPeriod] = useState('Haftalık')
  const { ref, visible } = useDashboardInView()
  const params = useMemo(() => ({
    period: outboundFlowPeriodValues[period] || 'weekly',
  }), [period])
  const { data, loading } = useDashboardWidgetData<OutboundCustomerServicePayload>({
    endpoint: '/panel/dashboard-widgets/outbound-customer-service',
    params,
    enabled: visible,
  })
  const fallback = useMemo(() => outboundFlowFallbackPayload(period), [period])
  const payload = data || fallback
  const types = Array.isArray(payload.types) && payload.types.length ? payload.types : fallback.types
  const points = Array.isArray(payload.trend?.points) ? payload.trend.points : []
  const personnel = Array.isArray(payload.personnel) ? payload.personnel : []
  const maxPending = Math.max(1, ...types.map((item) => Number(item.pending || 0)))
  const isLegacyPanelPayload = payload.legacyPanel === true
  const openOutboundCalls = (item: OutboundFlowTypeMetric) => {
    const path = isLegacyPanelPayload && isEmbed
      ? outboundFlowLegacyPanelPath(item)
      : outboundFlowCallsPath(item)
    if (isEmbed) {
      navigateOutsideDashboardFrame(path)
      return
    }
    router.push(path)
  }

  return (
    <section ref={ref} className={`panel-dashboard-block cart-member-card outbound-flow-card ${loading ? 'is-loading' : ''}`}>
      <div className="panel-dashboard-block__head cart-member-head">
        <div>
          <strong>OUTBOUND AKIŞI</strong>
          <span>Mevcut durum · Müşteri hizmetleri aramaları</span>
        </div>
        {!isEmbed && (
          <button type="button" onClick={() => router.push('/pages/customer_service/outbound')} aria-label="Outbound aramalarına git">
            <i className="fa-regular fa-arrow-up-right-from-square" />
          </button>
        )}
      </div>
      <div className="cart-member-dash" />
      <div className="outbound-flow-body">
        <div className="outbound-flow-types">
          {types.map((item) => (
            <article key={item.key} style={{ '--outbound-flow-accent': item.color || '#2360ff' } as CSSProperties}>
              <div>
                <span><i />Tip {item.legacyType}</span>
                <strong>{item.label}</strong>
              </div>
              <b>{formatCompact(Number(item.pending || 0))}</b>
              <small>
                <i style={{ width: `${Math.max(4, (Number(item.pending || 0) / maxPending) * 100)}%` }} />
              </small>
              <button
                type="button"
                className="outbound-flow-show-calls"
                disabled={loading && !data}
                onClick={(event) => {
                  event.stopPropagation()
                  openOutboundCalls(item)
                }}
              >
                Aramaları Göster
              </button>
            </article>
          ))}
        </div>

        <section className="outbound-flow-panel outbound-flow-trend">
          <div className="outbound-flow-panel__head">
            <strong>Trend</strong>
            <div className="outbound-flow-periods">
              {outboundFlowPeriodOptions.map((option) => (
                <button key={option} type="button" className={period === option ? 'is-selected' : ''} onClick={() => setPeriod(option)}>
                  {option}
                </button>
              ))}
            </div>
          </div>
          <div className="outbound-flow-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="#edf1fa" vertical={false} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#8791bc' }} />
                <YAxis hide allowDecimals={false} />
                <Tooltip cursor={{ stroke: '#c7d1f5', strokeDasharray: '3 3', strokeWidth: 1 }} content={<OutboundFlowTrendTooltip />} wrapperStyle={{ outline: 'none' }} />
                <Bar name="Toplam" dataKey="total" fill="#e7edf7" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                {types.map((item) => (
                  <Line
                    key={item.key}
                    name={item.label}
                    type="monotone"
                    dataKey={item.key}
                    stroke={item.color || '#2360ff'}
                    strokeWidth={1.7}
                    dot={false}
                    activeDot={{ r: 3, fill: item.color || '#2360ff', stroke: '#fff', strokeWidth: 1.5 }}
                    isAnimationActive={false}
                  />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="outbound-flow-panel outbound-flow-personnel">
          <div className="outbound-flow-panel__head">
            <strong>Personel</strong>
            <span>{formatCompact(personnel.reduce((sum, item) => sum + Number(item.completed || 0), 0))} arama</span>
          </div>
          <div className="outbound-flow-personnel__list">
            {personnel.length ? personnel.map((item) => (
              <div key={item.userId || item.userName}>
                <span>{item.userName}</span>
                <strong>{formatCompact(Number(item.completed || 0))}</strong>
                <small>Bugün {formatCompact(Number(item.completedToday || 0))}</small>
              </div>
            )) : (
              <div className="is-empty">
                <span>Kayıt yok</span>
                <strong>0</strong>
                <small>{payload.dateLabel || period}</small>
              </div>
            )}
          </div>
        </section>
      </div>
    </section>
  )
}

type SupplyRequestTrendLine = {
  key: 'single' | 'multi' | 'webApp' | 'marketplace'
  label: string
  color: string
}

type SupplyRequestTrendPoint = {
  date: string
  label: string
  total: number
  single: number
  multi: number
  webApp: number
  marketplace: number
  collected: number
}

type SupplyRequestTrendPayload = {
  dateLabel: string
  mode?: 'orders' | 'products'
  days?: number
  total: number
  collected: number
  points: SupplyRequestTrendPoint[]
  lines: SupplyRequestTrendLine[]
}

const supplyRequestTrendBasisOptions = ['Sipariş Bazlı', 'Ürün Bazlı']
const supplyRequestTrendModeValues: Record<string, 'orders' | 'products'> = {
  'Sipariş Bazlı': 'orders',
  'Ürün Bazlı': 'products',
}
const supplyRequestTrendLines: SupplyRequestTrendLine[] = [
  { key: 'single', label: 'Tekli Sipariş', color: '#2360ff' },
  { key: 'multi', label: 'Çoklu Sipariş', color: '#7c5cff' },
  { key: 'webApp', label: 'Web & Uygulama', color: '#16a085' },
  { key: 'marketplace', label: 'Pazaryerleri', color: '#ef476f' },
]

function supplyRequestTrendFallback(): SupplyRequestTrendPayload {
  return {
    dateLabel: 'Son 14 Gün',
    total: 0,
    collected: 0,
    points: [],
    lines: supplyRequestTrendLines,
  }
}

function SupplyRequestTrendTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: { value?: number | string; name?: string; color?: string }[]
  label?: string | number
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="channel-orders-tooltip supply-request-trend-tooltip">
      <span>{label}</span>
      {payload.map((item) => (
        <em key={item.name || String(item.value)}>
          {item.name || 'Seri'}: {formatCompact(Number(item.value || 0))}
        </em>
      ))}
    </div>
  )
}

function SupplyRequestTrendDashboard() {
  const [basis, setBasis] = useState('Sipariş Bazlı')
  const [basisOpen, setBasisOpen] = useState(false)
  const { ref, visible } = useDashboardInView()
  const mode = supplyRequestTrendModeValues[basis] || 'orders'
  const params = useMemo(() => ({
    mode,
    days: '7',
  }), [mode])
  const { data, loading } = useDashboardWidgetData<SupplyRequestTrendPayload>({
    endpoint: '/panel/dashboard-widgets/supply-request-trend',
    params,
    enabled: visible,
  })
  const fallback = useMemo(() => supplyRequestTrendFallback(), [])
  const payload = data || fallback
  const points = Array.isArray(payload.points) ? payload.points : []
  const lines = Array.isArray(payload.lines) && payload.lines.length ? payload.lines : supplyRequestTrendLines
  const unitLabel = mode === 'products' ? 'Ürün' : 'Sipariş'

  return (
    <section ref={ref} className={`panel-dashboard-block cart-member-card supply-request-trend-card ${loading ? 'is-loading' : ''}`}>
      <div className="panel-dashboard-block__head cart-member-head supply-request-trend-head">
        <div>
          <strong>TOPLAMA TRENDİ</strong>
          <span>{payload.dateLabel || 'Son 7 Gün'}</span>
        </div>
        <div className="supply-request-trend-total">
          <strong>{formatCompact(Number(payload.total || 0))}</strong>
          <span>{unitLabel}</span>
        </div>
      </div>
      <div className="cart-member-dash" />
      <div className="supply-request-trend-body">
        <div className="supply-request-trend-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={points} margin={{ top: 10, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#edf1fa" vertical={false} />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#8791bc' }} />
              <YAxis hide allowDecimals={false} />
              <Tooltip cursor={{ stroke: '#c7d1f5', strokeDasharray: '3 3', strokeWidth: 1 }} content={<SupplyRequestTrendTooltip />} wrapperStyle={{ outline: 'none' }} />
              <Bar name={`Toplam ${unitLabel}`} dataKey="total" fill="#e7edf7" radius={[4, 4, 0, 0]} isAnimationActive={false} />
              {lines.map((item) => (
                <Line
                  key={item.key}
                  name={item.label}
                  type="monotone"
                  dataKey={item.key}
                  stroke={item.color}
                  strokeWidth={1.7}
                  dot={false}
                  activeDot={{ r: 3, fill: item.color, stroke: '#fff', strokeWidth: 1.5 }}
                  isAnimationActive={false}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="supply-request-trend-legend">
          <span><i />Toplam {unitLabel}</span>
          {lines.map((item) => (
            <span key={item.key} style={{ '--supply-request-trend-color': item.color } as CSSProperties}>
              <i />{item.label}
            </span>
          ))}
          <em>{formatCompact(Number(payload.collected || 0))} toplandı</em>
        </div>
      </div>
      <div className="channel-orders-foot supply-request-trend-foot">
        <DashboardWidgetDropdown
          value={basis}
          options={supplyRequestTrendBasisOptions}
          open={basisOpen}
          onOpenChange={setBasisOpen}
          onSelect={setBasis}
          icon="fa-regular fa-layer-group"
        />
      </div>
    </section>
  )
}

type SupplyDashboardTone = 'blue' | 'green' | 'orange' | 'red' | 'purple'

type SupplyDashboardCard = {
  key: string
  label: string
  orders: number
  products: number
  tone: SupplyDashboardTone
  description?: string
  help?: string
}

type SupplyRadarPayload = {
  dateLabel: string
  scopeLabel?: string
  totalOrders: number
  totalProducts: number
  signalCount: number
  cards: SupplyDashboardCard[]
}

type SupplyAgingBucket = {
  key: string
  label: string
  orders: number
  products: number
}

type SupplyAgingChannel = {
  key: string
  label: string
  orders: number
  products: number
  color: string
}

type SupplyAgingPayload = {
  dateLabel: string
  scopeLabel?: string
  totalOrders: number
  totalProducts: number
  oldestDate?: string
  oldestAgeDays?: number
  buckets: SupplyAgingBucket[]
  channels: SupplyAgingChannel[]
}

type SupplyPerformancePoint = {
  date: string
  label: string
  requestedOrders: number
  requestedProducts: number
  collectedOrders: number
  collectedProducts: number
  singleOrders: number
  multiOrders: number
}

type SupplyPerformancePayload = {
  dateLabel: string
  requestedOrders: number
  requestedProducts: number
  collectedOrders: number
  collectedProducts: number
  completionRate: number
  backlogDelta?: number
  points: SupplyPerformancePoint[]
}

const supplyToneColors: Record<SupplyDashboardTone, string> = {
  blue: '#2360ff',
  green: '#16a085',
  orange: '#f59e0b',
  red: '#ef476f',
  purple: '#7c5cff',
}

const supplyRadarCardCopy: Record<string, { label: string; help: string }> = {
  pending_supply: {
    label: 'Tedarik Bekleyen',
    help: 'Ödemesi uygun, henüz toplama/tedarik listesine alınmamış sipariş adedi.',
  },
  active_carts: {
    label: 'Toplama Listesinde',
    help: 'Aktif toplama araçları/listeleri içinde işlem gören sipariş adedi.',
  },
  separation_waiting: {
    label: 'Ayrıştırma Bekleyen',
    help: 'Toplama sonrası ayrıştırma listesinde bekleyen sipariş adedi.',
  },
  missing_supply: {
    label: 'Eksik / Hatalı',
    help: 'Eksik ürün ya da hatalı toplama nedeniyle kontrol bekleyen sipariş adedi.',
  },
  invoice_waiting: {
    label: 'Fatura Bekleyen',
    help: 'Toplama sürecinden çıkmış, fatura kesimi bekleyen sipariş adedi.',
  },
  cargo_waiting: {
    label: 'Kargo Bekleyen',
    help: 'Fatura sonrası kargo teslimat/kontrol adımı bekleyen sipariş adedi.',
  },
  consignment: {
    label: 'Konsinye Bekleyen',
    help: 'Konsinye tedarik akışında bekleyen sipariş adedi.',
  },
}

const supplyRadarFallbackCards: SupplyDashboardCard[] = [
  { key: 'pending_supply', label: supplyRadarCardCopy.pending_supply.label, orders: 0, products: 0, tone: 'blue', description: supplyRadarCardCopy.pending_supply.help },
  { key: 'active_carts', label: supplyRadarCardCopy.active_carts.label, orders: 0, products: 0, tone: 'green', description: supplyRadarCardCopy.active_carts.help },
  { key: 'separation_waiting', label: supplyRadarCardCopy.separation_waiting.label, orders: 0, products: 0, tone: 'orange', description: supplyRadarCardCopy.separation_waiting.help },
  { key: 'missing_supply', label: supplyRadarCardCopy.missing_supply.label, orders: 0, products: 0, tone: 'red', description: supplyRadarCardCopy.missing_supply.help },
  { key: 'invoice_waiting', label: supplyRadarCardCopy.invoice_waiting.label, orders: 0, products: 0, tone: 'purple', description: supplyRadarCardCopy.invoice_waiting.help },
  { key: 'cargo_waiting', label: supplyRadarCardCopy.cargo_waiting.label, orders: 0, products: 0, tone: 'blue', description: supplyRadarCardCopy.cargo_waiting.help },
  { key: 'consignment', label: supplyRadarCardCopy.consignment.label, orders: 0, products: 0, tone: 'purple', description: supplyRadarCardCopy.consignment.help },
]

const supplyAgingFallbackBuckets: SupplyAgingBucket[] = [
  { key: 'today', label: 'Bugün', orders: 0, products: 0 },
  { key: 'one_two', label: '1-2 Gün', orders: 0, products: 0 },
  { key: 'three_six', label: '3-6 Gün', orders: 0, products: 0 },
  { key: 'seven_plus', label: '7+ Gün', orders: 0, products: 0 },
  { key: 'unknown', label: 'Tarihsiz', orders: 0, products: 0 },
]

function supplyRadarFallback(): SupplyRadarPayload {
  return {
    dateLabel: 'Mevcut Durum',
    totalOrders: 0,
    totalProducts: 0,
    signalCount: 0,
    cards: supplyRadarFallbackCards,
  }
}

function supplyAgingFallback(): SupplyAgingPayload {
  return {
    dateLabel: 'Mevcut Durum',
    totalOrders: 0,
    totalProducts: 0,
    oldestDate: '',
    oldestAgeDays: 0,
    buckets: supplyAgingFallbackBuckets,
    channels: [
      { key: 'web_app', label: 'Web & Uygulama', orders: 0, products: 0, color: '#16a085' },
      { key: 'marketplace', label: 'Pazaryerleri', orders: 0, products: 0, color: '#ef476f' },
      { key: 'other', label: 'Diğer', orders: 0, products: 0, color: '#7c5cff' },
    ],
  }
}

function supplyPerformanceFallback(): SupplyPerformancePayload {
  return {
    dateLabel: 'Son 7 Gün',
    requestedOrders: 0,
    requestedProducts: 0,
    collectedOrders: 0,
    collectedProducts: 0,
    completionRate: 0,
    backlogDelta: 0,
    points: [],
  }
}

function formatSupplyPercent(value: number) {
  return new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 1 }).format(value)
}

function cleanTooltipText(value: string) {
  return value.replace(/\s+/g, ' ').trim()
}

function SupplyDashboardTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: { value?: number | string; name?: string; color?: string }[]
  label?: string | number
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="channel-orders-tooltip supply-dashboard-tooltip">
      <span>{label}</span>
      {payload.map((item) => (
        <em key={item.name || String(item.value)}>
          {item.name || 'Seri'}: {formatCompact(Number(item.value || 0))}
        </em>
      ))}
    </div>
  )
}

function SupplyRadarDashboard() {
  const { ref, visible } = useDashboardInView()
  const params = useMemo(() => ({ scope: 'store' }), [])
  const { data, loading } = useDashboardWidgetData<SupplyRadarPayload>({
    endpoint: '/panel/dashboard-widgets/supply-radar',
    params,
    enabled: visible,
  })
  const payload = data || supplyRadarFallback()
  const cards = (Array.isArray(payload.cards) && payload.cards.length ? payload.cards : supplyRadarFallbackCards)
    .map((card) => ({
      ...card,
      label: supplyRadarCardCopy[card.key]?.label || card.label,
      help: supplyRadarCardCopy[card.key]?.help || card.help || card.description || '',
    }))
  const maxOrders = Math.max(...cards.map((card) => Number(card.orders || 0)), 1)

  return (
    <section ref={ref} className={`panel-dashboard-block cart-member-card supply-dashboard-card supply-radar-card ${loading ? 'is-loading' : ''}`}>
      <div className="panel-dashboard-block__head cart-member-head supply-dashboard-head">
        <div>
          <strong data-tooltip="Tedarik sürecindeki açık iş tiplerini ve darboğaz noktalarını gösterir.">TEDARİK İŞ RADARI</strong>
          <span>{payload.dateLabel || 'Mevcut Durum'} · {payload.scopeLabel || 'Seçili mağaza'}</span>
        </div>
        <div className="supply-dashboard-total" data-tooltip="Kartlardaki açık iş adedi toplamı. Alt satır aynı kartların ürün toplamıdır.">
          <strong>{formatCompact(Number(payload.totalOrders || 0))}</strong>
          <span>{formatCompact(Number(payload.totalProducts || 0))} ürün</span>
        </div>
      </div>
      <div className="cart-member-dash" />
      <div className="supply-radar-grid">
        {cards.map((card) => {
          const color = supplyToneColors[card.tone] || supplyToneColors.blue
          const width = Math.max(3, Math.round((Number(card.orders || 0) / maxOrders) * 100))
          return (
            <article
              key={card.key}
              data-tooltip={cleanTooltipText(`${card.help || card.description || card.label} Sipariş: ${formatCompact(Number(card.orders || 0))}, ürün: ${formatCompact(Number(card.products || 0))}.`)}
              style={{ '--supply-card-color': color, '--supply-card-width': `${width}%` } as CSSProperties}
            >
              <span>{card.label}</span>
              <strong>{formatCompact(Number(card.orders || 0))}</strong>
              <small>{formatCompact(Number(card.products || 0))} ürün</small>
              <i />
            </article>
          )
        })}
      </div>
      <div className="supply-dashboard-footline">
        <span data-tooltip="Sıfırdan büyük değer taşıyan iş tipi sayısı.">{formatCompact(Number(payload.signalCount || cards.filter((card) => Number(card.orders || 0) > 0).length))} aktif başlık</span>
        <em data-tooltip="Bu kart, hangi operasyon adımında yığılma olduğunu hızlı görmek içindir.">Darboğaz noktaları</em>
      </div>
    </section>
  )
}

function SupplyAgingDashboard() {
  const { ref, visible } = useDashboardInView()
  const params = useMemo(() => ({ scope: 'store' }), [])
  const { data, loading } = useDashboardWidgetData<SupplyAgingPayload>({
    endpoint: '/panel/dashboard-widgets/supply-aging',
    params,
    enabled: visible,
  })
  const payload = data || supplyAgingFallback()
  const buckets = Array.isArray(payload.buckets) && payload.buckets.length ? payload.buckets : supplyAgingFallbackBuckets
  const channels = Array.isArray(payload.channels) ? payload.channels : []
  const maxOrders = Math.max(...buckets.map((bucket) => Number(bucket.orders || 0)), 1)

  return (
    <section ref={ref} className={`panel-dashboard-block cart-member-card supply-dashboard-card supply-aging-card ${loading ? 'is-loading' : ''}`}>
      <div className="panel-dashboard-block__head cart-member-head supply-dashboard-head">
        <div>
          <strong data-tooltip="Tedarik bekleyen siparişlerin kaç gündür beklediğini gösterir.">TEDARİK YAŞLANDIRMA</strong>
          <span>{payload.dateLabel || 'Mevcut Durum'} · bekleyen toplama</span>
        </div>
        <div className="supply-dashboard-total" data-tooltip="Toplama/tedarik bekleyen sipariş adedi. Alt bilgi en eski açık kaydın kaç günlük olduğunu gösterir.">
          <strong>{formatCompact(Number(payload.totalOrders || 0))}</strong>
          <span>{Number(payload.oldestAgeDays || 0) > 0 ? `en eski ${formatCompact(Number(payload.oldestAgeDays || 0))} gün` : `${formatCompact(Number(payload.totalProducts || 0))} ürün`}</span>
        </div>
      </div>
      <div className="cart-member-dash" />
      <div className="supply-aging-body">
        <div className="supply-aging-bars">
          {buckets.map((bucket) => {
            const width = Math.max(2, Math.round((Number(bucket.orders || 0) / maxOrders) * 100))
            return (
              <div
                key={bucket.key}
                data-tooltip={cleanTooltipText(`${bucket.label} aralığında bekleyen sipariş: ${formatCompact(Number(bucket.orders || 0))}, ürün: ${formatCompact(Number(bucket.products || 0))}.`)}
                style={{ '--supply-aging-width': `${width}%` } as CSSProperties}
              >
                <span>{bucket.label}</span>
                <strong>{formatCompact(Number(bucket.orders || 0))}</strong>
                <small>{formatCompact(Number(bucket.products || 0))} ürün</small>
                <i />
              </div>
            )
          })}
        </div>
        <div className="supply-aging-channels">
          {channels.map((channel) => (
            <span key={channel.key} data-tooltip={`${channel.label} kaynaklı bekleyen sipariş adedi.`} style={{ '--supply-card-color': channel.color } as CSSProperties}>
              <i />{channel.label}<strong>{formatCompact(Number(channel.orders || 0))}</strong>
            </span>
          ))}
        </div>
      </div>
    </section>
  )
}

function SupplyPerformanceDashboard() {
  const { ref, visible } = useDashboardInView()
  const params = useMemo(() => ({ days: '7' }), [])
  const { data, loading } = useDashboardWidgetData<SupplyPerformancePayload>({
    endpoint: '/panel/dashboard-widgets/supply-performance',
    params,
    enabled: visible,
  })
  const payload = data || supplyPerformanceFallback()
  const points = Array.isArray(payload.points) ? payload.points : []
  const backlogDelta = typeof payload.backlogDelta === 'number'
    ? payload.backlogDelta
    : Number(payload.requestedOrders || 0) - Number(payload.collectedOrders || 0)
  const backlogLabel = backlogDelta > 0
    ? `${formatCompact(backlogDelta)} açık`
    : (backlogDelta < 0 ? `${formatCompact(Math.abs(backlogDelta))} fazla kapandı` : 'denge')

  return (
    <section ref={ref} className={`panel-dashboard-block cart-member-card supply-dashboard-card supply-performance-card ${loading ? 'is-loading' : ''}`}>
      <div className="panel-dashboard-block__head cart-member-head supply-dashboard-head">
        <div>
          <strong data-tooltip="Sürece giren siparişler talep tarihiyle, toplananlar tamamlanma tarihiyle hesaplanır.">TOPLAMA PERFORMANSI</strong>
          <span>{payload.dateLabel || 'Son 7 Gün'}</span>
        </div>
        <div className="supply-performance-summary" aria-label="Toplama performansı özeti">
          <div data-tooltip="Seçilen tarih aralığında toplama sürecine giren sipariş adedi.">
            <strong>{formatCompact(Number(payload.requestedOrders || 0))} Sipariş</strong>
            <span>Giren</span>
          </div>
          <div data-tooltip="Seçilen tarih aralığında tamamlanma tarihi oluşan sipariş adedi.">
            <strong>{formatCompact(Number(payload.collectedOrders || 0))} Sipariş</strong>
            <span>Toplanan</span>
          </div>
          <div data-tooltip="Toplanan sipariş / giren sipariş oranı ve dönem farkı.">
            <strong>%{formatSupplyPercent(Number(payload.completionRate || 0))}</strong>
            <em>{backlogLabel}</em>
          </div>
        </div>
      </div>
      <div className="cart-member-dash" />
      <div className="supply-performance-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="#edf1fa" vertical={false} />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#8791bc' }} />
            <YAxis hide allowDecimals={false} />
            <Tooltip cursor={{ stroke: '#c7d1f5', strokeDasharray: '3 3', strokeWidth: 1 }} content={<SupplyDashboardTooltip />} wrapperStyle={{ outline: 'none' }} />
            <Bar name="Toplam Giren" dataKey="requestedOrders" fill="#e7edf7" radius={[4, 4, 0, 0]} isAnimationActive={false} />
            <Line name="Toplanan Sipariş" type="monotone" dataKey="collectedOrders" stroke="#2360ff" strokeWidth={1.8} dot={false} activeDot={{ r: 3, fill: '#2360ff', stroke: '#fff', strokeWidth: 1.5 }} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="supply-performance-legend">
        <span data-tooltip="Barlar toplama sürecine giren sipariş adedini gösterir."><i />Toplam Giren</span>
        <span data-tooltip="Çizgi tamamlanma tarihine göre toplanan sipariş adedini gösterir."><i />Toplanan Sipariş</span>
        <span data-tooltip="Giren siparişlerin tek ürünlü olan kısmı."><i />Tekli {formatCompact(points.reduce((sum, point) => sum + Number(point.singleOrders || 0), 0))}</span>
        <span data-tooltip="Giren siparişlerin birden fazla ürünlü olan kısmı."><i />Çoklu {formatCompact(points.reduce((sum, point) => sum + Number(point.multiOrders || 0), 0))}</span>
        <em data-tooltip="Seçilen aralıkta tamamlanmış toplama ürün adedi.">{formatCompact(Number(payload.collectedProducts || 0))} ürün toplandı</em>
      </div>
    </section>
  )
}

type RefundBalanceCard = {
  key: string
  label: string
  count: number
  total: number
  tone: 'blue' | 'green' | 'orange' | 'red' | 'purple'
  description: string
}

type RefundBalanceTrendPoint = {
  date: string
  label: string
  count: number
  total: number
}

type RefundBalanceSummaryPayload = {
  dateLabel: string
  cards: RefundBalanceCard[]
  trend: RefundBalanceTrendPoint[]
}

type RefundReasonRow = {
  key: string
  label: string
  count: number
  total: number
  percent: number
  color: string
}

type RefundReasonPayload = {
  dateLabel: string
  totalCount: number
  totalAmount: number
  reasons: RefundReasonRow[]
  points: RefundBalanceTrendPoint[]
}

const refundBalanceFallbackCards: RefundBalanceCard[] = [
  { key: 'pending', label: 'Bekleyen Ödeme', count: 0, total: 0, tone: 'orange', description: 'Ödeme bekleyen geri ödeme talepleri' },
  { key: 'completed_today', label: 'Bugün Ödenen', count: 0, total: 0, tone: 'green', description: 'Bugün tamamlanan müşteri ödemeleri' },
  { key: 'invalid_iban', label: 'Hatalı IBAN', count: 0, total: 0, tone: 'red', description: 'IBAN bekleyen veya hatalı ödeme talepleri' },
  { key: 'member_balance', label: 'Toplam Bakiye', count: 0, total: 0, tone: 'blue', description: 'Müşteri bakiyesi bulunan üyeler' },
]

function refundTrendFallback(): RefundBalanceTrendPoint[] {
  return channelOrderFallbackLabels('Son 7 Gün', 7).map((label) => ({ date: label, label, count: 0, total: 0 }))
}

function refundBalanceFallbackPayload(): RefundBalanceSummaryPayload {
  return {
    dateLabel: dashboardLongDate(new Date()),
    cards: refundBalanceFallbackCards,
    trend: refundTrendFallback(),
  }
}

function refundReasonFallbackPayload(period: string): RefundReasonPayload {
  return {
    dateLabel: dashboardDateLabelForPeriod(period),
    totalCount: 0,
    totalAmount: 0,
    reasons: [],
    points: refundTrendFallback(),
  }
}

function RefundBalanceTrendTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: { dataKey?: string | number; value?: number | string }[]
  label?: string | number
}) {
  if (!active || !payload?.length) return null
  const total = Number(payload.find((item) => item.dataKey === 'total')?.value || 0)
  const count = Number(payload.find((item) => item.dataKey === 'count')?.value || 0)
  return (
    <div className="channel-orders-tooltip refund-dashboard-tooltip">
      <span>{label}</span>
      <strong>{formatChannelOrderMoney(total)}</strong>
      <em>{formatCompact(count)} işlem</em>
    </div>
  )
}

function RefundReasonTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: { payload?: RefundReasonRow }[]
}) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload
  if (!row || row.key === 'empty') return null
  return (
    <div className="channel-orders-tooltip refund-dashboard-tooltip">
      <span>{row.label}</span>
      <strong>{formatCompact(Number(row.count || 0))} talep</strong>
      <em>{formatChannelOrderMoney(Number(row.total || 0))}</em>
    </div>
  )
}

function RefundBalanceSummaryDashboard() {
  const { ref, visible } = useDashboardInView()
  const params = useMemo(() => ({}), [])
  const { data, loading } = useDashboardWidgetData<RefundBalanceSummaryPayload>({
    endpoint: '/panel/dashboard-widgets/refund-balance-summary',
    params,
    enabled: visible,
  })
  const fallback = useMemo(() => refundBalanceFallbackPayload(), [])
  const payload = data || fallback
  const cards = Array.isArray(payload.cards) && payload.cards.length ? payload.cards : fallback.cards
  const trend = Array.isArray(payload.trend) && payload.trend.length ? payload.trend : fallback.trend
  const pendingCard = cards.find((card) => card.key === 'pending') || cards[0]

  return (
    <section ref={ref} className={`panel-dashboard-block cart-member-card refund-balance-card ${loading ? 'is-loading' : ''}`}>
      <div className="panel-dashboard-block__head cart-member-head refund-dashboard-head">
        <div>
          <strong>GERİ ÖDEME VE BAKİYE</strong>
          <span>{payload.dateLabel || 'Mevcut Durum'}</span>
        </div>
        <b>{formatChannelOrderMoney(Number(pendingCard?.total || 0))}</b>
      </div>
      <div className="cart-member-dash" />
      <div className="refund-balance-body">
        <div className="refund-balance-grid">
          {cards.map((card) => (
            <article key={card.key} className={`refund-balance-item is-${card.tone || 'blue'}`}>
              <span>{card.label}</span>
              <strong>{card.key === 'member_balance' ? formatChannelOrderMoney(Number(card.total || 0)) : formatCompact(Number(card.count || 0))}</strong>
              <small>{card.key === 'member_balance' ? `${formatCompact(Number(card.count || 0))} üye` : formatChannelOrderMoney(Number(card.total || 0))}</small>
              <em>{card.description}</em>
            </article>
          ))}
        </div>
        <div className="refund-balance-trend">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
              <CartesianGrid vertical={false} stroke="#eef1f8" />
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#8b95c5', fontSize: 9, fontWeight: 700 }} />
              <YAxis hide domain={['dataMin', 'dataMax']} />
              <Tooltip cursor={{ stroke: '#c7d1f5', strokeDasharray: '3 3', strokeWidth: 1 }} content={<RefundBalanceTrendTooltip />} wrapperStyle={{ outline: 'none' }} />
              <Line type="monotone" dataKey="total" name="total" stroke="#16a085" strokeWidth={1.8} dot={false} activeDot={{ r: 4, fill: '#16a085', stroke: '#fff', strokeWidth: 1.5 }} isAnimationActive={false} />
              <Line type="monotone" dataKey="count" name="count" stroke="#2360ff" strokeWidth={1.4} dot={false} activeDot={{ r: 3, fill: '#2360ff', stroke: '#fff', strokeWidth: 1.5 }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  )
}

const refundReasonPeriodOptions = ['Son 7 Gün', 'Son 30 Gün', 'Bu Ay']
const refundReasonPeriodValues: Record<string, string> = {
  'Son 7 Gün': 'last7',
  'Son 30 Gün': 'last30',
  'Bu Ay': 'this_month',
}

function RefundReasonDistributionDashboard() {
  const [period, setPeriod] = useState('Son 30 Gün')
  const [openDropdown, setOpenDropdown] = useState(false)
  const { ref, visible } = useDashboardInView()
  const params = useMemo(() => ({
    period: refundReasonPeriodValues[period] || 'last30',
  }), [period])
  const { data, loading } = useDashboardWidgetData<RefundReasonPayload>({
    endpoint: '/panel/dashboard-widgets/refund-reason-distribution',
    params,
    enabled: visible,
  })
  const fallback = useMemo(() => refundReasonFallbackPayload(period), [period])
  const payload = data || fallback
  const reasons = Array.isArray(payload.reasons) ? payload.reasons : []
  const positiveReasons = reasons.filter((row) => Number(row.count || 0) > 0)
  const pieRows = positiveReasons.length
    ? positiveReasons
    : [{ key: 'empty', label: 'Veri Yok', count: 1, total: 0, percent: 0, color: '#e8ecf8' }]
  const points = Array.isArray(payload.points) && payload.points.length ? payload.points : fallback.points

  return (
    <section ref={ref} className={`panel-dashboard-block cart-member-card refund-reason-card ${loading ? 'is-loading' : ''}`}>
      <div className="panel-dashboard-block__head cart-member-head refund-dashboard-head">
        <div>
          <strong>GERİ ÖDEME SEBEP DAĞILIMI</strong>
          <span>{payload.dateLabel || dashboardDateLabelForPeriod(period)}</span>
        </div>
        <b>{formatCompact(Number(payload.totalCount || 0))} Talep</b>
      </div>
      <div className="cart-member-dash" />
      <div className="refund-reason-body">
        <div className="refund-reason-chart">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={pieRows} dataKey="count" nameKey="label" innerRadius={42} outerRadius={62} paddingAngle={2}>
                {pieRows.map((row) => <Cell key={row.key} fill={row.color} />)}
              </Pie>
              <Tooltip cursor={false} content={<RefundReasonTooltip />} wrapperStyle={{ outline: 'none' }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="refund-reason-center">
            <strong>{formatCompact(Number(payload.totalCount || 0))}</strong>
            <span>{formatChannelOrderMoney(Number(payload.totalAmount || 0))}</span>
          </div>
        </div>
        <div className="refund-reason-list">
          {(positiveReasons.length ? positiveReasons : reasons).slice(0, 5).map((row) => (
            <div key={row.key}>
              <i style={{ background: row.color }} />
              <strong>{row.label}</strong>
              <span>%{Number(row.percent || 0).toFixed(1).replace('.', ',')}</span>
              <b>{formatCompact(Number(row.count || 0))}</b>
            </div>
          ))}
          {!reasons.length && <div><i style={{ background: '#e8ecf8' }} /><strong>Veri Yok</strong><span>%0</span><b>0</b></div>}
        </div>
        <div className="refund-reason-trend">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 6, right: 8, bottom: 2, left: 8 }}>
              <XAxis dataKey="label" hide />
              <YAxis hide domain={['dataMin', 'dataMax']} />
              <Tooltip cursor={{ stroke: '#c7d1f5', strokeDasharray: '3 3', strokeWidth: 1 }} content={<RefundBalanceTrendTooltip />} wrapperStyle={{ outline: 'none' }} />
              <Line type="monotone" dataKey="count" name="count" stroke="#2360ff" strokeWidth={1.8} dot={false} activeDot={{ r: 3, fill: '#2360ff', stroke: '#fff', strokeWidth: 1.5 }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="channel-orders-foot refund-reason-foot">
        <DashboardWidgetDropdown
          value={period}
          options={refundReasonPeriodOptions}
          open={openDropdown}
          onOpenChange={setOpenDropdown}
          onSelect={setPeriod}
          icon="fa-regular fa-calendar-days"
        />
      </div>
    </section>
  )
}

function ReportDashboard({ dashboard }: { dashboard: DashboardDefinition }) {
  const key = dashboard.component_key || ''
  const weeklyRows = ['Pzt', 'Salı', 'Çrş', 'Prş', 'Cuma', 'Cts', 'Pazar'].map((label, index) => ({
    label,
    value: [68000, 74500, 71200, 83600, 92500, 88400, 97800][index],
    value2: [320, 348, 331, 386, 402, 390, 427][index]
  }))
  const platformRows = reportPlatforms.map((label, index) => ({
    label,
    value: [342, 286, 254, 118, 96][index],
    value2: [184500, 143200, 128700, 66500, 42100][index]
  }))
  const categoryRows = [
    { label: 'Ayakkabı', value: 328 },
    { label: 'Çanta', value: 214 },
    { label: 'Aksesuar', value: 176 },
    { label: 'Tekstil', value: 139 },
    { label: 'Kozmetik', value: 88 }
  ]

  switch (key) {
    case 'saas_total_revenue':
      return <SaasTotalRevenueDashboard dashboard={dashboard} />
    case 'crm_lost_deals_analysis':
      return <LostDealsAnalysisDashboard dashboard={dashboard} />
    case 'crm_sales_leaderboard':
      return <SalesLeaderboardDashboard dashboard={dashboard} />
    case 'crm_revenue_performance':
      return <RevenuePerformanceDashboard dashboard={dashboard} />
    case 'income_intensity_wave':
      return <IncomeIntensityDashboard dashboard={dashboard} />
    case 'commerce_total_sales':
      return <CommerceKpiDashboard dashboard={dashboard} type="sales" />
    case 'commerce_total_orders':
      return <CommerceKpiDashboard dashboard={dashboard} type="orders" />
    case 'commerce_total_products':
      return <CommerceKpiDashboard dashboard={dashboard} type="products" />
    case 'commerce_sales_statistics':
      return <CommerceSalesStatisticsDashboard dashboard={dashboard} />
    case 'commerce_recent_month':
      return <CommerceRecentMonthDashboard dashboard={dashboard} />
    case 'ev_age_users':
      return <EvAgeUsersDashboard dashboard={dashboard} />
    case 'ev_adoption_countries':
      return <EvAdoptionDashboard dashboard={dashboard} />
    case 'ev_app_popularity':
      return <EvAppPopularityDashboard dashboard={dashboard} />
    case 'ev_charging_locations':
      return <EvChargingLocationsDashboard dashboard={dashboard} />
    case 'hourly_orders_heatmap':
      return (
        <DashboardBlock dashboard={dashboard}>
          <ReportKpiGrid items={[
            { label: 'Sipariş', value: '1.128', delta: '+12%', tone: 'blue' },
            { label: 'Yoğun Saat', value: '16-18', tone: 'purple' },
            { label: 'Ortalama', value: '47/saat', tone: 'green' }
          ]} />
          <ReportHeatmap />
        </DashboardBlock>
      )
    case 'payment_type_orders':
      return (
        <DashboardBlock dashboard={dashboard}>
          <ReportKpiGrid items={[
            { label: 'Kredi Kartı', value: '684', delta: '%61', tone: 'blue' },
            { label: 'Kapıda Ödeme', value: '273', delta: '%24', tone: 'orange' },
            { label: 'Havale/EFT', value: '171', delta: '%15', tone: 'green' }
          ]} />
          <ReportBarList valueLabel="sipariş" rows={[
            { label: 'Kredi Kartı', value: 684 },
            { label: 'Kapıda Ödeme', value: 273 },
            { label: 'Havale/EFT', value: 171 },
            { label: 'Pazar Yeri Ödeme', value: 142 }
          ]} />
        </DashboardBlock>
      )
    case 'basket_member_stats':
      return (
        <DashboardBlock dashboard={dashboard}>
          <ReportKpiGrid items={[
            { label: 'Sepet Ort.', value: '842 TL', delta: '+8%', tone: 'green' },
            { label: 'Üye Siparişi', value: '716', delta: '%63', tone: 'blue' },
            { label: 'Misafir', value: '412', delta: '%37', tone: 'purple' }
          ]} />
          <ReportLineChart rows={[
            { label: '1', value: 520, value2: 310 },
            { label: '2', value: 610, value2: 360 },
            { label: '3', value: 580, value2: 340 },
            { label: '4', value: 740, value2: 395 },
            { label: '5', value: 690, value2: 420 },
            { label: '6', value: 780, value2: 455 },
          ]} secondary />
        </DashboardBlock>
      )
    case 'supply_compare':
      return (
        <DashboardBlock dashboard={dashboard}>
          <ReportSplitTable rows={[
            { label: 'Web', value: 418, value2: 212000 },
            { label: 'Hepsiburada', value: 286, value2: 146000 },
            { label: 'Trendyol', value: 264, value2: 138500 },
            { label: 'Çiçek Sepeti', value: 124, value2: 59000 },
          ]} />
        </DashboardBlock>
      )
    case 'supply_process_hours':
      return (
        <DashboardBlock dashboard={dashboard}>
          <ReportKpiGrid items={[
            { label: 'Bekleyen', value: '86', tone: 'orange' },
            { label: 'Toplanan', value: '742', delta: '+18%', tone: 'green' },
            { label: 'Ort. Süre', value: '34 dk', tone: 'blue' }
          ]} />
          <ReportHeatmap mode="supply" />
        </DashboardBlock>
      )
    case 'product_sales_status':
      return (
        <DashboardBlock dashboard={dashboard}>
          <ReportKpiGrid items={[
            { label: 'Yayında', value: '4.218', tone: 'green' },
            { label: 'Tükenen', value: '312', tone: 'red' },
            { label: 'Satışta', value: '1.924', tone: 'blue' }
          ]} />
          <ReportBarList valueLabel="ürün" rows={categoryRows} />
        </DashboardBlock>
      )
    case 'stock_sales':
      return (
        <DashboardBlock dashboard={dashboard}>
          <ReportSplitTable rows={[
            { label: 'Stoklu Satış', value: 924, value2: 486000 },
            { label: 'Kritik Stok', value: 118, value2: 72000 },
            { label: 'Stoksuz Talep', value: 54, value2: 31000 },
            { label: 'Yeniden Tedarik', value: 77, value2: 45500 },
          ]} />
        </DashboardBlock>
      )
    case 'cargo_cod_analysis':
      return (
        <DashboardBlock dashboard={dashboard}>
          <ReportKpiGrid items={[
            { label: 'Kapıda Ödeme', value: '273', delta: '+6%', tone: 'orange' },
            { label: 'Tahsilat', value: '126K TL', tone: 'green' },
            { label: 'İade Riski', value: '%4.2', tone: 'red' }
          ]} />
          <ReportBarList valueLabel="paket" rows={[
            { label: 'Yurtiçi', value: 96 },
            { label: 'MNG', value: 72 },
            { label: 'Aras', value: 58 },
            { label: 'Hepsijet', value: 47 },
          ]} />
        </DashboardBlock>
      )
    case 'weekly_revenue_quantity':
      return (
        <DashboardBlock dashboard={dashboard}>
          <ReportKpiGrid items={[
            { label: 'Haftalık Ciro', value: '571K TL', delta: '+14%', tone: 'green' },
            { label: 'Adet', value: '2.604', tone: 'blue' },
            { label: 'Ort. Fiş', value: '219 TL', tone: 'purple' }
          ]} />
          <ReportLineChart rows={weeklyRows} secondary />
        </DashboardBlock>
      )
    case 'return_summary':
      return (
        <DashboardBlock dashboard={dashboard}>
          <ReportKpiGrid items={[
            { label: 'İade Talebi', value: '84', tone: 'orange' },
            { label: 'Onaylanan', value: '51', tone: 'green' },
            { label: 'Red', value: '9', tone: 'red' }
          ]} />
          <ReportBarList valueLabel="iade" rows={[
            { label: 'Beden Uymadı', value: 28 },
            { label: 'Vazgeçti', value: 22 },
            { label: 'Hasarlı', value: 14 },
            { label: 'Yanlış Ürün', value: 11 },
          ]} />
        </DashboardBlock>
      )
    case 'return_product_category':
      return (
        <DashboardBlock dashboard={dashboard}>
          <ReportBarList valueLabel="iade" rows={[
            { label: 'Ayakkabı', value: 38 },
            { label: 'Tekstil', value: 26 },
            { label: 'Çanta', value: 19 },
            { label: 'Aksesuar', value: 12 },
            { label: 'Kozmetik', value: 7 },
          ]} />
        </DashboardBlock>
      )
    case 'payment_collection':
      return (
        <DashboardBlock dashboard={dashboard}>
          <ReportKpiGrid items={[
            { label: 'Bekleyen', value: '42K TL', tone: 'orange' },
            { label: 'Tahsil', value: '318K TL', delta: '+9%', tone: 'green' },
            { label: 'Mutabakat', value: '%96', tone: 'blue' }
          ]} />
          <ReportSplitTable rows={platformRows.slice(0, 4)} />
        </DashboardBlock>
      )
    case 'goods_receipt':
      return (
        <DashboardBlock dashboard={dashboard}>
          <ReportKpiGrid items={[
            { label: 'Mal Kabul', value: '126', tone: 'blue' },
            { label: 'Kontrol', value: '91', tone: 'orange' },
            { label: 'Tamamlanan', value: '78', tone: 'green' }
          ]} />
          <ReportBarList valueLabel="koli" rows={[
            { label: 'Depo - Lojistik', value: 126 },
            { label: 'Cadde Mağaza', value: 74 },
            { label: 'Outlet', value: 52 },
          ]} />
        </DashboardBlock>
      )
    case 'pending_supply_ops':
      return (
        <DashboardBlock dashboard={dashboard}>
          <ReportKpiGrid items={[
            { label: 'Toplama', value: '48', tone: 'orange' },
            { label: 'Paketleme', value: '31', tone: 'blue' },
            { label: 'Sevk', value: '19', tone: 'purple' }
          ]} />
          <ReportBarList valueLabel="iş" rows={[
            { label: '0-15 dk', value: 42 },
            { label: '15-30 dk', value: 31 },
            { label: '30-60 dk', value: 18 },
            { label: '60+ dk', value: 7 },
          ]} />
        </DashboardBlock>
      )
    case 'customer_service':
      return (
        <DashboardBlock dashboard={dashboard}>
          <ReportKpiGrid items={[
            { label: 'Açık Talep', value: '37', tone: 'orange' },
            { label: 'Yanıtlandı', value: '112', delta: '+21%', tone: 'green' },
            { label: 'Ort. Yanıt', value: '18 dk', tone: 'blue' }
          ]} />
          <ReportBarList valueLabel="talep" rows={[
            { label: 'İletişim', value: 52 },
            { label: 'Bayi', value: 28 },
            { label: 'İK', value: 19 },
            { label: 'Influencer', value: 13 },
          ]} />
        </DashboardBlock>
      )
    default:
      return (
        <DashboardBlock dashboard={dashboard}>
          <div className="panel-dashboard-placeholder">
            <i className="fa-duotone fa-chart-simple" />
            <strong>{dashboard.component_key || 'Dashboard'}</strong>
            <span>Bu dashboard bileşeni için web görünümü tanımlanmadı.</span>
          </div>
        </DashboardBlock>
      )
  }
}

function DashboardRenderer({
  dashboard,
  onNavigate,
  quickActionNavItems,
  userId,
  layout,
}: {
  dashboard: DashboardDefinition
  onNavigate: (path: string) => void
  quickActionNavItems: QuickActionNav[]
  userId?: number | string
  layout?: DashboardGridItemLayout
}) {
  if (dashboard.id === 62) {
    return <ChannelOrdersDashboard dashboard={dashboard} />
  }
  if (dashboard.id === 63) {
    return <HourlyOrdersDashboard />
  }
  if (dashboard.id === 64) {
    return <PaymentTypeOrdersDashboard />
  }
  if (dashboard.id === 65) {
    return <CartMemberStatsDashboard />
  }
  if (dashboard.id === 66) {
    return <GeneralStatsDashboard />
  }
  if (dashboard.id === 67) {
    return <OrderStatusSummaryDashboard />
  }
  if (dashboard.id === 58) {
    return <SalesReportDashboard />
  }
  if (dashboard.id === 47) {
    return <CargoStatusReportDashboard />
  }
  if (dashboard.id === 51) {
    return <CargoCompanyPerformanceDashboard />
  }
  if (dashboard.id === operationFlowDashboardId || dashboard.component_key === 'operation_flow') {
    return <OperationFlowDashboard />
  }
  if (dashboard.id === outboundCustomerServiceDashboardId || dashboard.component_key === 'outbound_customer_service') {
    return <OutboundCustomerServiceDashboard />
  }
  if (dashboard.id === supplyRequestTrendDashboardId || dashboard.component_key === 'supply_request_trend') {
    return <SupplyRequestTrendDashboard />
  }
  if (dashboard.id === supplyRadarDashboardId || dashboard.component_key === 'supply_radar') {
    return <SupplyRadarDashboard />
  }
  if (dashboard.id === supplyAgingDashboardId || dashboard.component_key === 'supply_aging') {
    return <SupplyAgingDashboard />
  }
  if (dashboard.id === supplyPerformanceDashboardId || dashboard.component_key === 'supply_performance') {
    return <SupplyPerformanceDashboard />
  }
  if (dashboard.id === marketplaceProductErrorsDashboardId || dashboard.component_key === 'marketplace_product_errors') {
    return <MarketplaceProductErrorsDashboard />
  }
  if (dashboard.id === refundBalanceDashboardId || dashboard.component_key === 'refund_balance_summary') {
    return <RefundBalanceSummaryDashboard />
  }
  if (dashboard.id === refundReasonDashboardId || dashboard.component_key === 'refund_reason_distribution') {
    return <RefundReasonDistributionDashboard />
  }
  if (isMockChartDashboard(dashboard)) {
    return <MockCommerceChartDashboard dashboard={dashboard} />
  }

  switch (dashboard.component_key) {
    case 'quick_actions':
      return <QuickActionsDashboard dashboard={dashboard} onNavigate={onNavigate} navItems={quickActionNavItems} userId={userId} layout={layout} />
    case 'kpi_strip':
      return <KpiStripDashboard dashboard={dashboard} layout={layout} />
    case 'activity_list':
      return <ActivityListDashboard dashboard={dashboard} />
    case 'trend_bars':
      return <TrendBarsDashboard dashboard={dashboard} />
    case 'hourly_orders_heatmap':
    case 'payment_type_orders':
    case 'basket_member_stats':
    case 'supply_compare':
    case 'supply_process_hours':
    case 'product_sales_status':
    case 'stock_sales':
    case 'cargo_cod_analysis':
    case 'weekly_revenue_quantity':
    case 'return_summary':
    case 'return_product_category':
    case 'payment_collection':
    case 'goods_receipt':
    case 'pending_supply_ops':
    case 'customer_service':
    case 'ev_age_users':
    case 'ev_adoption_countries':
    case 'ev_app_popularity':
    case 'ev_charging_locations':
    case 'commerce_total_sales':
    case 'commerce_total_orders':
    case 'commerce_total_products':
    case 'commerce_sales_statistics':
    case 'commerce_recent_month':
    case 'income_intensity_wave':
    case 'saas_total_revenue':
    case 'crm_lost_deals_analysis':
    case 'crm_sales_leaderboard':
    case 'crm_revenue_performance':
      return <ReportDashboard dashboard={dashboard} />
    default:
      return (
        <DashboardBlock dashboard={dashboard}>
          <div className="panel-dashboard-placeholder">
            <i className="fa-duotone fa-chart-simple" />
            <strong>{dashboard.component_key || 'Dashboard'}</strong>
            <span>Bu dashboard bileşeni web panelde sonraki adımda doldurulacak.</span>
          </div>
        </DashboardBlock>
      )
  }
}

type DashboardEmbedContentProps = {
  embedToken: string
}

type DashboardEmbedStatePayload = {
  user?: {
    id?: number | string
    admin?: number | boolean
    firm_id?: number | string
    firm_store_id?: number | string
  }
  dashboards?: DashboardDefinition[]
  preferences?: DashboardPreferences
}

export function DashboardEmbedContent({ embedToken }: DashboardEmbedContentProps) {
  const embedRootRef = useRef<HTMLElement | null>(null)
  const [availableDashboards, setAvailableDashboards] = useState<DashboardDefinition[]>([])
  const [visibleDashboardKeys, setVisibleDashboardKeys] = useState<string[]>([])
  const [completedDashboardKeys, setCompletedDashboardKeys] = useState<string[]>([])
  const [dashboardGridLayouts, setDashboardGridLayouts] = useState<Record<string, DashboardGridItemLayout>>({})
  const [dashboardUser, setDashboardUser] = useState<DashboardEmbedStatePayload['user'] | null>(null)
  const [customizingDashboards, setCustomizingDashboards] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!embedToken) {
      setLoading(false)
      setError('Embed token bulunamadı')
      return
    }

    const controller = new AbortController()
    const loadEmbedDashboard = async () => {
      setLoading(true)
      setError('')
      try {
        const response = await fetch(`${API_BASE}/panel/embed/dashboard-state`, {
          headers: { Authorization: `Bearer ${embedToken}` },
          cache: 'no-store',
          signal: controller.signal,
        })
        const payload = await response.json().catch(() => null)
        if (!response.ok || payload?.success === false) {
          throw new Error(payload?.message || 'Dashboard iframe verisi alınamadı')
        }

        const data = (payload?.data || {}) as DashboardEmbedStatePayload
        const dashboards = Array.isArray(data.dashboards) ? data.dashboards : []
        const dashboardByKey = new Map<string, DashboardDefinition>()
        for (const dashboard of [...dashboards, ...localDashboardDefinitions]) {
          if (!activeDashboardIds.has(Number(dashboard.id))) continue
          dashboardByKey.set(dashboardKey(dashboard), dashboard)
        }
        const sorted = Array.from(dashboardByKey.values())
          .sort((a, b) => Number(a.sort || 0) - Number(b.sort || 0) || Number(a.id || 0) - Number(b.id || 0))
        const preferences = data.preferences || {}
        const hasVisiblePreference = Array.isArray(preferences.visible_dashboard_keys)
        const nextVisibleKeys = hasVisiblePreference
          ? (normalizeVisibleDashboardKeys(preferences.visible_dashboard_keys, sorted) || [])
          : defaultDashboardKeys(sorted)
        const nextCompletedKeys = normalizeCompletedDashboardKeys(preferences.completed_dashboard_keys, sorted)
        const nextGridLayouts = createDefaultGridLayouts(
          sorted,
          nextVisibleKeys,
          parseDashboardGridLayouts(preferences.grid_layouts),
        )

        setDashboardUser(data.user || null)
        setAvailableDashboards(sorted)
        setVisibleDashboardKeys(nextVisibleKeys)
        setCompletedDashboardKeys(nextCompletedKeys)
        setDashboardGridLayouts(nextGridLayouts)
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return
        setDashboardUser(null)
        setAvailableDashboards([])
        setVisibleDashboardKeys([])
        setCompletedDashboardKeys([])
        setDashboardGridLayouts({})
        setError(err instanceof Error ? err.message : 'Dashboard iframe verisi alınamadı')
      } finally {
        setLoading(false)
      }
    }

    loadEmbedDashboard()
    return () => controller.abort()
  }, [embedToken])

  useEffect(() => {
    if (typeof window === 'undefined' || window.parent === window) return

    let frame = 0
    let lastHeight = 0
    const postHeight = () => {
      if (frame) window.cancelAnimationFrame(frame)
      frame = window.requestAnimationFrame(() => {
        const root = embedRootRef.current
        if (!root) return
        const height = Math.ceil(Math.max(
          root.scrollHeight || 0,
          root.offsetHeight || 0,
          root.getBoundingClientRect().height || 0,
        ))
        if (!Number.isFinite(height) || height <= 0 || Math.abs(height - lastHeight) < 2) return
        lastHeight = height

        window.parent.postMessage({
          type: 'FARKTOR_DASHBOARD_EMBED_RESIZE',
          route: 'dashboard',
          height,
        }, '*')
      })
    }

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(postHeight)
      : null
    const mutationObserver = typeof MutationObserver !== 'undefined'
      ? new MutationObserver(postHeight)
      : null

    if (resizeObserver) {
      if (embedRootRef.current) resizeObserver.observe(embedRootRef.current)
    }
    if (mutationObserver) {
      mutationObserver.observe(embedRootRef.current || document.body, {
        attributes: true,
        childList: true,
        subtree: true,
      })
    }

    window.addEventListener('load', postHeight)
    window.addEventListener('resize', postHeight)
    postHeight()
    const timers = [
      window.setTimeout(postHeight, 120),
      window.setTimeout(postHeight, 500),
      window.setTimeout(postHeight, 1200),
    ]

    return () => {
      if (frame) window.cancelAnimationFrame(frame)
      timers.forEach((timer) => window.clearTimeout(timer))
      resizeObserver?.disconnect()
      mutationObserver?.disconnect()
      window.removeEventListener('load', postHeight)
      window.removeEventListener('resize', postHeight)
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined' || window.parent === window) return
    const root = embedRootRef.current
    if (!root) return
    let wheelFrame = 0
    let pendingWheelDeltaX = 0
    let pendingWheelDeltaY = 0
    let pendingWheelDeltaMode = 0

    const flushWheel = () => {
      wheelFrame = 0
      const deltaX = pendingWheelDeltaX
      const deltaY = pendingWheelDeltaY
      const deltaMode = pendingWheelDeltaMode
      pendingWheelDeltaX = 0
      pendingWheelDeltaY = 0
      pendingWheelDeltaMode = 0
      if (!deltaX && !deltaY) return

      window.parent.postMessage({
        type: 'FARKTOR_DASHBOARD_EMBED_WHEEL',
        route: 'dashboard',
        deltaX,
        deltaY,
        deltaMode,
      }, '*')
    }

    const queueParentScroll = (deltaX: number, deltaY: number, deltaMode = 0) => {
      if (!deltaX && !deltaY) return
      if (wheelFrame && pendingWheelDeltaMode !== deltaMode) {
        window.cancelAnimationFrame(wheelFrame)
        flushWheel()
      }
      pendingWheelDeltaX += deltaX
      pendingWheelDeltaY += deltaY
      pendingWheelDeltaMode = deltaMode
      if (!wheelFrame) wheelFrame = window.requestAnimationFrame(flushWheel)
    }

    const canScrollInside = (target: EventTarget | null, deltaX: number, deltaY: number) => {
      let element = target instanceof HTMLElement ? target : null
      while (element && element !== root) {
        const style = window.getComputedStyle(element)
        const canScrollX = /(auto|scroll)/.test(`${style.overflow}${style.overflowX}`)
          && element.scrollWidth > element.clientWidth + 1
        if (Math.abs(deltaX) > Math.abs(deltaY) && canScrollX) {
          const atLeft = element.scrollLeft <= 0
          const atRight = element.scrollLeft + element.clientWidth >= element.scrollWidth - 1
          if ((deltaX < 0 && !atLeft) || (deltaX > 0 && !atRight)) return true
        }

        const keepVerticalInside = element.classList.contains('channel-orders-dropdown__menu')
        const canScrollY = keepVerticalInside
          && /(auto|scroll)/.test(`${style.overflow}${style.overflowY}`)
          && element.scrollHeight > element.clientHeight + 1
        if (canScrollY) {
          const atTop = element.scrollTop <= 0
          const atBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 1
          if ((deltaY < 0 && !atTop) || (deltaY > 0 && !atBottom)) return true
        }
        element = element.parentElement
      }
      return false
    }

    const handleWheel = (event: WheelEvent) => {
      if (!event.deltaX && !event.deltaY) return
      if (canScrollInside(event.target, event.deltaX, event.deltaY)) return
      event.preventDefault()
      queueParentScroll(event.deltaX, event.deltaY, event.deltaMode)
    }

    root.addEventListener('wheel', handleWheel, { passive: false })
    return () => {
      if (wheelFrame) window.cancelAnimationFrame(wheelFrame)
      root.removeEventListener('wheel', handleWheel)
    }
  }, [])

  const visibleDashboards = useMemo(() => {
    const byKey = new Map(availableDashboards.map((dashboard) => [dashboardKey(dashboard), dashboard]))
    return visibleDashboardKeys
      .map((key) => byKey.get(key))
      .filter(Boolean) as DashboardDefinition[]
  }, [availableDashboards, visibleDashboardKeys])

  const canPersonalize = false

  const saveDashboardPreferences = async (
    visibleKeys: string[],
    gridLayouts: Record<string, DashboardGridItemLayout>,
    completedKeys = completedDashboardKeys,
  ) => {
    if (!canPersonalize || !embedToken) return
    try {
      await fetch(`${API_BASE}/auth/dashboard-preferences`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${embedToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          visible_dashboard_keys: visibleKeys,
          completed_dashboard_keys: completedKeys,
          grid_layouts: gridLayouts,
        }),
      })
    } catch (error) {
      console.error('Embed dashboard preferences save failed:', error)
    }
  }

  const saveVisibleDashboardKeys = (next: string[]) => {
    const available = new Set(availableDashboards.map(dashboardKey))
    const deduped = [...new Set(next)].filter((key) => available.has(key))
    setVisibleDashboardKeys(deduped)
    setDashboardGridLayouts((current) => {
      const merged = createDefaultGridLayouts(availableDashboards, deduped, current)
      void saveDashboardPreferences(deduped, merged)
      return merged
    })
  }

  const normalizeDashboardGridLayout = (layout: Layout) => {
    const available = new Set(availableDashboards.map(dashboardKey))
    const byKey = new Map(availableDashboards.map((dashboard) => [dashboardKey(dashboard), dashboard]))
    return layout.reduce<Record<string, DashboardGridItemLayout>>((acc, item) => {
      if (!available.has(item.i)) return acc
      const dashboard = byKey.get(item.i)
      if (!dashboard) return acc
      acc[item.i] = normalizeGridItem(dashboard, item as Partial<DashboardGridItemLayout>)
      return acc
    }, {})
  }

  const saveDashboardGridLayouts = (layout: Layout) => {
    if (!canPersonalize) return
    const clean = normalizeDashboardGridLayout(layout)
    setDashboardGridLayouts(clean)
    void saveDashboardPreferences(visibleDashboardKeys, clean)
  }

  return (
    <main ref={embedRootRef} className="dashboard-embed-root">
      {canPersonalize && availableDashboards.length > 0 && (
        <div className="dashboard-header dashboard-embed-header">
          <div className="dashboard-heading">
            <div className="dashboard-title">Dashboard</div>
          </div>
          <div className="dashboard-actions">
            <button
              className={`action-button ${customizingDashboards ? 'active' : ''}`}
              onClick={() => setCustomizingDashboards((value) => !value)}
            >
              <i className="fa-duotone fa-grid-2-plus" style={{ fontSize: '13px' }} />
              {customizingDashboards ? 'Tamam' : 'Dashboardları Seç'}
            </button>
          </div>
        </div>
      )}
      <DashboardWorkspace
        dashboards={availableDashboards}
        visibleDashboards={visibleDashboards}
        visibleDashboardKeys={visibleDashboardKeys}
        completedDashboardKeys={completedDashboardKeys}
        dashboardGridLayouts={dashboardGridLayouts}
        orderedCustomizerDashboards={availableDashboards}
        customizing={customizingDashboards}
        loading={loading}
        error={error}
        onToggleDashboard={(key, checked) => {
          const next = checked
            ? [...visibleDashboardKeys, key]
            : visibleDashboardKeys.filter((itemKey) => itemKey !== key)
          saveVisibleDashboardKeys(next)
        }}
        onLayoutChange={saveDashboardGridLayouts}
        onCustomizerClose={() => setCustomizingDashboards(false)}
        onNavigate={() => {}}
        quickActionNavItems={[]}
        userId={undefined}
        canArrange={false}
      />
    </main>
  )
}

export default function Home() {
  const router = useRouter()
  const pathname = usePathname()
  const { user, loading, refreshUser } = useUser()
  const [showDashboardDropdown, setShowDashboardDropdown] = useState(false)
  const [dashboardSearch, setDashboardSearch] = useState('')
  const [switchingStore, setSwitchingStore] = useState(false)
  const [adminStoreNavScope, setAdminStoreNavScope] = useState(false)
  const [availableDashboards, setAvailableDashboards] = useState<DashboardDefinition[]>([])
  const [quickActionNavItems, setQuickActionNavItems] = useState<QuickActionNav[]>([])
  const [visibleDashboardKeys, setVisibleDashboardKeys] = useState<string[]>([])
  const [completedDashboardKeys, setCompletedDashboardKeys] = useState<string[]>([])
  const [dashboardGridLayouts, setDashboardGridLayouts] = useState<Record<string, DashboardGridItemLayout>>({})
  const [customizingDashboards, setCustomizingDashboards] = useState(false)
  const [dashboardsLoading, setDashboardsLoading] = useState(false)
  const [dashboardsError, setDashboardsError] = useState('')
  const [setupCompleted, setSetupCompleted] = useState<boolean | null>(null)
  const [setupStatus, setSetupStatus] = useState<SetupStatus | null>(null)
  const [setupStatusLoading, setSetupStatusLoading] = useState(false)
  const [setupModulesLoading, setSetupModulesLoading] = useState(false)
  const [setupPaymentOptions, setSetupPaymentOptions] = useState<SetupOffer[]>([])
  const [setupCargoOptions, setSetupCargoOptions] = useState<SetupOffer[]>([])
  const [dashboardMainTab, setDashboardMainTab] = useState<DashboardMainTab>('setup')
  const isAdminUser = Number(user?.admin || 0) === 1

  const saveDashboardPreferences = async (
    visibleKeys: string[],
    gridLayouts: Record<string, DashboardGridItemLayout>,
    completedKeys = completedDashboardKeys,
    widgetSettings?: Record<string, unknown>
  ) => {
    const userId = user?.id || 'me'
    window.localStorage.setItem(`panel_dashboard_visible_${userId}`, JSON.stringify(visibleKeys))
    window.localStorage.setItem(`panel_dashboard_grid_${userId}`, JSON.stringify(gridLayouts))
    window.localStorage.setItem(`panel_dashboard_completed_${userId}`, JSON.stringify(completedKeys))

    try {
      const token = window.localStorage.getItem('auth_token')
      const body: DashboardPreferences = {
        visible_dashboard_keys: visibleKeys,
        completed_dashboard_keys: completedKeys,
        grid_layouts: gridLayouts,
      }
      if (widgetSettings) body.widget_settings = widgetSettings
      const response = await fetch(`${API_BASE}/auth/dashboard-preferences`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })
      if (!response.ok) {
        throw new Error(`Dashboard preferences save failed: ${response.status}`)
      }
    } catch (error) {
      console.error('Dashboard preferences save failed:', error)
    }
  }

  useEffect(() => {
    // Token yoksa login'e yönlendir
    const token = localStorage.getItem('auth_token')
    console.log('[Dashboard] Token check:', { hasToken: !!token, loading, hasUser: !!user })
    
    if (!token) {
      console.log('[Dashboard] No token, redirecting to login')
      router.push('/login')
      return
    }

    // User data yoksa ve loading değilse, login'e yönlendir
    if (!loading && !user) {
      console.log('[Dashboard] No user after loading, redirecting to login')
      router.push('/login')
    } else if (user) {
      console.log('[Dashboard] User loaded successfully:', { email: user.email, navItems: user.nav?.length })
    }
  }, [user, loading, router])

  useEffect(() => {
    setAdminStoreNavScope(localStorage.getItem('admin_store_nav_scope') === '1')
  }, [])

  useEffect(() => {
    if (!user?.id) return

    const controller = new AbortController()
    const loadDashboards = async () => {
      setDashboardsLoading(true)
      setDashboardsError('')
      try {
        const token = localStorage.getItem('auth_token')
        const params = new URLSearchParams({
          platform: 'all',
          surface: Number(user.admin || 0) === 1 ? 'all' : 'web'
        })
        if (Number(user.admin || 0) === 1 && adminStoreNavScope) {
          params.set('store_scope', '1')
        }
        const [dashboardRes, navRes, preferencesRes] = await Promise.all([
          fetch(`https://app.canerozen.com/api/auth/dashboards?${params}`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal
          }),
          fetch(`https://app.canerozen.com/api/auth/navs?platform=all${Number(user.admin || 0) === 1 && adminStoreNavScope ? '&store_scope=1' : ''}`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal
          }),
          fetch(`${API_BASE}/auth/dashboard-preferences`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal
          })
        ])
        const data = await dashboardRes.json()
        const navData = await navRes.json().catch(() => null)
        const preferenceData = await preferencesRes.json().catch(() => null)
        if (!dashboardRes.ok || !data.success) {
          throw new Error(data.message || 'Dashboardlar yüklenemedi')
        }
	        setQuickActionNavItems(Array.isArray(navData?.data?.navs) ? navData.data.navs as QuickActionNav[] : [])
	        const dashboards = Array.isArray(data.data?.dashboards) ? data.data.dashboards as DashboardDefinition[] : []
	        const sorted = [
	          ...dashboards.filter((dashboard) => activeDashboardIds.has(Number(dashboard.id))),
	          ...localDashboardDefinitions,
	          ...(isAdminUser ? mockChartDashboards : []),
	        ]
	          .filter((dashboard) => activeDashboardIds.has(Number(dashboard.id)))
          .sort((a, b) => Number(a.sort || 0) - Number(b.sort || 0) || a.id - b.id)
        setAvailableDashboards(sorted)

        const prefKey = `panel_dashboard_visible_${user.id || 'me'}`
        const gridPrefKey = `panel_dashboard_grid_${user.id || 'me'}`
        const legacyPrefKey = `panel_dashboard_visible_${user.id || 'me'}_${user.role}`
        const legacyGridPrefKey = `panel_dashboard_grid_${user.id || 'me'}_${user.role}`
        const backendPreferences = preferenceData?.success && preferenceData?.data?.preferences
          ? preferenceData.data.preferences as DashboardPreferences
          : {}
        const rawBackendVisibleKeys = Array.isArray(backendPreferences.visible_dashboard_keys)
          ? backendPreferences.visible_dashboard_keys.map(String)
          : []
        const rawBackendCompletedKeys = Array.isArray(backendPreferences.completed_dashboard_keys)
          ? backendPreferences.completed_dashboard_keys.map(String)
          : []
        const hasBackendCompletedPreference = Array.isArray(backendPreferences.completed_dashboard_keys)
        const backendVisibleKeys = normalizeVisibleDashboardKeys(backendPreferences.visible_dashboard_keys, sorted)
        const backendCompletedKeys = normalizeCompletedDashboardKeys(backendPreferences.completed_dashboard_keys, sorted)
        const backendGridLayouts = parseDashboardGridLayouts(backendPreferences.grid_layouts)
        const backendWidgetSettings = backendPreferences.widget_settings && typeof backendPreferences.widget_settings === 'object' && !Array.isArray(backendPreferences.widget_settings)
          ? backendPreferences.widget_settings
          : {}
        const sortedDashboardKeys = new Set(sorted.map(dashboardKey))
        const backendGridLayoutsHasVisibleDashboard = Object.keys(backendGridLayouts).some((key) => sortedDashboardKeys.has(key))
        const savedGridLayouts = backendGridLayoutsHasVisibleDashboard
          ? backendGridLayouts
          : isAdminUser
            ? parseSavedGridLayouts(localStorage.getItem(gridPrefKey))
            : defaultUserDashboardGridLayouts
        const legacySavedGridLayouts = isAdminUser ? parseSavedGridLayouts(localStorage.getItem(legacyGridPrefKey)) : {}

        const savedPreference = localStorage.getItem(prefKey)
        const savedCompletedPreference = localStorage.getItem(`panel_dashboard_completed_${user.id || 'me'}`)
        const legacySavedPreference = localStorage.getItem(legacyPrefKey)
        const saved = savedPreference !== null ? savedPreference : legacySavedPreference
        let nextVisibleKeys = defaultDashboardKeys(sorted)
        let hasSavedPreference = false
        if (isAdminUser) {
          hasSavedPreference = Array.isArray(backendVisibleKeys)
          if (backendVisibleKeys) {
            nextVisibleKeys = backendVisibleKeys
          } else if (saved !== null) {
            hasSavedPreference = true
            try {
              const parsed = JSON.parse(saved || '[]')
              if (Array.isArray(parsed)) {
                nextVisibleKeys = normalizeVisibleDashboardKeys(parsed, sorted) || []
              } else {
                hasSavedPreference = false
              }
            } catch {
              hasSavedPreference = false
            }
          }
        }
        const operationFlowKey = sorted.find((dashboard) => Number(dashboard.id) === operationFlowDashboardId || dashboard.component_key === 'operation_flow')
          ? dashboardKey(sorted.find((dashboard) => Number(dashboard.id) === operationFlowDashboardId || dashboard.component_key === 'operation_flow') as DashboardDefinition)
          : ''
        const outboundCustomerServiceKey = sorted.find((dashboard) => Number(dashboard.id) === outboundCustomerServiceDashboardId || dashboard.component_key === 'outbound_customer_service')
          ? dashboardKey(sorted.find((dashboard) => Number(dashboard.id) === outboundCustomerServiceDashboardId || dashboard.component_key === 'outbound_customer_service') as DashboardDefinition)
          : ''
        const supplyRequestTrendKey = sorted.find((dashboard) => Number(dashboard.id) === supplyRequestTrendDashboardId || dashboard.component_key === 'supply_request_trend')
          ? dashboardKey(sorted.find((dashboard) => Number(dashboard.id) === supplyRequestTrendDashboardId || dashboard.component_key === 'supply_request_trend') as DashboardDefinition)
          : ''
	        const supplyDashboardSuiteKeys = [
	          sorted.find((dashboard) => Number(dashboard.id) === supplyRadarDashboardId || dashboard.component_key === 'supply_radar'),
	          sorted.find((dashboard) => Number(dashboard.id) === supplyAgingDashboardId || dashboard.component_key === 'supply_aging'),
	          sorted.find((dashboard) => Number(dashboard.id) === supplyPerformanceDashboardId || dashboard.component_key === 'supply_performance'),
        ]
	          .filter(Boolean)
	          .map((dashboard) => dashboardKey(dashboard as DashboardDefinition))
	        const missingSupplyDashboardSuiteKeys = supplyDashboardSuiteKeys.filter((key) => !nextVisibleKeys.includes(key))
	        const marketplaceProductErrorsKey = sorted.find((dashboard) => Number(dashboard.id) === marketplaceProductErrorsDashboardId || dashboard.component_key === 'marketplace_product_errors')
	          ? dashboardKey(sorted.find((dashboard) => Number(dashboard.id) === marketplaceProductErrorsDashboardId || dashboard.component_key === 'marketplace_product_errors') as DashboardDefinition)
	          : ''
	        const shouldAutoAddOperationFlow = Boolean(
	          isAdminUser
	          && operationFlowKey
          && !backendWidgetSettings[operationFlowAutoAddPreferenceKey]
          && !nextVisibleKeys.includes(operationFlowKey)
        )
        const shouldAutoAddOutboundCustomerService = Boolean(
          isAdminUser
          && outboundCustomerServiceKey
          && !backendWidgetSettings[outboundCustomerServiceAutoAddPreferenceKey]
          && !nextVisibleKeys.includes(outboundCustomerServiceKey)
        )
        const shouldAutoAddSupplyRequestTrend = Boolean(
          isAdminUser
          && supplyRequestTrendKey
          && !backendWidgetSettings[supplyRequestTrendAutoAddPreferenceKey]
          && !nextVisibleKeys.includes(supplyRequestTrendKey)
        )
        const shouldAutoAddSupplyDashboardSuite = Boolean(
          isAdminUser
	          && missingSupplyDashboardSuiteKeys.length
	          && !backendWidgetSettings[supplyDashboardSuiteAutoAddPreferenceKey]
	        )
	        const shouldAutoAddMarketplaceProductErrors = Boolean(
	          isAdminUser
	          && marketplaceProductErrorsKey
	          && !backendWidgetSettings[marketplaceProductErrorsAutoAddPreferenceKey]
	          && !nextVisibleKeys.includes(marketplaceProductErrorsKey)
	        )
	        if (shouldAutoAddOperationFlow) {
	          nextVisibleKeys = [...nextVisibleKeys, operationFlowKey]
	        }
        if (shouldAutoAddOutboundCustomerService) {
          nextVisibleKeys = [...nextVisibleKeys, outboundCustomerServiceKey]
        }
        if (shouldAutoAddSupplyRequestTrend) {
          nextVisibleKeys = [...nextVisibleKeys, supplyRequestTrendKey]
        }
	        if (shouldAutoAddSupplyDashboardSuite) {
	          nextVisibleKeys = [...nextVisibleKeys, ...missingSupplyDashboardSuiteKeys]
	        }
	        if (shouldAutoAddMarketplaceProductErrors) {
	          nextVisibleKeys = [...nextVisibleKeys, marketplaceProductErrorsKey]
	        }
	        setVisibleDashboardKeys(nextVisibleKeys)
        let nextCompletedKeys = backendCompletedKeys
        if (!hasBackendCompletedPreference && savedCompletedPreference !== null) {
          try {
            nextCompletedKeys = normalizeCompletedDashboardKeys(JSON.parse(savedCompletedPreference || '[]'), sorted)
          } catch {
            nextCompletedKeys = []
          }
        }
        setCompletedDashboardKeys(nextCompletedKeys)
        const nextGridLayouts = createDefaultGridLayouts(
          sorted,
          nextVisibleKeys,
          Object.keys(savedGridLayouts).length ? savedGridLayouts : legacySavedGridLayouts
        )
        setDashboardGridLayouts(nextGridLayouts)
        const visibleKeysWereCleaned = rawBackendVisibleKeys.length > 0 && rawBackendVisibleKeys.length !== nextVisibleKeys.length
        const completedKeysWereCleaned = rawBackendCompletedKeys.length > 0 && rawBackendCompletedKeys.length !== nextCompletedKeys.length
	        if (!preferencesRes.ok || !preferenceData?.success || !hasSavedPreference || !Object.keys(backendGridLayouts).length || visibleKeysWereCleaned || completedKeysWereCleaned || shouldAutoAddOperationFlow || shouldAutoAddOutboundCustomerService || shouldAutoAddSupplyRequestTrend || shouldAutoAddSupplyDashboardSuite || shouldAutoAddMarketplaceProductErrors) {
	          const nextWidgetSettings = shouldAutoAddOperationFlow || shouldAutoAddOutboundCustomerService || shouldAutoAddSupplyRequestTrend || shouldAutoAddSupplyDashboardSuite || shouldAutoAddMarketplaceProductErrors
	            ? {
	                ...backendWidgetSettings,
	                ...(shouldAutoAddOperationFlow ? { [operationFlowAutoAddPreferenceKey]: true } : {}),
	                ...(shouldAutoAddOutboundCustomerService ? { [outboundCustomerServiceAutoAddPreferenceKey]: true } : {}),
	                ...(shouldAutoAddSupplyRequestTrend ? { [supplyRequestTrendAutoAddPreferenceKey]: true } : {}),
	                ...(shouldAutoAddSupplyDashboardSuite ? { [supplyDashboardSuiteAutoAddPreferenceKey]: true } : {}),
	                ...(shouldAutoAddMarketplaceProductErrors ? { [marketplaceProductErrorsAutoAddPreferenceKey]: true } : {}),
	              }
	            : undefined
          await saveDashboardPreferences(
            nextVisibleKeys,
            nextGridLayouts,
            nextCompletedKeys,
            nextWidgetSettings
          )
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return
        setDashboardsError(err instanceof Error ? err.message : 'Dashboardlar yüklenemedi')
        setAvailableDashboards([])
        setQuickActionNavItems([])
        setVisibleDashboardKeys([])
        setCompletedDashboardKeys([])
        setDashboardGridLayouts({})
      } finally {
        setDashboardsLoading(false)
      }
    }

    loadDashboards()
    return () => controller.abort()
  }, [user?.id, user?.role, user?.admin, adminStoreNavScope, isAdminUser])

  useEffect(() => {
    if (!isAdminUser || !user?.firm_id || !user?.firm_store_id) {
      setSetupCompleted(null)
      setSetupStatus(null)
      return
    }

    const controller = new AbortController()
    const loadSetupStatus = async () => {
      setSetupStatusLoading(true)
      try {
        const params = new URLSearchParams({
          firm_id: String(user.firm_id),
          store_id: String(user.firm_store_id),
        })
        const token = localStorage.getItem('auth_token') || ''
        const response = await fetch(`/api/setup/status?${params}`, {
          signal: controller.signal,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        const data = await response.json().catch(() => null)
        const nextStatus: SetupStatus = {
          setupCompleted: Boolean(response.ok && data?.success && data?.data?.setup_completed),
          completedSteps: Number(data?.data?.completed_steps || 0),
          totalSteps: Number(data?.data?.total_steps || 25),
          progressPercent: Number(data?.data?.progress_percent || 0),
        }
        setSetupCompleted(nextStatus.setupCompleted)
        setSetupStatus(nextStatus)
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return
        setSetupCompleted(true)
        setSetupStatus(null)
      } finally {
        setSetupStatusLoading(false)
      }
    }

    loadSetupStatus()
    window.addEventListener('panel:setup-status-changed', loadSetupStatus)
    return () => {
      controller.abort()
      window.removeEventListener('panel:setup-status-changed', loadSetupStatus)
    }
  }, [isAdminUser, user?.firm_id, user?.firm_store_id])

  useEffect(() => {
    if (!isAdminUser) {
      setSetupPaymentOptions([])
      setSetupCargoOptions([])
      return
    }

    const controller = new AbortController()
    const toSetupOffer = (item: SetupModuleApiItem, icon: string, cta: string): SetupOffer => ({
      id: Number(item?.id) || undefined,
      title: String(item?.display_name || item?.name || 'Modül'),
      logo: String(item?.display_name || item?.name || 'Modül'),
      note: item?.short_desc ? String(item.short_desc) : '',
      icon,
      cta,
    })

    const loadSetupModules = async () => {
      setSetupModulesLoading(true)
      try {
        const token = localStorage.getItem('auth_token') || ''
        const response = await fetch('/api/setup/modules', {
          signal: controller.signal,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        const data = await response.json().catch(() => null)
        const payment: SetupModuleApiItem[] = Array.isArray(data?.data?.payment) ? data.data.payment : []
        const cargo: SetupModuleApiItem[] = Array.isArray(data?.data?.cargo) ? data.data.cargo : []
        setSetupPaymentOptions(payment.map((item) => toSetupOffer(item, 'fa-regular fa-credit-card', 'Ekle')))
        setSetupCargoOptions(cargo.map((item) => toSetupOffer(item, 'fa-regular fa-truck-fast', 'Ekle')))
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return
        setSetupPaymentOptions([])
        setSetupCargoOptions([])
      } finally {
        setSetupModulesLoading(false)
      }
    }

    loadSetupModules()
    return () => controller.abort()
  }, [isAdminUser])

  const visibleDashboards = useMemo(() => {
    const byKey = new Map(availableDashboards.map((dashboard) => [dashboardKey(dashboard), dashboard]))
    return visibleDashboardKeys
      .map((key) => byKey.get(key))
      .filter(Boolean) as DashboardDefinition[]
  }, [availableDashboards, visibleDashboardKeys])

  const orderedCustomizerDashboards = useMemo(() => {
    return availableDashboards
  }, [availableDashboards])

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div>Yükleniyor...</div>
      </div>
    )
  }

  if (!user) {
    return null // redirect olacak
  }

  // Sol panel için verileri hazırla
  const { parentMenu, subMenus, activeMenuIds } = getLeftPanelData(pathname, user.nav || [])

  // firmStores listesi
  const firmStores: FirmStoreOption[] = (user?.firmStores || []).map((s: RawFirmStore, i: number) => ({
    id: s.id,
    firm_id: s.firm_id,
    name: s.name,
    code: s.code,
    initials: getInitials(s.name || s.code || ''),
    colorIndex: i % storeColorCount,
  }))

  const selectedStore = firmStores.find((s) => Number(s.id) === Number(user?.firm_store_id))
  const showSetupTabs = isAdminUser && setupCompleted === false
  const activeDashboardMainTab: DashboardMainTab = showSetupTabs ? dashboardMainTab : 'statistics'

  const filteredStores = firmStores.filter((s) =>
    (s.name || '').toLowerCase().includes(dashboardSearch.toLowerCase()) ||
    (s.code || '').toLowerCase().includes(dashboardSearch.toLowerCase())
  )

  const saveVisibleDashboardKeys = (next: string[]) => {
    const available = new Set(availableDashboards.map(dashboardKey))
    const deduped = [...new Set(next)].filter((key) => available.has(key))
    setVisibleDashboardKeys(deduped)
    setDashboardGridLayouts((current) => {
      const merged = createDefaultGridLayouts(availableDashboards, deduped, current)
      void saveDashboardPreferences(deduped, merged)
      return merged
    })
  }

  const normalizeDashboardGridLayout = (layout: Layout) => {
    const available = new Set(availableDashboards.map(dashboardKey))
    const byKey = new Map(availableDashboards.map((dashboard) => [dashboardKey(dashboard), dashboard]))
    return layout.reduce<Record<string, DashboardGridItemLayout>>((acc, item) => {
      if (!available.has(item.i)) return acc
      const dashboard = byKey.get(item.i)
      if (!dashboard) return acc
      acc[item.i] = normalizeGridItem(dashboard, item as Partial<DashboardGridItemLayout>)
      return acc
    }, {})
  }

  const saveDashboardGridLayouts = (layout: Layout) => {
    if (!isAdminUser) return
    const clean = normalizeDashboardGridLayout(layout)
    setDashboardGridLayouts(clean)
    void saveDashboardPreferences(visibleDashboardKeys, clean)
  }

  const handleSwitchStore = async (store: FirmStoreOption) => {
    setSwitchingStore(true)
    try {
      const token = localStorage.getItem('auth_token')
      const res = await fetch('https://app.canerozen.com/api/auth/switch-store', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: store.id })
      })
      if (res.ok) {
        await refreshUser()
      }
    } catch (e) {
      console.error('Store switch failed:', e)
    } finally {
      setSwitchingStore(false)
      setShowDashboardDropdown(false)
      setDashboardSearch('')
    }
  }

  const handleAdminStoreNavScope = async () => {
    const next = !adminStoreNavScope
    setAdminStoreNavScope(next)
    localStorage.setItem('admin_store_nav_scope', next ? '1' : '0')
    await refreshUser()
  }

  return (
    <div className="panel-layout">
      <Sidebar 
        collapsed={false}
        nav={user.nav || []} 
        platforms={user.platforms || []}
        currentPlatform={user.role}
      />
      
      {/* Sol Panel - Alt Menüler */}
      {parentMenu && subMenus.length > 0 && (
        <LeftPanel 
          title={parentMenu.name}
          icon={parentMenu.icon}
          items={subMenus}
          activeIds={activeMenuIds}
        />
      )}
      
      <div className="panel-main">
        <TopBar 
          dashboardSelector={
            <div className="dashboard-selector">
              <button
                onClick={() => setShowDashboardDropdown(!showDashboardDropdown)}
                className="dashboard-selector-button"
                disabled={switchingStore}
              >
                {selectedStore ? (
                  <>
                    <div className={`dashboard-avatar dashboard-avatar--c${selectedStore.colorIndex}`}>
                      {selectedStore.initials}
                    </div>
                    <div className="dashboard-info">
                      <div className="dashboard-name">
                        {selectedStore.name}
                      </div>
                      <div className="dashboard-code">
                        {selectedStore.name} ({selectedStore.code})
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="dashboard-info">
                    <div className="dashboard-name">Mağaza Seçin</div>
                  </div>
                )}
                <i className="fa-duotone fa-chevron-down dashboard-chevron" />
              </button>

              {/* Dashboard Dropdown */}
              {showDashboardDropdown && (
                <>
                  <div 
                    className="dashboard-dropdown-overlay"
                    onClick={() => {
                      setShowDashboardDropdown(false)
                      setDashboardSearch('')
                    }}
                  />
                  <div className="dashboard-dropdown">
                    {/* Header */}
                    <div className="dashboard-dropdown-header">
                      <div className="dashboard-dropdown-top">
                        <div className="dashboard-dropdown-copy">
                          <div className="dashboard-dropdown-title">Mağaza Seç</div>
                          <div className="dashboard-dropdown-subtitle">Seçili mağaza panel bağlamını belirler.</div>
                        </div>
                        {Number(user?.admin || 0) === 1 && (
                          <button
                            type="button"
                            className={`dashboard-scope-toggle ${adminStoreNavScope ? 'dashboard-scope-toggle--active' : ''}`}
                            onClick={handleAdminStoreNavScope}
                          >
                            <span className="dashboard-scope-toggle__track">
                              <span className="dashboard-scope-toggle__thumb" />
                            </span>
                            <span>Mağaza yetkileri</span>
                          </button>
                        )}
                      </div>
                      <div className="dashboard-search">
                        <label className="dashboard-search-label" htmlFor="dashboard-store-search">
                          <i className="fa-duotone fa-search dashboard-search-icon" />
                          <span>Mağaza ara</span>
                        </label>
                        <input
                          id="dashboard-store-search"
                          type="text"
                          value={dashboardSearch}
                          onChange={(e) => setDashboardSearch(e.target.value)}
                          className="dashboard-search-input"
                          autoFocus
                        />
                      </div>
                    </div>
                    
                    {/* List */}
                    <div className="dashboard-dropdown-list">
                      {filteredStores.map((store) => (
                        <button
                          key={store.id}
                          onClick={() => handleSwitchStore(store)}
                          disabled={switchingStore}
                          className={`dashboard-item ${selectedStore?.id === store.id ? 'active' : ''}`}
                        >
                          <div className={`dashboard-item-avatar dashboard-avatar--c${store.colorIndex}`}>
                            {store.initials}
                          </div>
                          <div className="dashboard-item-info">
                            <div className="dashboard-item-name">
                              {store.name}
                            </div>
                            <div className="dashboard-item-code">
                              {store.code}
                            </div>
                          </div>
                        </button>
                      ))}
                      
                      {filteredStores.length === 0 && (
                        <div className="dashboard-dropdown-empty">
                          Sonuç bulunamadı
                        </div>
                      )}
                    </div>
                    
                    {/* Footer */}
                    <div className="dashboard-dropdown-footer">
                      <span>{firmStores.length} mağaza</span>
                      {Number(user?.admin || 0) === 1 && adminStoreNavScope && (
                        <strong>Yetkili nav görünümü açık</strong>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          }
        />
        <main className="panel-content dashboard-panel-content" data-panel-scroll="managed">
          {/* Dashboard Header */}
          <div className="dashboard-header">
            <div className="dashboard-heading">
              <div className="dashboard-title">Dashboard</div>
              {showSetupTabs && (
                <div className="dashboard-main-tabs" role="tablist" aria-label="Dashboard görünümü">
                  <button
                    type="button"
                    className={activeDashboardMainTab === 'setup' ? 'is-active' : ''}
                    onClick={() => setDashboardMainTab('setup')}
                    role="tab"
                    aria-selected={activeDashboardMainTab === 'setup'}
                  >
                    Kurulum
                  </button>
                  <button
                    type="button"
                    className={activeDashboardMainTab === 'statistics' ? 'is-active' : ''}
                    onClick={() => setDashboardMainTab('statistics')}
                    role="tab"
                    aria-selected={activeDashboardMainTab === 'statistics'}
                  >
                    İstatistikler
                  </button>
                </div>
              )}
            </div>
            <div className="dashboard-actions">
              {activeDashboardMainTab === 'statistics' && isAdminUser && availableDashboards.length > 0 && (
                <button
                  className={`action-button ${customizingDashboards ? 'active' : ''}`}
                  onClick={() => setCustomizingDashboards((value) => !value)}
                >
                  <i className="fa-duotone fa-grid-2-plus" style={{ fontSize: '13px' }} />
                  {customizingDashboards ? 'Tamam' : 'Dashboardları Seç'}
                </button>
              )}
            </div>
          </div>

          <div className="dashboard-content">
            {setupStatusLoading && showSetupTabs ? (
              <div className="panel-dashboard-state">Kurulum durumu kontrol ediliyor...</div>
            ) : activeDashboardMainTab === 'setup' ? (
              <SetupDashboard
                storeName={selectedStore?.name}
                paymentOptions={setupPaymentOptions}
                cargoOptions={setupCargoOptions}
                setupModulesLoading={setupModulesLoading}
                setupStatus={setupStatus}
              />
            ) : (
              <DashboardWorkspace
                dashboards={availableDashboards}
                visibleDashboards={visibleDashboards}
                visibleDashboardKeys={visibleDashboardKeys}
                completedDashboardKeys={completedDashboardKeys}
                dashboardGridLayouts={dashboardGridLayouts}
                orderedCustomizerDashboards={orderedCustomizerDashboards}
                customizing={customizingDashboards}
                loading={dashboardsLoading}
                error={dashboardsError}
                onToggleDashboard={(key, checked) => {
                  const next = checked
                    ? [...visibleDashboardKeys, key]
                    : visibleDashboardKeys.filter((itemKey) => itemKey !== key)
                  saveVisibleDashboardKeys(next)
                }}
                onLayoutChange={saveDashboardGridLayouts}
                onCustomizerClose={() => setCustomizingDashboards(false)}
                onNavigate={(path) => router.push(path)}
                quickActionNavItems={quickActionNavItems}
                userId={user?.id}
                canArrange={false}
              />
            )}
          </div>
        </main>
        <BottomBar />
        <MobileNav />
      </div>
    </div>
  )
}
